<template>
  <div>
    <spinner-loader :loadingStuff="loadingStuff" />
    <app-template />
    <div
      class="uk-container uk-container-large trialist-page with-drawer"
      :class="{ 'nav-collapsed': navCollapsed }"
    >
      <h2>{{ activeTabTitle }}</h2>

      <SectionNav
        title="Trialists"
        :items="trialistNavItems"
        :active="selectedTab"
        v-model:collapsed="navCollapsed"
        @select="(k) => tabChange('selectedTab', k)"
      />

      <div>
        <div class="tabs-wrapper">
          <div class="current-week-tab leads-tab tab-content" v-if="selectedTab === 'leads-tab'">
            <div class="add-new-lead-wrapper">
              <button @click="openLeadsModal('new')" class="green-button add-new-lead">
                Add Player lead
              </button>
            </div>

            <!-- Player Leads Enhancement Tabs & Filters -->

            <div class="lead-tabs-filters">
              <div class="lead-filters">
                <div class="filter-group">
                  <label class="filter-label">Priority</label>
                  <b-form-select v-model="filterPriority" :options="priorityOptions" class="filter-select" />
                </div>
                <div class="filter-group">
                  <label class="filter-label">Status</label>
                  <b-form-select v-model="filterStatus" :options="statusOptions" class="filter-select" />
                </div>
                <div class="filter-group">
                  <label class="filter-label">Scout</label>
                  <b-form-select v-model="filterScout" :options="scoutOptions" class="filter-select" />
                </div>
                <button v-if="filterPriority || filterStatus || filterScout" type="button"
                  class="filter-reset" @click="filterPriority = ''; filterStatus = ''; filterScout = ''">
                  <i class="bi bi-x-circle" aria-hidden="true"></i> Reset
                </button>
              </div>
            </div>

            <div v-if="allPlayerLeadsData.length > 0" class="leads-count">
              <span v-if="filteredLeads.length === allPlayerLeadsData.length">
                {{ allPlayerLeadsData.length }} lead{{ allPlayerLeadsData.length === 1 ? '' : 's' }}
              </span>
              <span v-else>
                Showing {{ filteredLeads.length }} of {{ allPlayerLeadsData.length }} leads
              </span>
            </div>

            <div v-if="filteredLeads.length > 0" class="table-wrapper table-scroll">
              <vue-good-table :columns="columnsPlayerLeads" :rows="filteredLeads" :search-options="{ enabled: true }">
                <template v-slot:table-row="props">
                  <span v-if="props.column.field === 'priority'">
                    <b-badge :variant="priorityBadgeVariant(props.row.priority)">
                      {{ capitalize(props.row.priority) }}
                    </b-badge>
                  </span>
                  <span v-else-if="props.column.field == 'dob'">
                    <span>{{
                      formatDates(props.formattedRow[props.column.field])
                      }}</span>
                  </span>
                  <span v-else-if="props.column.field == 'userImage'">
                    <span>
                      <TenantLogo id="main-logo" logo-class="user-image-small" />
                    </span>
                  </span>
                  <span class="action-buttons" v-else-if="props.column.field == 'state'">
                    <span :class="setPlayerLeadTextColor(props.row.state)">{{
                      props.row.state
                      }}</span>
                  </span>
                  <span class="action-buttons" v-else-if="props.column.field == 'date'">
                    <span>{{ formatDateForHuman(props.row.date) }}</span>
                  </span>
                  <span v-else-if="props.column.field === 'status'">
                    <StatusIcon :status="props.row.watch_category" />
                    {{ props.row.watch_category }}
                  </span>
                  <span v-else-if="props.column.field === 'phone'">
                    <a v-if="props.row.phone" :href="'tel:' + props.row.phone" class="lead-phone-link">
                      {{ props.row.phone }}
                    </a>
                    <span v-else class="lead-cell-empty">—</span>
                  </span>
                  <span v-else-if="props.column.field === 'last_contact_date'">
                    <span :class="lastContactClass(props.row.last_contact_date)">
                      {{ formatRelativeDate(props.row.last_contact_date) }}
                    </span>
                  </span>
                  <span v-else-if="props.column.field === 'next_follow_up_date'">
                    <span :class="followUpClass(props.row.next_follow_up_date)">
                      {{ formatRelativeDate(props.row.next_follow_up_date) }}
                    </span>
                  </span>
                  <span v-else>
                    {{ props.formattedRow[props.column.field] }}
                  </span>
                  <span v-if="props.column.field === 'actions'">
                    <div class="lead-buttons">
                      <b-button v-if="
                        props.row.trialistId === null &&
                        userData.userType === 'Admin'
                      " @click="addEditTrialistModel(props.row)" variant="outline-success" v-b-tooltip.hover
                        title="Add lead as a trialist">
                        <i class="bi bi-person" aria-hidden="true"></i></b-button>

                      <b-button @click="selectedUseChanged(props.row, 'playerLead')" variant="outline-success"
                        v-b-tooltip.hover title="View report"><i class="bi bi-eye" aria-hidden="true"></i></b-button>
                      <b-button @click="markContactedToday(props.row)" variant="outline-primary"
                        v-b-tooltip.hover title="Mark contacted today">
                        <i class="bi bi-telephone-fill" aria-hidden="true"></i>
                      </b-button>
                      <div class="lead-filters">
                        <b-form-select v-model="props.row.priority" :options="priorityOptions" class="mr-2"
                          @change="updatePlayerLeadPriority(props.row)" />
                      </div>
                    </div>
                  </span>
                </template>
              </vue-good-table>
            </div>
            <div v-else-if="allPlayerLeadsData.length === 0">
              <div class="empty-state">
                <img src="../img/emptyStateIllustration.svg" />
                <p>No player leads yet. Click "Add Player lead" to create one.</p>
              </div>
            </div>
            <div v-else>
              <div class="empty-state">
                <img src="../img/emptyStateIllustration.svg" />
                <p>No leads match your current filters.</p>
                <button type="button" class="filter-reset"
                  @click="filterPriority = ''; filterStatus = ''; filterScout = ''">
                  <i class="bi bi-x-circle" aria-hidden="true"></i> Clear filters
                </button>
              </div>
            </div>
          </div>
          <div class="current-week-tab trialist-tab tab-content" v-if="selectedTab === 'trialist-tab'">
            <div style="width: 100%; margin-bottom: 2rem">
              <button @click="addEditTrialistModel()" class="green-button add-new-programme">
                Add trialist
              </button>
            </div>

            <div>
              <section class="player-profile-wrapper" v-if="liveTrialistsData.length > 0">
                <div class="button-wrapper-top filters-sort">
                  <b-input-group size="sm" class="mb-2 autocomplete search-small">
                    <i class="bi bi-search"></i>
                    <b-form-input type="search" placeholder="Search players"
                      @update:modelValue="lookUpSearch($event)"></b-form-input>
                  </b-input-group>

                  <ul class="autocomplete-results" v-if="filteredTrialistsData.length > 0">
                    <li @click="selectedUseChanged(userData, 'trialist')" v-for="userData in filteredTrialistsData"
                      :key="userData.id" class="autocomplete-result">
                      {{ userData.name }}
                    </li>
                  </ul>
                </div>

                <div class="lead-tabs-filters">
                  <div class="lead-filters">
                    <div class="filter-group">
                      <label class="filter-label">Coach</label>
                      <b-form-select v-model="trialistsFilterCoach" :options="trialistCoachOptions"
                        class="filter-select" />
                    </div>
                    <div class="filter-group">
                      <label class="filter-label">Team</label>
                      <b-form-select v-model="trialistsFilterTeam" :options="trialistTeamOptions"
                        class="filter-select" />
                    </div>
                    <div class="filter-group">
                      <label class="filter-label">Trial status</label>
                      <b-form-select v-model="trialistsFilterStatus" :options="trialistStatusOptions"
                        class="filter-select" />
                    </div>
                    <div class="filter-group">
                      <label class="filter-label">Sort by</label>
                      <b-form-select v-model="trialistsSortBy" :options="trialistSortOptions"
                        class="filter-select" />
                    </div>
                    <button v-if="trialistsFilterCoach || trialistsFilterTeam || trialistsFilterStatus"
                      type="button" class="filter-reset"
                      @click="trialistsFilterCoach = ''; trialistsFilterTeam = ''; trialistsFilterStatus = ''">
                      <i class="bi bi-x-circle" aria-hidden="true"></i> Reset
                    </button>
                  </div>
                </div>

                <div class="leads-count">
                  <span v-if="filteredTrialistsList.length === liveTrialistsData.length">
                    {{ liveTrialistsData.length }} trialist{{ liveTrialistsData.length === 1 ? '' : 's' }}
                  </span>
                  <span v-else>
                    Showing {{ filteredTrialistsList.length }} of {{ liveTrialistsData.length }} trialists
                  </span>
                </div>

                <div v-if="filteredTrialistsList.length === 0" class="empty-state">
                  <img src="../img/emptyStateIllustration.svg" />
                  <p>No trialists match your current filters.</p>
                  <button type="button" class="filter-reset"
                    @click="trialistsFilterCoach = ''; trialistsFilterTeam = ''; trialistsFilterStatus = ''">
                    <i class="bi bi-x-circle" aria-hidden="true"></i> Clear filters
                  </button>
                </div>

                <template v-else>
                  <template v-for="groupKey in ['ending-soon', 'active', 'ended']" :key="groupKey">
                    <template v-if="!trialistsFilterStatus && groupedTrialists[groupKey].length > 0">
                      <h3 class="trialist-group-heading" :class="`group-${groupKey}`">
                        <span class="trialist-group-pill">
                          <span v-if="groupKey === 'ending-soon'">Ending soon (≤7 days)</span>
                          <span v-else-if="groupKey === 'active'">Active</span>
                          <span v-else>Ended</span>
                          <span class="group-count">{{ groupedTrialists[groupKey].length }}</span>
                        </span>
                      </h3>
                      <div v-for="trialist in groupedTrialists[groupKey]" :value="trialist.id" :key="trialist.id"
                        class="player-profile-details">
                        <template v-if="trialist">
                          <div class="user-photo-info">
                            <img v-if="
                              trialist.userImage !== '' &&
                              trialist.userImage !== null
                            " :src="getImgUrl(trialist.userImage)" alt="" />
                            <TenantLogo v-else logo-class="login-logo" class="player-photo" />
                          </div>
                          <div class="user-info">
                            <h2>
                              {{ trialist.name }}
                              <span class="trial-status-pill" :class="`status-${trialStatusFor(trialist)}`">
                                {{ trialStatusLabel(trialist) }}
                              </span>
                            </h2>
                            <p>Position: {{ trialist.position }}</p>
                            <p>Team: {{ getTeamName(trialist.teamId) }}</p>
                            <p>Trial length: {{ trialLength(trialist.trial_length) }}</p>
                            <p>Trial time left:
                              {{ getTimeLeftInTrial(trialist.trial_start_date, trialist.trial_length) }}
                            </p>
                            <p>Trial score: {{ totalScore(trialist) }} / 30</p>
                            <p>Coach: {{ trialist.coach_name }}</p>
                            <p :class="`outCome-${outComeText(trialist.outcome)}`">
                              {{ outComeText(trialist.outcome) }}
                            </p>
                          </div>

                          <div class="player-game-responce">
                            <b-button variant="outline-warning" v-b-tooltip.hover title="Edit"
                              @click="selectedUseChanged(trialist, 'trialist')"><i class="bi bi-pencil"
                                aria-hidden="true"></i></b-button>

                            <b-button @click="confirmDeleteTrialist(trialist)" variant="outline-danger"
                              v-b-tooltip.hover title="Delete trialist"><i class="bi bi-trash"
                                aria-hidden="true"></i></b-button>
                            <b-button @click="extendTrialModal(trialist)" variant="outline-success" v-b-tooltip.hover
                              title="Extend trial"><i class="bi bi-alarm" aria-hidden="true"></i></b-button>
                          </div>
                        </template>
                      </div>
                    </template>
                  </template>

                  <template v-if="trialistsFilterStatus">
                    <div v-for="trialist in filteredTrialistsList" :value="trialist.id" :key="trialist.id"
                      class="player-profile-details">
                      <template v-if="trialist">
                        <div class="user-photo-info">
                          <img v-if="
                            trialist.userImage !== '' &&
                            trialist.userImage !== null
                          " :src="getImgUrl(trialist.userImage)" alt="" />
                          <TenantLogo v-else logo-class="login-logo" class="player-photo" />
                        </div>
                        <div class="user-info">
                          <h2>
                            {{ trialist.name }}
                            <span class="trial-status-pill" :class="`status-${trialStatusFor(trialist)}`">
                              {{ trialStatusLabel(trialist) }}
                            </span>
                          </h2>
                          <p>Position: {{ trialist.position }}</p>
                          <p>Team: {{ getTeamName(trialist.teamId) }}</p>
                          <p>Trial length: {{ trialLength(trialist.trial_length) }}</p>
                          <p>Trial time left:
                            {{ getTimeLeftInTrial(trialist.trial_start_date, trialist.trial_length) }}
                          </p>
                          <p>Trial score: {{ totalScore(trialist) }} / 30</p>
                          <p>Coach: {{ trialist.coach_name }}</p>
                          <p :class="`outCome-${outComeText(trialist.outcome)}`">
                            {{ outComeText(trialist.outcome) }}
                          </p>
                        </div>

                        <div class="player-game-responce">
                          <b-button variant="outline-warning" v-b-tooltip.hover title="Edit"
                            @click="selectedUseChanged(trialist, 'trialist')"><i class="bi bi-pencil"
                              aria-hidden="true"></i></b-button>

                          <b-button @click="confirmDeleteTrialist(trialist)" variant="outline-danger"
                            v-b-tooltip.hover title="Delete trialist"><i class="bi bi-trash"
                              aria-hidden="true"></i></b-button>
                          <b-button @click="extendTrialModal(trialist)" variant="outline-success" v-b-tooltip.hover
                            title="Extend trial"><i class="bi bi-alarm" aria-hidden="true"></i></b-button>
                        </div>
                      </template>
                    </div>
                  </template>
                </template>
              </section>

              <div class="empty-state" v-else>
                <img src="../img/emptyStateIllustration.svg" />
                <p>No trialists yet. Click "Add trialist" to create one.</p>
              </div>
            </div>
          </div>

          <div class="current-week-tab trialist-tab tab-content" v-if="selectedTab === 'signed-tab'">
            <div>
              <section class="player-profile-wrapper" v-if="signedtrialistsData.length > 0">
                <div v-for="trialist in signedtrialistsData" :value="trialist.id" :key="trialist.id"
                  class="player-profile-details">
                  <template v-if="trialist">
                    <div class="user-photo-info">
                      <img v-if="
                        trialist.userImage !== '' &&
                        trialist.userImage !== null
                      " :src="getImgUrl(trialist.userImage)" alt="" />
                      <TenantLogo v-else logo-class="login-logo" />
                    </div>
                    <div class="user-info">
                      <h2>{{ trialist.name }}</h2>
                      <p>Position: {{ trialist.position }}</p>
                      <p>Team: {{ getTeamName(trialist.teamId) }}</p>
                      <p>
                        Trial length: {{ trialLength(trialist.trial_length) }}
                      </p>

                      <p>Trial score: {{ totalScore(trialist) }} / 30</p>
                      <p>Coach: {{ trialist.coach_name }}</p>
                      <p :class="`outCome-${outComeText(trialist.outcome)}`">
                        {{ outComeText(trialist.outcome) }}
                      </p>
                    </div>

                    <div class="player-game-responce">
                      <b-button variant="outline-warning" v-b-tooltip.hover title="Edit" @click="
                        selectedUseChanged(trialist, 'trialist', 'signed')
                        "><i class="bi bi-pencil" aria-hidden="true"></i></b-button>

                      <b-button @click="removeTrialistFromDB(trialist.id)" variant="outline-danger" v-b-tooltip.hover
                        title="Delete trialist"><i class="bi bi-trash" aria-hidden="true"></i></b-button>
                    </div>
                  </template>
                </div>
              </section>

              <div class="empty-state" v-else>
                <img src="../img/emptyStateIllustration.svg" />
                <p>Please select a trialist to view their information</p>
              </div>
            </div>
          </div>

          <div class="current-week-tab trialist-tab tab-content" v-if="selectedTab === 'gamesAttened-tab'">
            <div class="add-new-lead-wrapper" style="width: 100%">
              <button class="green-button add-new-lead" @click="addEditGameAttendanceModel()">
                Add game attendance
              </button>
            </div>

            <div class="lead-tabs-filters" v-if="allScoutGameAttendanceReports.length > 0">
              <div class="lead-filters">
                <div class="filter-group">
                  <label class="filter-label">Scout</label>
                  <b-form-select v-model="gamesFilterScout" :options="scoutOptions" class="filter-select" />
                </div>
                <div class="filter-group">
                  <label class="filter-label">Age group</label>
                  <b-form-select v-model="gamesFilterAgeGroup" :options="gamesAgeGroupOptions" class="filter-select" />
                </div>
                <button v-if="gamesFilterScout || gamesFilterAgeGroup" type="button"
                  class="filter-reset" @click="gamesFilterScout = ''; gamesFilterAgeGroup = ''">
                  <i class="bi bi-x-circle" aria-hidden="true"></i> Reset
                </button>
              </div>
            </div>

            <div v-if="allScoutGameAttendanceReports.length > 0" class="leads-count">
              <span v-if="filteredGameAttendanceReports.length === allScoutGameAttendanceReports.length">
                {{ allScoutGameAttendanceReports.length }} game{{ allScoutGameAttendanceReports.length === 1 ? '' : 's' }}
              </span>
              <span v-else>
                Showing {{ filteredGameAttendanceReports.length }} of {{ allScoutGameAttendanceReports.length }} games
              </span>
            </div>

            <div class="table-wrapper table-scroll">
              <div v-if="filteredGameAttendanceReports.length > 0" class="">
                <vue-good-table style-class="vgt-table bordered" :columns="columnsScoutGameAttendance"
                  :rows="filteredGameAttendanceReports" :search-options="{
                    enabled: true,
                    placeholder: 'Search games',
                  }" :sort-options="{
                    enabled: true,
                    initialSortBy: { field: 'matchDate', type: 'desc' },
                  }">
                  <template v-slot:table-row="props">
                    <span v-if="props.column.field == 'scoutId'">
                      <span>{{ getScoutName(props.row.scoutId) }}</span>
                    </span>

                    <span v-else-if="props.column.field == 'teams'">
                      <span>{{ props.row.homeTeam }} v
                        {{ props.row.awayTeam }}</span>
                    </span>

                    <span v-else-if="props.column.field == 'matchDate'">
                      <span>{{ formatDateForHuman(props.row.matchDate) }}</span>
                    </span>

                    <span v-else-if="props.column.field == 'competition'">
                      <span class="competition-info">
                        <div v-if="props.row.competition || (props.row.gameContext && props.row.gameContext.competition)">
                          <strong>{{
                            props.row.competition || props.row.gameContext.competition
                            }}</strong>
                          <small v-if="props.row.division || (props.row.gameContext && props.row.gameContext.division)"
                            class="text-muted d-block">
                            {{ props.row.division || props.row.gameContext.division }}
                          </small>
                          <span v-if="props.row.game_type || (props.row.gameContext && props.row.gameContext.gameType)"
                            :class="getGameTypeClass(props.row.game_type || props.row.gameContext.gameType)"
                            class="badge badge-sm">
                            {{ props.row.game_type || props.row.gameContext.gameType }}
                          </span>
                        </div>
                        <span v-else class="lead-cell-empty">—</span>
                      </span>
                    </span>

                    <span v-else-if="props.column.field == 'result'">
                      <span class="result-info">
                        <strong v-if="
                          (props.row.home_score !== null && props.row.home_score !== undefined) ||
                          (props.row.away_score !== null && props.row.away_score !== undefined) ||
                          (props.row.gameContext && props.row.gameContext.gameResult &&
                            (props.row.gameContext.gameResult.homeScore !== null ||
                             props.row.gameContext.gameResult.awayScore !== null))
                        " class="score">
                          {{ props.row.home_score ?? (props.row.gameContext && props.row.gameContext.gameResult ? props.row.gameContext.gameResult.homeScore : 0) ?? 0 }}
                          -
                          {{ props.row.away_score ?? (props.row.gameContext && props.row.gameContext.gameResult ? props.row.gameContext.gameResult.awayScore : 0) ?? 0 }}
                        </strong>
                        <span v-else class="lead-cell-empty">—</span>
                      </span>
                    </span>

                    <span v-else-if="props.column.field == 'players_assessed'">
                      <span class="players-assessed-pill"
                        :class="{ 'no-players': playersAssessedCount(props.row.id) === 0 }">
                        {{ playersAssessedCount(props.row.id) }}
                      </span>
                    </span>

                    <span class="action-buttons" v-else-if="props.column.field == 'actions'">
                      <div class="player-game-responce">
                        <b-button @click="
                          addEditGameAttendanceModel(props.row.id, 'edit')
                          " variant="outline-success" v-b-tooltip.hover title="View / edit report">
                          <i class="bi bi-eye" aria-hidden="true"></i>
                        </b-button>
                        <b-button @click="confirmDeleteGameAttendance(props.row)" variant="outline-danger"
                          v-b-tooltip.hover title="Delete game">
                          <i class="bi bi-trash" aria-hidden="true"></i>
                        </b-button>
                      </div>
                    </span>
                  </template>
                </vue-good-table>
              </div>
              <div v-else-if="allScoutGameAttendanceReports.length === 0">
                <div class="empty-state">
                  <img src="../img/emptyStateIllustration.svg" />
                  <p>No game attendance reports yet. Click "Add game attendance" to create one.</p>
                </div>
              </div>
              <div v-else>
                <div class="empty-state">
                  <img src="../img/emptyStateIllustration.svg" />
                  <p>No games match your current filters.</p>
                  <button type="button" class="filter-reset"
                    @click="gamesFilterScout = ''; gamesFilterAgeGroup = ''">
                    <i class="bi bi-x-circle" aria-hidden="true"></i> Clear filters
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="current-week-tab trialist-tab tab-content scout-fixtures-tab"
            v-if="selectedTab === 'fixtures-tab'">
            <p style="width: 100%">Upcoming games</p>

            <div class="games" v-if="allGamesData.length > 0">
              <div v-for="(game, index) in allGamesData" :key="index" class="game" :id="game.id + index">
                <div class="game-wrapper">
                  <div class="match-details">
                    <span>{{ game.gameType }}</span>
                    <p v-if="game.result_added == '0'" class="teams">
                      {{ getTeamName(game.teamId) }} <span>v</span>
                      {{ game.opposition }}
                    </p>
                    <p class="game-info">
                      <span><i class="bi bi-calendar3"></i>{{ getDayAndDate(game.matchDate) }}</span>
                      <span><i class="bi bi-stopwatch"></i>{{
                        formatISOStringDateAndTime(game.kickOffTime)
                      }}</span>
                    </p>
                    <p style="margin-bottom: 1rem">
                      <span class="venue-text"><i class="bi bi-map" aria-hidden="true"></i> Venue:
                        {{ game.address }} {{ game.postCode }}</span>
                    </p>
                    <p style="margin-bottom: 1rem">
                      {{ game.locationName }} -
                      <a class="map-link" v-if="game.postCode !== ''"
                        :href="`https://maps.google.fr/maps?q=${game.postCode}`" target="_blank"><i
                          class="bi bi-pin-map" aria-hidden="true"></i> Map</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div v-else>
              <div class="empty-state">
                <img src="../img/emptyStateIllustration.svg" />
                <p>No Upcoming Games.</p>
              </div>
            </div>
          </div>
          <div class="current-week-tab trialist-tab tab-content analytics-dashboard-tab"
            v-if="selectedTab === 'analytics-tab' && isAnalyticsDashboardVisible">
            <div class="analytics-dashboard-wrapper">
              <!-- Dashboard Overview -->
              <AnalyticsDashboardOverview v-if="analyticsView === 'overview'" :analyticsData="analyticsData"
                :userData="userData" :trialistsData="trialistsData" :signedTrialistsData="signedtrialistsData"
                :archivedTrialistsData="archivedtrialistsData" :allScoutsData="allScoutsData"
                :allAssessmentsData="allAssessmentsData" :gameAttendanceData="allScoutGameAttendanceReports"
                @navigate="handleAnalyticsNavigation" />

              <!-- Performance Benchmarking Detail -->
              <PerformanceBenchmarking v-if="analyticsView === 'performance-benchmarking'"
                :analyticsData="analyticsData" :dateRange="dateRange" :filters="analyticsFilters"
                :allAssessmentsData="allAssessmentsData" :signedTrialistsData="signedtrialistsData"
                :gameAttendanceData="allScoutGameAttendanceReports" @data-point-click="handleAnalyticsDataPointClick"
                @navigate-back="analyticsView = 'overview'" />

              <!-- Player Demographics Detail -->
              <PlayerDemographicsAnalysis v-if="analyticsView === 'player-demographics'" :analyticsData="analyticsData"
                :dateRange="dateRange" :filters="analyticsFilters"
                :allAssessmentsData="allAssessmentsData" :signedTrialistsData="signedtrialistsData"
                :gameAttendanceData="allScoutGameAttendanceReports" :playerLeadsData="allPlayerLeadsData"
                :trialistsData="trialistsData"
                @data-point-click="handleAnalyticsDataPointClick"
                @navigate-back="analyticsView = 'overview'" />

              <!-- Scout Activity Detail -->
              <ScoutActivityAnalytics v-if="analyticsView === 'scout-activity'" :analyticsData="analyticsData"
                :dateRange="dateRange" :filters="analyticsFilters" :gameAttendanceData="allScoutGameAttendanceReports"
                :allScoutsData="allScoutsData" @data-point-click="handleAnalyticsDataPointClick"
                @navigate-back="analyticsView = 'overview'" />
            </div>
          </div>

          <div class="current-week-tab trialist-tab tab-content scout-id-tab" v-if="selectedTab === 'id-tab'">
            <div class="table-scroll">
              <div class="scout-id">
                <h2>
                  <TenantLogo logo-class="login-logo" />
                  {{ setClubName() }} scout
                </h2>

                <div class="id-info-wrapper">
                  <div class="user-photo-info">
                    <img v-if="
                      userData.userImage !== '' && userData.userImage !== null
                    " :src="getImgUrl(userData.userImage)" alt="" />
                    <TenantLogo v-else logo-class="login-logo" />
                  </div>

                  <div class="id-info">
                    <p>name: {{ userData.name }}</p>
                    <p>Scouting representative</p>
                    <p>{{ setTodaysDate() }}</p>
                  </div>
                </div>
                <TenantLogo logo-class="background-logo" />
              </div>
            </div>
          </div>

          <div class="current-week-tab trialist-tab tab-content" v-if="selectedTab === 'archived-tab'">
            <div>
              <section class="player-profile-wrapper" v-if="archivedtrialistsData.length > 0">
                <div v-for="trialist in archivedtrialistsData" :value="trialist.id" :key="trialist.id"
                  class="player-profile-details">
                  <template v-if="trialist">
                    <div class="user-photo-info">
                      <img v-if="
                        trialist.userImage !== '' &&
                        trialist.userImage !== null
                      " :src="getImgUrl(trialist.userImage)" alt="" />
                      <TenantLogo v-else logo-class="login-logo" />
                    </div>
                    <div class="user-info">
                      <h2>{{ trialist.name }}</h2>
                      <p>Position: {{ trialist.position }}</p>
                      <p>Team: {{ getTeamName(trialist.teamId) }}</p>
                      <p>
                        Trial length: {{ trialLength(trialist.trial_length) }}
                      </p>
                      <p>
                        Trial time left:
                        {{
                          getTimeLeftInTrial(
                            trialist.trial_start_date,
                            trialist.trial_length,
                          )
                        }}
                      </p>

                      <p>Trial score: {{ totalScore(trialist) }} / 30</p>
                      <p>Coach: {{ trialist.coach_name }}</p>
                      <p :class="`outCome-${outComeText(trialist.outcome)}`">
                        {{ outComeText(trialist.outcome) }}
                      </p>
                    </div>

                    <div class="player-game-responce">
                      <b-button variant="outline-warning" v-b-tooltip.hover title="Edit"
                        @click="selectedUseChanged(trialist, 'trialist')"><i class="bi bi-pencil"
                          aria-hidden="true"></i></b-button>

                      <b-button @click="removeTrialistFromDB(trialist.id)" variant="outline-danger" v-b-tooltip.hover
                        title="Delete trialist"><i class="bi bi-trash" aria-hidden="true"></i></b-button>
                    </div>
                  </template>
                </div>
              </section>

              <div class="empty-state" v-else>
                <img src="../img/emptyStateIllustration.svg" />
                <p>Please select a trialist to view their information</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <b-modal v-if="showViewLead" v-model="showViewLead" size="lg" title="Lead information" :hide-footer="true">
      <div class="form lead-form-wrapper">
        <div class="form__item">
          <label class="form__label" for="name">Player name</label>
          <p>{{ selectedLeadData.name }}</p>
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Club name</label>
          <p>{{ selectedLeadData.club_name }}</p>
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Age group</label>
          <p>{{ selectedLeadData.age_group }}</p>
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Position</label>
          <p>{{ selectedLeadData.playing_position }}</p>
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Status</label>
          <p>{{ selectedLeadData.state }}</p>
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Scout name</label>
          <p>{{ selectedLeadData.scout_name }}</p>
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Scout notes / videos</label>
          <p v-html="selectedLeadData.notes"></p>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showAddEditLead" v-model="showAddEditLead" size="lg"
      :title="editMode === false ? 'Add new lead' : 'Edit lead details'" :hide-footer="true">
      <div class="form lead-form-wrapper">
        <div class="form__item">
          <label class="form__label" for="name">Player name</label>
          <input class="form__input" type="text" v-model="selectedLeadData.name" id="name" />
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Current club name</label>
          <input class="form__input" type="text" v-model="selectedLeadData.club_name" id="name" />
        </div>

        <div class="form__item">
          <label class="form__label" for="leadPhone">Phone number</label>
          <input class="form__input" type="text" v-model="selectedLeadData.phone" id="leadPhone" />
        </div>

        <div class="form__item">
          <label class="form__label" for="dob">DOB</label>

          <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="selectedLeadData.dob" name="datepicker" id="dob" input-class="input-class"
            :enable-time-picker="false" />
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Age group</label>

          <VueDatePicker :locale="'en-GB'" format="yyyy" text-input-options="{ format: 'yyyy' }"
            v-model="selectedLeadData.age_group" year-picker name="datepicker" id="input-id" input-class="input-class"
            :enable-time-picker="false" />
        </div>

        <div class="form__item">
          <label class="form__label" for="position">Position</label>
          <select id="position" v-model="selectedLeadData.playing_position">
            <option disabled value="">Please select a position</option>
            <option value="Goalkeeper">Goalkeeper</option>
            <option value="Right Full-back (Wingback)">
              Right Full-back (Wingback)
            </option>
            <option value="Left Full-back (Wingback)">
              Left Full-back (Wingback)
            </option>
            <option value="Center-back left">Center-back left</option>
            <option value="Center-back right">Center-back right</option>
            <option value="Defensive Midfielder">Defensive Midfielder</option>
            <option value="Centre midfield">Centre midfield</option>
            <option value="Centre Defensive Midfield">
              Centre Defensive Midfield
            </option>
            <option value="Right Midfielder (Winger)">
              Right Midfielder (Winger)
            </option>
            <option value="Left Midfielder (Winger)">
              Left Midfielder (Winger)
            </option>
            <option value="Attacking Midfielder">Attacking Midfielder</option>
            <option value="Center Forward (Striker) left">
              Center Forward (Striker) left
            </option>
            <option value="Center Forward (Striker) right">
              Center Forward (Striker) right
            </option>
            <option value="Plays in more than one position">
              Play's in more than one position
            </option>
          </select>
        </div>

        <div class="form__item">
          <label class="form__label" for="position">Status</label>
          <select id="position" v-model="selectedLeadData.state">
            <option disabled value="">Please select a state</option>
            <option value="Still to Watch">Still to Watch</option>
            <option value="Invited for trial">Invited for trial</option>
            <option value="Need to watch again">Need to watch again</option>
            <option value="Trial invite refused">Trial invite refused</option>
            <option value="Player signed">Player signed</option>
            <option value="Not at the standard required">
              Not at the standard required
            </option>
          </select>
        </div>

        <div class="form__item">
          <label class="form__label" for="position">Assign scout</label>
          <select id="position" @change="populateAssignedScout($event)">
            <option disabled value="">Please select a state</option>
            <option value="">Mark as unassigned</option>
            <option v-for="scout in allScoutsData" :key="scout.id" :value="scout.id"
              :selected="scout.id === selectedLeadData.scoutId">
              {{ scout.name }}
            </option>
          </select>
        </div>

        <div class="form__item">
          <label class="form__label" for="leadLastContact">Last contacted</label>
          <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="selectedLeadData.last_contact_date" name="leadLastContact" id="leadLastContact"
            input-class="input-class" :enable-time-picker="false" />
        </div>

        <div class="form__item">
          <label class="form__label" for="leadFollowUp">Next follow-up</label>
          <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="selectedLeadData.next_follow_up_date" name="leadFollowUp" id="leadFollowUp"
            input-class="input-class" :enable-time-picker="false" />
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Scout notes / videos</label>
          <VoiceDictationButton label="Dictate notes"
            @transcript="appendTranscriptToLeadNotes($event)" />
          <QuillEditor v-model:content="selectedLeadData.notes" contentType="html" theme="snow" />
        </div>

        <div class="form__item" v-if="editMode === false">
          <button @click="addNewPlayerLead()" type="submit" class="green-button">
            Add player lead
          </button>
        </div>

        <div class="form__item" v-else>
          <button @click="editPlayerLead()" type="submit" class="green-button">
            Edit player lead
          </button>
        </div>
      </div>
    </b-modal>
    <b-modal v-if="showAddEditTrialist" v-model="showAddEditTrialist" size="lg"
      :title="editMode === false ? 'Add new trialist' : 'Edit trialist details'" :hide-footer="true"
      @hide="resetTrialistData()">
      <div class="invite-panel" v-if="editMode === false">
        <h3 class="invite-panel__title">Invite the family to sign up</h3>
        <p class="invite-panel__hint">
          Pick the team and trial details, then send the family a secure link.
          They fill in their own details and the trialist appears here once
          they finish. Links expire after 14 days and work once.
        </p>

        <div class="invite-panel__fields">
          <div class="form__item">
            <label class="form__label" for="inviteTeam">Team</label>
            <select id="inviteTeam" v-model="invite.teamId">
              <option disabled value="">Please select a team</option>
              <option v-for="team in clubTeams" :value="team.id" :key="team.id">
                {{ team.teamName }}
              </option>
            </select>
          </div>

          <div class="form__item">
            <label class="form__label" for="inviteTrialLength">Trial length</label>
            <select id="inviteTrialLength" v-model="invite.trialLength">
              <option value="3">3 weeks</option>
              <option value="6">6 weeks</option>
              <option value="12">12 weeks</option>
            </select>
          </div>

          <div class="form__item">
            <label class="form__label" for="inviteStartDate">Trial start date</label>
            <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" v-model="invite.startDate" name="inviteStartDate"
              id="inviteStartDate" :enable-time-picker="false" />
          </div>
        </div>

        <div class="invite-panel__email">
          <input class="form__input" type="email" v-model="invite.recipientEmail" id="inviteRecipientEmail"
            placeholder="parent@example.com" aria-label="Family email address" />
          <b-button :variant="invite.emailSent ? 'success' : 'primary'"
            :disabled="invite.emailSending || invite.teamId === '' || invite.recipientEmail === ''"
            @click="emailTrialInvite()">
            {{ invite.emailSending ? "Sending…" : invite.emailSent ? "Sent ✓" : "Email invite" }}
          </b-button>
        </div>

        <div class="invite-panel__actions">
          <b-button variant="outline-primary" :disabled="invite.loading || invite.teamId === ''"
            @click="generateTrialInvite()">
            {{ invite.loading ? "Creating link…" : "Or create a link to copy" }}
          </b-button>
          <template v-if="invite.link">
            <span class="invite-panel__link">{{ invite.link }}</span>
            <b-button :variant="invite.copied ? 'success' : 'outline-primary'" @click="copyInviteLink()">
              {{ invite.copied ? "Copied ✓" : "Copy link" }}
            </b-button>
          </template>
        </div>
        <p class="invite-panel__error" v-if="invite.error">{{ invite.error }}</p>

        <p class="invite-panel__divider">
          Or add the trialist's details yourself below.
        </p>
      </div>
      <form class="form" @submit.prevent="">
        <div class="form__item">
          <label class="form__label" for="name">Name</label>
          <input class="form__input" type="text" v-model="selectedUserData.name" id="name" />
        </div>

        <div class="form__item">
          <label class="form__label" for="phone">phone</label>
          <input type="number" v-model="selectedUserData.phone" id="phone" />
        </div>

        <div class="form__item">
          <label class="form__label" for="email">Email Address</label>
          <input :class="validationValues.email.state === true
            ? 'danger-outline form__input'
            : 'form__input'
            " @change="checkChangeVlaidation(selectedUserData.email, 'email')" type="email"
            v-model="selectedUserData.email" id="email" />
          <p class="invalid-feedback" v-if="validationValues.email.state === true">
            {{ validationValues.email.text }}
          </p>
        </div>

        <div v-if="editMode === false" class="form__item">
          <label class="form__label" for="password">Password</label>
          <input type="password" v-model="selectedUserData.password" id="password" />
        </div>

        <div class="form__item uk-form dob">
          <label class="form__label" for="dob">DOB</label>
          <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="selectedUserData.dob" name="dob" id="dob" :enable-time-picker="false" />
        </div>

        <ImageUploader @populateImageObjectFromUpload="imageUploadFileDataChnged($event)"
          @populateImageFileDataFromUpload="
            imageUploadObjectDataChanged($event)
            " @clearImageDataFromUpload="clearImageDataFromUpload()" :imageURL="selectedUserData.userImage" />

        <div class="form__item">
          <label class="form__label" for="position">Position</label>
          <select id="position" v-model="selectedUserData.position">
            <option disabled value="">Please select a position</option>
            <option value="Goalkeeper">Goalkeeper</option>
            <option value="Right Full-back (Wingback)">
              Right Full-back (Wingback)
            </option>
            <option value="Left Full-back (Wingback)">
              Left Full-back (Wingback)
            </option>
            <option value="Center-back left">Center-back left</option>
            <option value="Center-back right">Center-back right</option>
            <option value="Defensive Midfielder">Defensive Midfielder</option>
            <option value="Centre midfield">Centre midfield</option>
            <option value="Centre Defensive Midfield">
              Centre Defensive Midfield
            </option>
            <option value="Right Midfielder (Winger)">
              Right Midfielder (Winger)
            </option>
            <option value="Left Midfielder (Winger)">
              Left Midfielder (Winger)
            </option>
            <option value="Attacking Midfielder">Attacking Midfielder</option>
            <option value="Center Forward (Striker) left">
              Center Forward (Striker) left
            </option>
            <option value="Center Forward (Striker) right">
              Center Forward (Striker) right
            </option>
            <option value="I played more than one position">
              I played more than one position
            </option>
          </select>
        </div>

        <div class="form__item">
          <label class="form__label" for="coachSelect">Select team</label>
          <select v-model="selectedUserData.teamId" @change="
            selectTeamSelectCoach('selectedUserData', selectedUserData.teamId)
            " id="coachSelect">
            <option disabled value="">Please select a team</option>
            <option v-for="team in clubTeams" :value="team.id" :key="team.id">
              {{ team.teamName }}
            </option>
          </select>
        </div>

        <!--<div class="form__item">
            <label class="form__label" for="coachSelect">Select coach</label>
            <select v-model="selectedUserData.coach_id" id="coachSelect">
              <option disabled value="">Please select a coach</option>
              <option
                v-for="coach in coachData"
                :value="coach.id"
                :key="coach.id"
              >
                {{ coach.name }}
              </option>
            </select>
          </div>-->

        <div class="form__item uk-form address">
          <label class="form__label" for="address">Address</label>

          <b-form-textarea id="address" v-model="selectedUserData.address" placeholder="" rows="8"></b-form-textarea>
        </div>

        <div class="form__item uk-form post_code">
          <label class="form__label" for="post_code">Post code</label>

          <input class="form__input" type="text" v-model="selectedUserData.post_code" id="post_code" />
        </div>

        <div class="form__item uk-form place_of_birth">
          <label class="form__label" for="place_of_birth">Place of birth</label>

          <input class="form__input" type="text" v-model="selectedUserData.place_of_birth" id="place_of_birth" />
        </div>

        <div class="form__item uk-form place_of_birth">
          <label class="form__label" for="country_of_birth">Country of birth</label>

          <input class="form__input" type="text" v-model="selectedUserData.country_of_birth" id="place_of_birth" />
        </div>

        <div class="form__item uk-form nationality">
          <label class="form__label" for="nationality">Nationality</label>

          <input class="form__input" type="text" v-model="selectedUserData.nationality" id="nationality" />
        </div>

        <div class="form__item uk-form previous_club">
          <label class="form__label" for="previous_club">Boys club (current or most recent)</label>

          <input class="form__input" type="text" v-model="selectedUserData.previous_club" id="previous_club" />
        </div>

        <div class="form__item">
          <label class="form__label" for="parent_name">Parent name</label>
          <input class="form__input" type="text" v-model="selectedUserData.parent_name" id="parent_name" />
        </div>

        <div class="form__item">
          <label class="form__label" for="emergency_contact_one">Emergency Contact</label>
          <input class="form__input" type="text" v-model="selectedUserData.emergency_contact_one"
            id="emergency_contact_one" />
        </div>

        <div class="form__item">
          <label class="form__label" for="emergency_contact_two">Emergency Contact two</label>
          <input class="form__input" type="text" v-model="selectedUserData.emergency_contact_two"
            id="emergency_contact_two" />
        </div>

        <div class="form__item">
          <label class="form__label" for="position">Permission</label>
          <b-form-checkbox id="checkbox-1" v-model="selectedUserData.permission" name="checkbox-1">
            Permission to take photographs
          </b-form-checkbox>
        </div>

        <div class="form__item">
          <label class="form__label" for="position">Medical information</label>

          <b-form-textarea id="textarea" v-model="selectedUserData.medical" placeholder="" rows="8"></b-form-textarea>
        </div>

        <div class="form__item">
          <label class="form__label" for="coachSelect">Select trial length</label>
          <select v-model="selectedTrialistsData.trial_length" id="trialLengthSelect">
            <option disabled value="">Please select a trial length</option>
            <option value="3">3 weeks</option>
            <option value="6">6 weeks</option>
            <option value="12">12 weeks</option>
          </select>
        </div>

        <div class="form__item uk-form dob">
          <label class="form__label" for="trialStartDate">Trial start date</label>
          <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="selectedTrialistsData.trial_start_date" name="trialStartDate" id="trialStartDate"
            :enable-time-picker="false" />
        </div>

        <div class="form__item">
          <label class="form__label" for="preferredFootSelect">Select preferred foot</label>
          <select v-model="selectedTrialistsData.preferred_foot" id="preferredFootSelect">
            <option disabled value="">Please select a preferred foot</option>
            <option value="Right">Right</option>
            <option value="Left">Left</option>
            <option value="Both">Both</option>
          </select>
        </div>

        <div v-if="editMode === false" class="form__item">
          <button @click="addEditCoachDetails()" class="green-button">
            Add trialist
          </button>
        </div>

        <div v-else class="form__item">
          <button @click="saveTrialistPerformanceReview()" class="green-button">
            Edit trialist
          </button>
        </div>
      </form>
    </b-modal>

    <b-modal v-if="showViewTrialistLeadPlayerProfile" v-model="showViewTrialistLeadPlayerProfile" size="lg"
      modal-class="modal-fullscreen trialist-player-profile" title="Player information" :hide-footer="true"
      @hide="resetSelectedCoach()">
      <div class="profile-page">
        <div class="intro-header" style="width: 100%">
          <img class="main-image-top" src="./img/pitch.svg" alt="pitch" />
          <div class="user-photo-info">
            <img id="user_photo" v-if="
              selectedUserData.userImage !== '' &&
              selectedUserData.userImage !== null
            " :src="getImgUrl(selectedUserData.userImage)" alt="" />
            <TenantLogo v-else logo-class="user_photo" />
            <h1>
              <span> Player Profile</span><br />
              for {{ selectedUserData.name }}
            </h1>
          </div>

          <div class="sessons-header">
            <div class="header-buttons">
              <h2>
                <img id="small_shirt" src="./img/player-shirt-blue.svg" />Player
                profile
              </h2>
              <span class="week-selected-dates-text">
                <span class="selected-user">Name:<span class="highlighted-text">{{
                  selectedUserData.name
                    }}</span></span>
                <span class="selected-user">DOB:<span class="highlighted-text">{{
                  dayMonthYear(selectedUserData.dob)
                    }}</span></span>
                <span class="selected-user">Age group:<span class="highlighted-text">{{
                  selectedUserData.age_group
                    }}</span></span>
                <span class="selected-user">Nationality:<span class="highlighted-text">{{
                  selectedUserData.nationality
                    }}</span></span>
              </span>
            </div>
          </div>

          <div class="out-come-buttons" v-if="
            selectedTrialistsData.id !== undefined &&
            selectedTrialistsData.outcome === 0
          ">
            <button v-if="userData.userType === 'Admin'" @click="recordOutcome('1')" class="green-button">
              <i class="bi bi-pen" aria-hidden="true"></i> Sign
            </button>
            <button v-if="userData.userType === 'Admin'" @click="recordOutcome('2')" class="red-button">
              <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
              Reject
            </button>

            <!--<button @click="recordOutcome('3')" class="yellow-button">Waiting list</button>-->
            <p>
              When you sign a player, they will be automatically registered with
              the team they tried out for.
            </p>
          </div>
          <p class="player-status-text signed" v-if="selectedTrialistsData.outcome === 1">
            Player signed
          </p>
          <p class="player-status-text rejected" v-if="selectedTrialistsData.outcome === 2">
            Player rejected
          </p>
        </div>

        <ul class="tabs">
          <li class="tab-item">
            <a :class="selectedPlayerInfoTab === 'lead-tab'
              ? 'tab-link active'
              : 'tab-link'
              " @click="tabChange('selectedPlayerInfoTab', 'lead-tab')" href="javascript:;">Lead information</a>
          </li>
          <li class="tab-item" v-if="selectedTrialistsData.id !== undefined">
            <a :class="selectedPlayerInfoTab === 'trial-tab'
              ? 'tab-link active'
              : 'tab-link'
              " @click="tabChange('selectedPlayerInfoTab', 'trial-tab')" href="javascript:;">Trial information</a>
          </li>

          <li class="tab-item" v-if="selectedTrialistsData.id !== undefined">
            <a :class="selectedPlayerInfoTab === 'player-stats-tab'
              ? 'tab-link active'
              : 'tab-link'
              " @click="tabChange('selectedPlayerInfoTab', 'player-stats-tab')" href="javascript:;">Player stats</a>
          </li>
          <li class="tab-item" v-if="selectedTrialistsData.id !== undefined">
            <a :class="selectedPlayerInfoTab === 'player-history-tab'
              ? 'tab-link active'
              : 'tab-link'
              " @click="tabChange('selectedPlayerInfoTab', 'player-history-tab')" href="javascript:;">Player
              history</a>
          </li>
          <li class="tab-item" v-if="selectedTrialistsData.id !== undefined">
            <a :class="selectedPlayerInfoTab === 'player-documents-tab'
              ? 'tab-link active'
              : 'tab-link'
              " @click="
                tabChange('selectedPlayerInfoTab', 'player-documents-tab')
                " href="javascript:;">Documents</a>
          </li>
          <li class="tab-item" v-if="selectedTrialistsData.id !== undefined">
            <a :class="selectedPlayerInfoTab === 'player-videos-tab'
              ? 'tab-link active'
              : 'tab-link'
              " @click="tabChange('selectedPlayerInfoTab', 'player-videos-tab')" href="javascript:;">videos</a>
          </li>
          <li class="tab-item" v-if="selectedTrialistsData.id !== undefined">
            <a :class="selectedPlayerInfoTab === 'player-report-tab'
              ? 'tab-link active'
              : 'tab-link'
              " @click="tabChange('selectedPlayerInfoTab', 'player-report-tab')" href="javascript:;">Trialists full
              report</a>
          </li>
        </ul>

        <div class="tabs-wrapper">
          <div class="tab-content no-flex" v-if="selectedPlayerInfoTab === 'lead-tab'">
            <div class="games-info" v-if="Object.keys(selectedLeadData).length > 0">
              <b-button @click="
                openLeadsModal('add more lead information', selectedLeadData)
                " type="submit" v-b-tooltip.hover title="Add more lead information" variant="outline-success"><i
                  class="bi bi-plus" aria-hidden="true"></i>Add
              </b-button>
              <br />
              <br />

              <div class="form lead-form-wrapper">
                <div class="form__item">
                  <label class="form__label" for="name">Club name</label>
                  <p>{{ selectedLeadData.club_name }}</p>
                </div>

                <div class="form__item">
                  <label class="form__label" for="name">Phone number</label>
                  <p>{{ selectedLeadData.phone || "—" }}</p>
                </div>

                <div class="form__item">
                  <label class="form__label" for="name">Age group</label>
                  <p>{{ selectedLeadData.age_group }}</p>
                </div>

                <div class="form__item">
                  <label class="form__label" for="name">Position</label>
                  <p>{{ selectedLeadData.playing_position }}</p>
                </div>

                <div class="form__item">
                  <label class="form__label" for="name">Status</label>
                  <p>{{ selectedLeadData.state }}</p>
                </div>

                <div class="form__item">
                  <label class="form__label" for="name">Scout name</label>
                  <p>{{ selectedLeadData.scout_name }}</p>
                </div>

                <div class="form__item">
                  <label class="form__label" for="name">Scout notes</label>
                  <p v-html="selectedLeadData.notes"></p>
                </div>
              </div>

              <div class="player-game-responce">
                <b-button variant="outline-warning" @click="openLeadsModal('edit', selectedLeadData)"><i
                    class="bi bi-pencil" aria-hidden="true"></i>
                  Edit</b-button>
                <b-button @click="removePlayerLead(selectedLeadData.id)" variant="outline-danger"><i class="bi bi-trash"
                    aria-hidden="true"></i>
                  Delete</b-button>
              </div>

              <div class="other-leads-wrapper"
                v-if="metaDataReults && metaDataReults.some(l => l.name == selectedLeadData.name)">
                <h4>More lead information</h4>
                <template v-for="extraLeads in metaDataReults" :key="extraLeads.id">
                  <div class="other-leads" v-if="extraLeads.name == selectedLeadData.name">
                    <div class="leads-data">
                      <div class="form lead-form-wrapper">
                        <div class="form__item">
                          <label class="form__label" for="name">Club name</label>
                          <p>{{ extraLeads.club_name }}</p>
                        </div>

                        <div class="form__item">
                          <label class="form__label" for="name">Age group</label>
                          <p>{{ extraLeads.age_group }}</p>
                        </div>

                        <div class="form__item">
                          <label class="form__label" for="name">Position</label>
                          <p>{{ extraLeads.playing_position }}</p>
                        </div>

                        <div class="form__item">
                          <label class="form__label" for="name">Status</label>
                          <p>{{ extraLeads.state }}</p>
                        </div>

                        <div class="form__item">
                          <label class="form__label" for="name">Scout name</label>
                          <p>{{ extraLeads.scout_name }}</p>
                        </div>

                        <div class="form__item">
                          <label class="form__label" for="name">Scout notes</label>
                          <p v-html="extraLeads.notes"></p>
                        </div>
                      </div>

                      <div class="player-game-responce">
                        <b-button variant="outline-warning" @click="openLeadsModal('edit', extraLeads)"><i
                            class="bi bi-pencil" aria-hidden="true"></i>
                          Edit</b-button>
                        <b-button @click="removePlayerLead(extraLeads.id)" variant="outline-danger"><i class="bi bi-trash"
                            aria-hidden="true"></i>
                          Delete</b-button>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <div class="scout-assessments-wrapper" style="margin-top: 2rem;">
                <div style="display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
                  <h4 style="margin: 0;">Scout assessments
                    <span v-if="leadAssessments.length > 0" style="color: #888; font-weight: normal;">
                      ({{ leadAssessments.length }})
                    </span>
                  </h4>
                  <div style="display: flex; gap: 0.5rem;">
                    <b-button variant="outline-secondary" size="sm" @click="findPriorAssessments()"
                      v-b-tooltip.hover title="Search for unlinked Game Attendance Reports of this player">
                      <i class="bi bi-search" aria-hidden="true"></i> Find prior reports
                    </b-button>
                    <b-button variant="outline-success" size="sm" @click="openAddAssessmentModal()">
                      <i class="bi bi-plus" aria-hidden="true"></i> Add report
                    </b-button>
                  </div>
                </div>

                <div v-if="leadAssessments.length === 0" class="empty-state" style="padding: 1rem 0;">
                  <p style="color: #888;">No scout reports yet for this lead.</p>
                </div>

                <div v-else class="scout-assessments-list">
                  <div class="scout-assessment-row" v-for="a in leadAssessments" :key="a.id"
                    style="border: 1px solid #e5e5e5; border-radius: 4px; padding: 0.75rem; margin-bottom: 0.75rem;">
                    <div style="display: flex; justify-content: space-between; align-items: flex-start;">
                      <div>
                        <div style="font-weight: 600;">
                          {{ a.matchDate ? formatDateForHuman(a.matchDate) : formatDateForHuman(a.created_date) }}
                          <span v-if="a.homeTeam || a.awayTeam">
                            — {{ a.homeTeam || "?" }} vs {{ a.awayTeam || "?" }}
                          </span>
                        </div>
                        <div style="font-size: 0.85rem; color: #666;">
                          Scout: {{ a.scout_name || "—" }} ·
                          Position: {{ a.position || "—" }}
                        </div>
                      </div>
                      <b-button variant="outline-danger" size="sm" v-b-tooltip.hover
                        title="Unlink this report from the lead" @click="unlinkAssessment(a.id)">
                        <i class="bi bi-x"></i>
                      </b-button>
                    </div>

                    <div style="font-size: 0.85rem; margin-top: 0.5rem;">
                      Att <strong>{{ a.attitude || 0 }}</strong> ·
                      Skill <strong>{{ a.skill || 0 }}</strong> ·
                      Phys <strong>{{ a.physique || 0 }}</strong> ·
                      Ment <strong>{{ a.mentality || 0 }}</strong> ·
                      Lead <strong>{{ a.leadership || 0 }}</strong> ·
                      Prof <strong>{{ a.professionalism || 0 }}</strong>
                    </div>

                    <div v-if="a.comments" style="font-size: 0.85rem; margin-top: 0.5rem; color: #444;">
                      <em>{{ a.comments }}</em>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-state">
              <img src="../img/emptyStateIllustration.svg" />
              <p>No lead data</p>
            </div>
          </div>
        </div>

        <div class="tab-content no-flex" v-if="selectedPlayerInfoTab === 'trial-tab'">
          <div class="games-info">
            <div class="two-col-pop">
              <div class="user-details-pop">
                <p><span>Phone:</span> {{ selectedUserData.phone }}</p>
                <p><span>Email:</span> {{ selectedUserData.email }}</p>
                <p>
                  <span>DOB:</span>
                  {{ formatDateForHuman(selectedUserData.dob) }}
                </p>
                <p><span>Position:</span> {{ selectedUserData.position }}</p>
                <p>
                  <span>Preferred foot:</span>
                  {{ selectedTrialistsData.preferred_foot }}
                </p>

                <p>
                  <span>Trial length:</span>
                  {{ trialLength(selectedTrialistsData.trial_length) }}
                </p>
                <p>
                  <span>Trial start date:</span>
                  {{
                    formatDateForHuman(selectedTrialistsData.trial_start_date)
                  }}
                </p>

                <p>
                  <span>Coach:</span>
                  {{ selectedTrialistsData.coach_name }}
                </p>

                <p>
                  <span>Team:</span>
                  {{ getTeamName(selectedTrialistsData.teamId) }}
                </p>

                <button style="margin-bottom: 2rem" @click="editTrialistPerformanceReview(selectedTrialistsData)"
                  class="green-button">
                  Edit trialist
                </button>
              </div>
            </div>

            <div class="trial-performance-wrapper">
              <h4>Performance review</h4>

              <div class="form__item">
                <label class="form__label" for="attitude">Attitude</label>
                <div class="ratingStars">
                  <vue3-star-ratings v-model="selectedTrialistsData.attitude" :star-size="25" />
                </div>
              </div>

              <div class="form__item">
                <label class="form__label" for="skill">Skill</label>
                <div class="ratingStars">
                  <vue3-star-ratings v-model="selectedTrialistsData.skill" :star-size="25" />
                </div>
              </div>

              <div class="form__item">
                <label class="form__label" for="physique">Physique</label>
                <div class="ratingStars">
                  <vue3-star-ratings v-model="selectedTrialistsData.physique" :star-size="25" />
                </div>
              </div>

              <div class="form__item">
                <label class="form__label" for="physique">Mentality</label>
                <div class="ratingStars">
                  <vue3-star-ratings v-model="selectedTrialistsData.mentality" :star-size="25" />
                </div>
              </div>

              <div class="form__item">
                <label class="form__label" for="physique">Leadership</label>
                <div class="ratingStars">
                  <vue3-star-ratings v-model="selectedTrialistsData.leadership" :star-size="25" />
                </div>
              </div>

              <div class="form__item">
                <label class="form__label" for="physique">Professionalism</label>
                <div class="ratingStars">
                  <vue3-star-ratings v-model="selectedTrialistsData.professionalism" :star-size="25" />
                </div>
              </div>

              <div class="form__item">
                <label class="form__label" for="comments">Comments</label>
                <QuillEditor v-model:content="selectedTrialistsData.comments" contentType="html" theme="snow" />
              </div>
            </div>

            <div class="form__item">
              <button @click="saveTrialistPerformanceReview()" class="green-button">
                Save Performance review
              </button>
            </div>
          </div>
        </div>

        <div class="tab-content no-flex" v-if="selectedPlayerInfoTab === 'player-stats-tab'">
          <div class="games-info">
            <div class="stats-wrapper">
              <div class="game-stats">
                <h3 class="no-margin">Game stats for this season</h3>
                <ul class="key-value-list">
                  <li>
                    <span>Appearances:</span>{{ gamePerformanceData.gamesPlayed }}
                  </li>
                  <li v-if="checkUserPosition() !== 'Goalkeeper'">
                    <span>Goals:</span>
                    <template v-if="gamePerformanceData.goals !== null">{{
                      gamePerformanceData.goals
                      }}</template>
                    <template v-else>0</template>
                  </li>
                  <li v-else>
                    <span>Clean sheets:</span>
                    <template v-if="gamePerformanceData.clean_sheet !== null">{{
                      gamePerformanceData.clean_sheet
                      }}</template>
                    <template v-else>0</template>
                  </li>
                  <li>
                    <span>Assists:</span>
                    {{ gamePerformanceData.assists }}
                  </li>
                  <!--<li
                          v-if="gamePerformanceData.player_self_rating !== null"
                        >
                          <span>Player performace rating:</span>
                          {{ gamePerformanceData.player_self_rating }}
                        </li>-->
                  <li v-if="gamePerformanceData.timeOnPitch !== null">
                    <span>Game time:</span>
                    {{ gamePerformanceData.timeOnPitch }} mins
                  </li>
                  <li>
                    <span>Yellow cards:</span>
                    {{ gamePerformanceData.yellowCardTotal }}
                  </li>
                  <li>
                    <span>Red cards:</span>
                    {{ gamePerformanceData.redCardTotal }}
                  </li>
                </ul>
              </div>

              <div class="playing-positions">
                <h3>Playing positions</h3>
                <div class="pitch-wrapper">
                  <img class="goalkeeper small-shirt" src="../img/player-shirt-gray.svg" alt="player-shirt" />

                  <img class="small-shirt right-full-back" src="../img/player-shirt-gray.svg" alt="player-shirt" />
                  <img class="small-shirt center-back-left" src="../img/player-shirt-gray.svg" alt="player-shirt" />
                  <img class="small-shirt center-back-right" src="../img/player-shirt-gray.svg" alt="player-shirt" />
                  <img class="small-shirt left-full-back" src="../img/player-shirt-gray.svg" alt="player-shirt" />

                  <img class="small-shirt left-midfielder" src="../img/player-shirt-gray.svg" alt="player-shirt" />
                  <img class="small-shirt defensive-midfielder" src="../img/player-shirt-gray.svg" alt="player-shirt" />
                  <img class="small-shirt attacking-midfielder" src="../img/player-shirt-gray.svg" alt="player-shirt" />
                  <img class="small-shirt right-midfielder" src="../img/player-shirt-gray.svg" alt="player-shirt" />

                  <img class="small-shirt striker-left" src="../img/player-shirt-gray.svg" alt="player-shirt" />
                  <img class="small-shirt striker-right" src="../img/player-shirt-gray.svg" alt="player-shirt" />

                  <div class="position-played-wrapper" v-for="(
position, index
                    ) in gamePerformanceData.postion_played" :key="index">
                    <img v-if="position === 'Goalkeeper'" class="goalkeeper small-shirt"
                      src="../img/player-shirt-blue.svg" alt="player-shirt" />

                    <img v-if="position === 'Right Full-back (Wingback)'" class="small-shirt right-full-back"
                      src="../img/player-shirt-blue.svg" alt="player-shirt" />
                    <img v-if="position === 'Center-back left'" class="small-shirt center-back-left"
                      src="../img/player-shirt-blue.svg" alt="player-shirt" />
                    <img v-if="position === 'Center-back right'" class="small-shirt center-back-right"
                      src="../img/player-shirt-blue.svg" alt="player-shirt" />
                    <img v-if="position === 'Left Full-back (Wingback)'" class="small-shirt left-full-back"
                      src="../img/player-shirt-blue.svg" alt="player-shirt" />

                    <img v-if="position === 'Left Midfielder (Winger)'" class="small-shirt left-midfielder"
                      src="../img/player-shirt-blue.svg" alt="player-shirt" />
                    <img v-if="
                      position === 'Defensive Midfielder' ||
                      position === 'Centre Defensive Midfield'
                    " class="small-shirt defensive-midfielder" src="../img/player-shirt-blue.svg" alt="player-shirt" />
                    <img v-if="
                      position === 'Attacking Midfielder' ||
                      position === 'Centre midfield'
                    " class="small-shirt attacking-midfielder" src="../img/player-shirt-blue.svg" alt="player-shirt" />
                    <img v-if="position === 'Right Midfielder (Winger)'" class="small-shirt right-midfielder"
                      src="../img/player-shirt-blue.svg" alt="player-shirt" />

                    <img v-if="position === 'Center Forward (Striker) left'" class="small-shirt striker-left"
                      src="../img/player-shirt-blue.svg" alt="player-shirt" />
                    <img v-if="position === 'Center Forward (Striker) right'" class="small-shirt striker-right"
                      src="../img/player-shirt-blue.svg" alt="player-shirt" />
                  </div>

                  <img src="../img/pitch.svg" alt="pitch" class="pitch-svg" id="pitch_svg" />
                </div>
              </div>

              <div class="training-scores">
                <h3 class="no-margin">Training stats for this season</h3>
                <ul v-if="trainingPerformanceData.traningAttended > 0" class="key-value-list">
                  <li>
                    <span>Sessions attended:</span>{{ trainingPerformanceData.traningAttended }}
                  </li>
                  <li>
                    <span>Training hours this season:</span>{{ trainingPerformanceData.time }} mins
                  </li>
                  <li>
                    <span>Last Training session rating:</span>{{ trainingPerformanceData.enjoyment }}
                  </li>

                  <span v-if="gamePerformanceData.created_date !== undefined"
                    class="small-date-player-coach full-width-error" :class="`days-${checkDateTimeFromNow(
                      gamePerformanceData.created_date,
                    )}`">
                    Updated on
                    {{ formatDate(gamePerformanceData.created_date) }}</span>
                </ul>
                <p v-else>
                  You have not attened any training sessions this seasion. You
                  must attened at least one training session and submit a
                  traiing repoort to view training statistics.
                </p>

                <div v-if="trainingPerformanceData.effort == '10'" class="rpe-scale-result ten">
                  <span class="big-number"><i class="bi bi-stopwatch" aria-hidden="true"></i> 10</span>
                  <h3>MAX EFFORT ACTIVITY</h3>
                  <p>
                    Enjoyment rating: {{ trainingPerformanceData.enjoyment }}
                  </p>
                </div>

                <div v-if="trainingPerformanceData.effort == '9'" class="rpe-scale-result nine">
                  <span class="big-number"><i class="bi bi-stopwatch" aria-hidden="true"></i> 9</span>
                  <h3>VERY HARD ACTIVITY</h3>
                  <p>
                    Enjoyment rating: {{ trainingPerformanceData.enjoyment }}
                  </p>
                </div>

                <div v-if="trainingPerformanceData.effort == '8'" class="rpe-scale-result eight">
                  <span class="big-number"><i class="bi bi-stopwatch" aria-hidden="true"></i> 8</span>
                  <h3>VIGOROUS ACTIVITY</h3>
                  <p>
                    Enjoyment rating: {{ trainingPerformanceData.enjoyment }}
                  </p>
                </div>

                <div v-if="trainingPerformanceData.effort == '7'" class="rpe-scale-result four-six">
                  <span class="big-number"><i class="bi bi-stopwatch" aria-hidden="true"></i> 7</span>
                  <h3>MODERATE ACTIVITY</h3>
                  <p>
                    Enjoyment rating: {{ trainingPerformanceData.enjoyment }}
                  </p>
                </div>

                <div v-if="trainingPerformanceData.effort == '6'" class="rpe-scale-result two-three">
                  <span class="big-number"><i class="bi bi-stopwatch" aria-hidden="true"></i> 6</span>
                  <h3>LIGHT ACTIVITY</h3>
                  <p>
                    Enjoyment rating: {{ trainingPerformanceData.enjoyment }}
                  </p>
                </div>

                <div v-if="trainingPerformanceData.effort == '5'" class="rpe-scale-result one">
                  <span class="big-number"><i class="bi bi-stopwatch" aria-hidden="true"></i> 5</span>
                  <h3>VERY LIGHT ACTIVITY</h3>
                  <p>
                    Enjoyment rating: {{ trainingPerformanceData.enjoyment }}
                  </p>
                </div>
                <div v-if="trainingPerformanceData.effort === undefined">
                  <div class="empty-state">
                    <img src="../img/emptyStateIllustration.svg" />
                    <p>No session effort rating data</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="tab-content no-flex" v-if="selectedPlayerInfoTab === 'player-history-tab'">
          <div class="games-info">
            <div class="button-wrapper-top">
              <b-button class="main-button" @click="produceThePlayerCV()" size="lg" v-b-tooltip.hover
                title="View players c.v" variant="outline-success"><i class="bi bi-eye"></i></b-button>
            </div>

            <div class="player-cv">
              <section class="cv-section">
                <h3>Personal profile</h3>

                <p v-if="playerCvData.personal_profile !== ''">
                  {{ playerCvData.personal_profile }}
                </p>
                <p v-else>Add your personal Profile.</p>

                <b-button variant="outline-warning" @click="openPlayerCVpersonalProfileModal()"><i class="bi bi-pencil"
                    aria-hidden="true"></i>
                  Edit</b-button>
              </section>
              <section class="cv-section">
                <h3>PLAYING EXPERIENCE</h3>

                <p v-if="playerCvData.playingExperence.length === 0">
                  Add your playing Experence
                </p>

                <table v-else style="margin-bottom: 0.5rem" class="w-a-style">
                  <thead>
                    <tr>
                      <th>Club</th>
                      <th>Time at the club</th>
                      <th>Playing stats</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, index) in playerCvData.playingExperence" :key="index">
                      <td>{{ item.club }}</td>
                      <td>
                        {{ formatDateForHuman(item.start_date_at_club) }} -
                        {{ formatDateForHuman(item.end_date_at_club) }}
                      </td>
                      <td>{{ item.playing_info }}</td>
                      <td>
                        <div class="buttons-row">
                          <b-button @click="openPlayerCVPlayingExperenceModal(index)" size="lg"
                            variant="outline-success">
                            Update</b-button>

                          <b-button @click="deletePlayingExperence(item.id)" size="lg"
                            variant="outline-danger">Delete</b-button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>

                <b-button variant="outline-success" @click="openPlayerCVPlayingExperenceModal()"><i class="bi bi-pencil"
                    aria-hidden="true"></i> Add</b-button>
              </section>

              <section class="cv-section">
                <div class="education">
                  <h3>EDUCATION</h3>

                  <p v-if="playerCvData.education.length === 0">
                    Add your education
                  </p>

                  <ul style="margin-bottom: 0.5rem">
                    <li v-for="(item, index) in playerCvData.education" :key="index">
                      <span class="title">{{ item.title }}</span>
                      <span class="info">{{ item.start_date }} - {{ item.end_date }}</span>
                      <span class="info">{{ item.more_info }}</span>
                      <div class="buttons-row">
                        <b-button @click="openPlayerEducationModal(index)" size="lg" variant="outline-success">
                          Update</b-button>

                        <b-button @click="deletePlayerEducation(item.id)" size="lg"
                          variant="outline-danger">Delete</b-button>
                      </div>
                    </li>
                  </ul>

                  <b-button variant="outline-success" @click="openPlayerEducationModal()"><i class="bi bi-pencil"
                      aria-hidden="true"></i>
                    Add</b-button>
                </div>
              </section>

              <section class="cv-section">
                <div class="hobbies">
                  <h3>CAREER STATISTICS</h3>

                  <!--<ul>
                      <li>Goals: 31</li>
                      <li>Shots: 104</li>
                      <li>Assists: 43</li>
                      <li>Pass completion: 84.6%</li>
                    </ul>-->
                </div>
              </section>

              <section class="cv-section">
                <div class="hobbies">
                  <h3>Interest hobbies outside of football</h3>

                  <p v-if="playerCvData.interests_hobbies.length === 0">
                    Add your interest and hobbies
                  </p>
                  <table v-else style="margin-bottom: 0.5rem" class="w-a-style">
                    <thead>
                      <tr>
                        <th>Interest hobbies</th>
                        <th>More information</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in playerCvData.interests_hobbies" :key="index">
                        <td>{{ item.interests }}</td>
                        <td>
                          {{ item.more_info }}
                        </td>

                        <td>
                          <div class="buttons-row">
                            <b-button @click="openPlayerInterestsHobbiesModal(index)" size="lg"
                              variant="outline-success">
                              Update</b-button>

                            <b-button @click="deletePlayerInterestsHobbies(item.id)" size="lg"
                              variant="outline-danger">Delete</b-button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <b-button variant="outline-success" @click="openPlayerInterestsHobbiesModal()"><i class="bi bi-pencil"
                      aria-hidden="true"></i>
                    Add</b-button>
                </div>
              </section>

              <section class="cv-section">
                <div class="players-videos">
                  <h3>Videos</h3>
                  <p v-if="playerCvData.videos.length === 0">
                    Add your football videos
                  </p>

                  <div class="sessions-info" style="margin-bottom: 0.5rem">
                    <div class="video-wrapper" v-for="(item, index) in playerCvData.videos" :key="index">
                      <a v-if="item.category === ''" target="_blank" :href="item.video_url" class="session-card">
                        <TenantImage image-name="video" extension="jpg" />
                        <p class="small-date">Video</p>
                        <h3>{{ item.video_name }}</h3>
                      </a>

                      <div class="buttons-row" v-if="item.category === ''">
                        <b-button @click="openPlayerVideosModal(index)" size="lg" variant="outline-success">
                          Update</b-button>

                        <b-button @click="deletePlayerVideoLink(item.id)" size="lg"
                          variant="outline-danger">Delete</b-button>
                      </div>
                    </div>
                  </div>
                </div>

                <b-button variant="outline-success" @click="openPlayerVideosModal()"><i class="bi bi-pencil"
                    aria-hidden="true"></i> Add</b-button>
              </section>
            </div>
          </div>
        </div>

        <div class="tab-content no-flex" v-if="selectedPlayerInfoTab === 'player-documents-tab'">
          <div class="games-info">
            <DocumentUploader @getDocumentUploadsCall="getDocumentUploadsCall" documentCategory="player"
              :club_id="userData.clubId" :user_id="userData.id"
              :selectedDocumentData="selectedDocumentData.approvedAccessList" />

            <div class="document" v-for="document in uploadedDocuments" :key="document.id" v-b-tooltip.hover
              :title="removeNumberFromStart(document.name)">
              <a :href="getDocumentUrl(document.url)" target="_blank">
                <img src="../img/documentlib.svg" />
                <h3>{{ removeNumberFromStart(document.name) }}</h3>
              </a>
            </div>
            <div v-if="uploadedDocuments.length === 0">
              <div class="empty-state">
                <img src="../img/emptyStateIllustration.svg" />
                <p>No documents</p>
              </div>
            </div>
          </div>
        </div>

        <div class="tab-content no-flex" v-if="selectedPlayerInfoTab === 'player-report-tab'">
          <div class="games-info">
            <b-button class="main-button create-academy-report-button" @click="editAcademyReportModal()" size="lg"
              variant="outline-warning" style="margin-right: 1rem">
              <i class="bi bi-pencil" aria-hidden="true"></i> Edit player
              report</b-button>

            <div class="player-report-content">
              <iframe v-if="showReport === true" id="theAcademyReportPdf_preview" src="" />
            </div>
          </div>
        </div>

        <div class="tab-content no-flex" v-if="selectedPlayerInfoTab === 'player-videos-tab'">
          <div class="games-info">
            <b-button variant="outline-success" @click="openPlayerVideosModal()"><i class="bi bi-pencil"
                aria-hidden="true"></i> Add</b-button>

            <div class="players-videos">
              <div v-if="playerCvData.videos.length === 0">
                <div class="empty-state">
                  <img src="../img/emptyStateIllustration.svg" />
                  <p>No players videos</p>
                </div>
              </div>

              <div class="sessions-info" style="margin-bottom: 0.5rem">
                <div class="video-wrapper" v-for="(item, index) in playerCvData.videos" :key="index">
                  <a v-if="item.category === ''" target="_blank" :href="item.video_url" class="session-card">
                    <TenantImage image-name="video" extension="jpg" />
                    <p class="small-date">Video</p>
                    <h3>{{ item.video_name }}</h3>
                  </a>

                  <div class="buttons-row" v-if="item.category === ''">
                    <b-button @click="openPlayerVideosModal(index)" size="lg" variant="outline-success">
                      Update</b-button>

                    <b-button @click="deletePlayerVideoLink(item.id)" size="lg"
                      variant="outline-danger">Delete</b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </b-modal>

    <b-modal v-if="showViewTrialist" v-model="showViewTrialist" size="lg" title="Trialist performance review"
      :hide-footer="true">
      <div class="two-col-pop">
        <div class="user-photo-info">
          <img v-if="
            selectedTrialistsData.userImage !== '' &&
            selectedTrialistsData.userImage !== null
          " :src="getImgUrl(selectedTrialistsData.userImage)" alt="" />
          <TenantLogo v-else logo-class="login-logo" />
        </div>
        <div class="user-details-pop">
          <p><span>Name:</span> {{ selectedTrialistsData.name }}</p>
          <p><span>Phone:</span> {{ selectedTrialistsData.phone }}</p>
          <p><span>Email:</span> {{ selectedTrialistsData.email }}</p>
          <p>
            <span>DOB:</span>
            {{ formatDateForHuman(selectedTrialistsData.dob) }}
          </p>
          <p><span>Position:</span> {{ selectedTrialistsData.position }}</p>
          <p>
            <span>Trial length:</span>
            {{ trialLength(selectedTrialistsData.trial_length) }}
          </p>
          <p>
            <span>Trial start date:</span>
            {{ formatDateForHuman(selectedTrialistsData.trial_start_date) }}
          </p>
          <p>
            <span>Preferred foot:</span>
            {{ selectedTrialistsData.preferred_foot }}
          </p>

          <p>
            <span>Team:</span> {{ getTeamName(selectedTrialistsData.teamId) }}
          </p>

          <p>
            <span>Coach:</span>
            {{ selectedTrialistsData.coach_name }}
          </p>
        </div>
      </div>

      <h4>Performance review</h4>

      <div class="form__item">
        <label class="form__label" for="attitude">Attitude</label>
        <div class="ratingStars">
          <vue3-star-ratings v-model="selectedTrialistsData.attitude" :star-size="25" />
        </div>
      </div>

      <div class="form__item">
        <label class="form__label" for="skill">Skill</label>
        <div class="ratingStars">
          <vue3-star-ratings v-model="selectedTrialistsData.skill" :star-size="25" />
        </div>
      </div>

      <div class="form__item">
        <label class="form__label" for="physique">Physique</label>
        <div class="ratingStars">
          <vue3-star-ratings v-model="selectedTrialistsData.physique" :star-size="25" />
        </div>
      </div>

      <div class="form__item">
        <label class="form__label" for="physique">Mentality</label>
        <div class="ratingStars">
          <vue3-star-ratings v-model="selectedTrialistsData.mentality"
            @updatev-model="selectedTrialistsData.mentality = $event" :star-size="25" />
        </div>
      </div>

      <div class="form__item">
        <label class="form__label" for="physique">Leadership</label>
        <div class="ratingStars">
          <vue3-star-ratings v-model="selectedTrialistsData.leadership" :star-size="25" />
        </div>
      </div>

      <div class="form__item">
        <label class="form__label" for="physique">Professionalism</label>
        <div class="ratingStars">
          <vue3-star-ratings v-model="selectedTrialistsData.professionalism" :star-size="25" />
        </div>
      </div>

      <div class="form__item">
        <label class="form__label" for="comments">Comments</label>
        <QuillEditor v-model:content="selectedTrialistsData.comments" contentType="html" theme="snow" />
      </div>

      <div class="out-come-buttons" v-if="selectedTrialistsData.outcome === 0">
        <button @click="recordOutcome('1')" class="green-button">
          <i class="bi bi-pen" aria-hidden="true"></i> Sign
        </button>
        <button @click="recordOutcome('2')" class="red-button">
          <i class="bi bi-exclamation-circle" aria-hidden="true"></i> Reject
        </button>
        <!--<button @click="recordOutcome('3')" class="yellow-button">Waiting list</button>-->
      </div>

      <p>
        When you sign a player, they will be automatically registered with the
        team they tried out for.
      </p>

      <div class="form__item">
        <button @click="saveTrialistPerformanceReview()" class="green-button">
          Save trialist
        </button>
      </div>
    </b-modal>

    <b-modal v-if="showPlayerVideosModal" v-model="showPlayerVideosModal" size="lg" :hide-footer="true"
      title="Personal profile">
      <div class="player-cv-content">
        <div class="playing-experence-wrapper">
          <div class="each-target playing-experence-item">
            <div class="form__item">
              <label class="form__label" for="contact">Video title:</label>
              <input class="form__input" type="text" id="club" v-model="selectedPlayerVideoData.video_name" />
            </div>

            <div class="form__item">
              <label class="form__label" for="contact">Video URL:</label>
              <input class="form__input" type="text" id="club" v-model="selectedPlayerVideoData.video_url" />
            </div>

            <b-button v-b-tooltip.hover title="Save" variant="success" v-if="selectedPlayerVideoDataEdit === false"
              @click="savePlayerVideoLink()">Save video</b-button>

            <b-button v-b-tooltip.hover title="Update" variant="success" v-else @click="editPlayerVideoLink()">Update
              video</b-button>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showGameAttendanceReportModal" v-model="showGameAttendanceReportModal" size="lg" :hide-footer="true"
      title="Game attendance report">
      <div class="performance-target-modal-content form">
        <!-- Advanced Mode Toggle -->
        <div class="advanced-mode-toggle">
          <b-button @click="showAdvancedGameDetails = !showAdvancedGameDetails"
            :variant="showAdvancedGameDetails ? 'success' : 'outline-secondary'" size="sm" class="mb-3">
            <i class="bi bi-gear" aria-hidden="true"></i>
            {{
              showAdvancedGameDetails
                ? "Hide Advanced Details"
                : "Show Advanced Details"
            }}
          </b-button>
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Home team name</label>
          <input class="form__input" type="text" v-model="selectedMatchAttendedData.homeTeam" id="homeTeam" />
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Away team name</label>
          <input class="form__input" type="text" v-model="selectedMatchAttendedData.awayTeam" id="awayTeam" />
        </div>

        <div class="form__item">
          <label class="form__label" for="name">Age group</label>

          <VueDatePicker :locale="'en-GB'" format="yyyy" text-input-options="{ format: 'yyyy' }"
            v-model="selectedMatchAttendedData.age_group" year-picker name="datepicker" id="input-id"
            input-class="input-class" :enable-time-picker="false" />
        </div>

        <div class="form__item">
          <label class="form__label" for="gameDate">Match date</label>

          <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="selectedMatchAttendedData.matchDate" name="gameDate" id="gameDate-id" input-class="input-class"
            :enable-time-picker="false" />
        </div>

        <div class="form__item">
          <label class="form__label" for="weather_conditions">Weather conditions</label>
          <input class="form__input" type="text" v-model="selectedMatchAttendedData.weather_conditions"
            id="weather_conditions" />
        </div>

        <div class="form__item">
          <label class="form__label" for="textarea-game-comments">Game Comments</label>
          <VoiceDictationButton v-model="selectedMatchAttendedData.game_comments"
            label="Dictate game comments" />
          <b-form-textarea id="textarea-game-comments" placeholder="Add game comments"
            v-model="selectedMatchAttendedData.game_comments"></b-form-textarea>
        </div>

        <!-- Enhanced Game Context Section - Only shown when advanced mode is enabled -->
        <div v-if="showAdvancedGameDetails">
          <!-- Enhanced Game Context Section -->
          <div class="enhanced-game-context-section">
            <h4 class="section-title">Game Context & Details</h4>

            <div class="form-row">
              <div class="form__item form__item--half">
                <label class="form__label" for="competition">Competition/League</label>
                <input class="form__input" type="text" v-model="selectedMatchAttendedData.gameContext.competition"
                  id="competition" placeholder="e.g., Scottish Premier League U18" />
              </div>

              <div class="form__item form__item--half">
                <label class="form__label" for="division">Division</label>
                <input class="form__input" type="text" v-model="selectedMatchAttendedData.gameContext.division"
                  id="division" placeholder="e.g., Division 1" />
              </div>
            </div>

            <div class="form-row">
              <div class="form__item form__item--half">
                <label class="form__label" for="gameType">Game Type</label>
                <select class="form__input" v-model="selectedMatchAttendedData.gameContext.gameType" id="gameType">
                  <option value="">Select game type</option>
                  <option value="League">League</option>
                  <option value="Cup">Cup</option>
                  <option value="Friendly">Friendly</option>
                  <option value="Tournament">Tournament</option>
                  <option value="Playoff">Playoff</option>
                </select>
              </div>

              <div class="form__item form__item--half">
                <label class="form__label" for="gameImportance">Game Importance</label>
                <select class="form__input" v-model="selectedMatchAttendedData.gameContext.gameImportance"
                  id="gameImportance">
                  <option value="">Select importance level</option>
                  <option value="High">High (Cup Final, Derby, etc.)</option>
                  <option value="Medium">
                    Medium (Important League Match)
                  </option>
                  <option value="Low">Low (Friendly, Training Match)</option>
                </select>
              </div>
            </div>

            <div class="form-row">
              <div class="form__item form__item--half">
                <label class="form__label" for="broadcastLevel">Broadcast Level</label>
                <select class="form__input" v-model="selectedMatchAttendedData.gameContext.broadcastLevel"
                  id="broadcastLevel">
                  <option value="">Select broadcast level</option>
                  <option value="Televised">Televised</option>
                  <option value="Streamed">Live Streamed</option>
                  <option value="Local">Local Coverage</option>
                  <option value="None">No Coverage</option>
                </select>
              </div>

              <div class="form__item form__item--half">
                <label class="form__label" for="attendance">Attendance</label>
                <input class="form__input" type="number" v-model="selectedMatchAttendedData.gameContext.gameResult.attendance
                  " id="attendance" placeholder="e.g., 1200" min="0" />
              </div>
            </div>
          </div>

          <!-- Venue Information Section -->
          <div class="venue-information-section">
            <h4 class="section-title">Venue Information</h4>

            <div class="form-row">
              <div class="form__item form__item--half">
                <label class="form__label" for="venueName">Venue Name</label>
                <input class="form__input" type="text" v-model="selectedMatchAttendedData.gameContext.venue.name"
                  id="venueName" placeholder="e.g., Firhill Stadium" />
              </div>

              <div class="form__item form__item--half">
                <label class="form__label" for="venueAddress">Venue Address</label>
                <input class="form__input" type="text" v-model="selectedMatchAttendedData.gameContext.venue.address"
                  id="venueAddress" placeholder="Full venue address" />
              </div>
            </div>

            <div class="form-row">
              <div class="form__item form__item--half">
                <label class="form__label" for="venueCapacity">Venue Capacity</label>
                <input class="form__input" type="number" v-model="selectedMatchAttendedData.gameContext.venue.capacity"
                  id="venueCapacity" placeholder="e.g., 10102" min="0" />
              </div>

              <div class="form__item form__item--half">
                <label class="form__label" for="venueSurface">Playing Surface</label>
                <select class="form__input" v-model="selectedMatchAttendedData.gameContext.venue.surface"
                  id="venueSurface">
                  <option value="">Select surface type</option>
                  <option value="Grass">Natural Grass</option>
                  <option value="Artificial">Artificial Turf</option>
                  <option value="Hybrid">Hybrid (Grass/Artificial)</option>
                  <option value="Indoor">Indoor Surface</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Match Result Section -->
          <div class="match-result-section">
            <h4 class="section-title">Match Result</h4>

            <div class="form-row">
              <div class="form__item form__item--third">
                <label class="form__label" for="homeScore">Home Score</label>
                <input class="form__input" type="number" v-model="selectedMatchAttendedData.gameContext.gameResult.homeScore
                  " id="homeScore" placeholder="0" min="0" />
              </div>

              <div class="form__item form__item--third">
                <label class="form__label" for="awayScore">Away Score</label>
                <input class="form__input" type="number" v-model="selectedMatchAttendedData.gameContext.gameResult.awayScore
                  " id="awayScore" placeholder="0" min="0" />
              </div>

              <div class="form__item form__item--third">
                <label class="form__label">Final Result</label>
                <div class="result-display">
                  <span class="team-result">{{
                    selectedMatchAttendedData.homeTeam || "Home"
                    }}</span>
                  <span class="score-display">
                    {{
                      selectedMatchAttendedData.gameContext.gameResult
                        .homeScore || 0
                    }}
                    -
                    {{
                      selectedMatchAttendedData.gameContext.gameResult
                        .awayScore || 0
                    }}
                  </span>
                  <span class="team-result">{{
                    selectedMatchAttendedData.awayTeam || "Away"
                    }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form__item">
          <label class="form__label" for="position">Scout attending game</label>
          <select id="position" @change="populateAssignedScout($event, 'game attendance')">
            <option disabled value="">Please select a state</option>
            <option value="">Mark as unassigned</option>
            <option v-for="scout in allScoutsData" :key="scout.id" :value="scout.id"
              :selected="scout.id === selectedMatchAttendedData.scoutId">
              {{ scout.name }}
            </option>
          </select>

          <p v-if="
            selectedMatchAttendedData.scoutId !== undefined &&
            selectedMatchAttendedData.scoutId !== null
          ">
            Assigned scout:
            {{ getScoutNameFromId(selectedMatchAttendedData.scoutId) }}
          </p>
        </div>

        <label class="form__label" for="perfomance-targets">Add player to be assessed

          <b-button v-b-tooltip.hover title="Add player to be assessed" @click="addItem()" variant="outline-success">
            <i class="bi bi-person" aria-hidden="true"></i>
          </b-button>
        </label>

        <div class="player-watched-details-wrapper" v-if="selectedMatchAttendedData.playersGameData.length > 0">
          <div class="player-watched-details" v-for="(item, index) in selectedMatchAttendedData.playersGameData"
            :key="index">
            <div class="buttons-wrapper">
              <b-button v-if="item.leadId === null" v-b-tooltip.hover title="Add player as lead"
                variant="outline-success" @click="openLeadsModal('scout report lead', item)">
                <i class="bi bi-person" aria-hidden="true"></i></b-button>
              <p class="player-added" v-if="item.leadId !== null">
                <i class="bi bi-person" aria-hidden="true"></i> Player added as
                lead
              </p>

              <!--<b-button
                  v-b-tooltip.hover
                  title="Invite player for trial"
                  variant="outline-success"
                >
                  <i class="bi bi-person"
                    v-if="item.trialistId === null"
                    aria-hidden="true"
                    @click="removeItem(index)"
                  ></i
                ></b-button>-->
              <p v-if="item.trialistId !== null">Player invited for trial</p>

              <b-button v-if="item.leadId === null" v-b-tooltip.hover title="remove player" variant="outline-danger">
                <i class="bi bi-x" aria-hidden="true" @click="removeItem(index, item.id)"></i></b-button>
            </div>

            <div class="form__item">
              <label class="form__label" for="name">Players name</label>
              <input class="form__input" type="text" v-model="item.name" id="name" />
            </div>

            <div class="form__item">
              <label class="form__label" for="team">Players team</label>
              <input class="form__input" type="text" v-model="item.team" id="team" />
            </div>

            <div class="form__item">
              <label class="form__label" for="phone">Phone number</label>
              <input class="form__input" type="number" v-model="item.phone" id="phone" />
            </div>

            <div class="form__item">
              <label class="form__label" for="position">Position played</label>
              <select id="position" v-model="item.position">
                <option disabled value="">Please select a position</option>
                <option :selected="item.position === 'Goalkeeper'" value="Goalkeeper">
                  Goalkeeper
                </option>
                <option :selected="item.position === 'Right Full-back (Wingback)'" value="Right Full-back (Wingback)">
                  Right Full-back (Wingback)
                </option>
                <option :selected="item.position === 'Left Full-back (Wingback)'" value="Left Full-back (Wingback)">
                  Left Full-back (Wingback)
                </option>
                <option :selected="item.position === 'Center-back left'" value="Center-back left">
                  Center-back left
                </option>
                <option :selected="item.position === 'Center-back right'" value="Center-back right">
                  Center-back right
                </option>
                <option :selected="item.position === 'Defensive Midfielder'" value="Defensive Midfielder">
                  Defensive Midfielder
                </option>
                <option :selected="item.position === 'Centre midfield'" value="Centre midfield">
                  Centre midfield
                </option>
                <option :selected="item.position === 'Centre Defensive Midfield'" value="Centre Defensive Midfield">
                  Centre Defensive Midfield
                </option>
                <option :selected="item.position === 'Right Midfielder (Winger)'" value="Right Midfielder (Winger)">
                  Right Midfielder (Winger)
                </option>
                <option :selected="item.position === 'Left Midfielder (Winger)'" value="Left Midfielder (Winger)">
                  Left Midfielder (Winger)
                </option>
                <option :selected="item.position === 'Attacking Midfielder'" value="Attacking Midfielder">
                  Attacking Midfielder
                </option>
                <option :selected="item.position === 'Center Forward (Striker) left'"
                  value="Center Forward (Striker) left">
                  Center Forward (Striker) left
                </option>
                <option :selected="item.position === 'Center Forward (Striker) right'"
                  value="Center Forward (Striker) right">
                  Center Forward (Striker) right
                </option>
                <option :selected="item.position === 'I played more than one position'
                  " value="I played more than one position">
                  I played more than one position
                </option>
              </select>
            </div>

            <div class="form__item">
              <label class="form__label" for="attitude">Attitude</label>
              <div class="ratingStars">
                <vue3-star-ratings v-model="item.attitude" :star-size="25" />
              </div>
            </div>

            <div class="form__item">
              <label class="form__label" for="skill">Skill</label>
              <div class="ratingStars">
                <vue3-star-ratings v-model="item.skill" :star-size="25" />
              </div>
            </div>

            <div class="form__item">
              <label class="form__label" for="physique">Physique</label>
              <div class="ratingStars">
                <vue3-star-ratings v-model="item.physique" :star-size="25" />
              </div>
            </div>

            <div class="form__item">
              <label class="form__label" for="physique">Mentality</label>
              <div class="ratingStars">
                <vue3-star-ratings v-model="item.mentality" :star-size="25" />
              </div>
            </div>

            <div class="form__item">
              <label class="form__label" for="physique">Leadership</label>
              <div class="ratingStars">
                <vue3-star-ratings v-model="item.leadership" :star-size="25" />
              </div>
            </div>

            <div class="form__item">
              <label class="form__label" for="physique">Professionalism</label>
              <div class="ratingStars">
                <vue3-star-ratings v-model="item.professionalism" :star-size="25" />
              </div>
            </div>

            <!-- AI Insights Panel (defensive checks) -->
            <AIInsightsPanel v-if="item && typeof item === 'object' && item.name !== undefined" :currentPlayer="item"
              :gameContext="{
                scoutAttendanceReportId:
                  item && item.scoutAttendanceReportId
                    ? item.scoutAttendanceReportId
                    : undefined,
                team: item && item.team ? item.team : '',
                position: item && item.position ? item.position : '',
              }" :allGameReports="Array.isArray(filteredTrialistsData)
                ? filteredTrialistsData
                : []
                " class="ai-insights-integration" />

            <div class="form__item">
              <label class="form__label" for="comments">Overall Assessment</label>
              <VoiceDictationButton label="Dictate assessment"
                @transcript="appendTranscriptToPlayerComments(item, $event)" />
              <QuillEditor v-model:content="item.comments" contentType="html" theme="snow" />
            </div>

            <!-- Phase 2: Advanced Player Demographics & Profiling Toggle -->
            <div class="advanced-profiling-toggle" style="margin: 1.5rem 0">
              <b-button @click="toggleAdvancedProfiling(item)" :variant="item.showAdvancedProfiling ? 'success' : 'outline-secondary'
                " size="sm">
                <i class="bi bi-person-gear" aria-hidden="true"></i>
                {{
                  item.showAdvancedProfiling
                    ? "Hide Advanced Profiling"
                    : "Show Advanced Profiling"
                }}
              </b-button>
            </div>

            <!-- Advanced Player Demographics & Profiling Sections -->
            <div v-if="item.showAdvancedProfiling" class="advanced-profiling-sections">
              <!-- Demographics Section -->
              <div class="demographics-section">
                <h5 class="section-title">
                  <i class="bi bi-person-vcard" aria-hidden="true"></i> Player
                  Demographics
                </h5>

                <div class="form-row">
                  <div class="form__item form__item--third">
                    <label class="form__label" for="age">Age</label>
                    <input class="form__input" type="number" v-model="item.demographics.age" min="14" max="35" />
                  </div>

                  <div class="form__item form__item--third">
                    <label class="form__label" for="nationality">Nationality</label>
                    <input class="form__input" type="text" v-model="item.demographics.nationality" />
                  </div>

                  <div class="form__item form__item--third">
                    <label class="form__label" for="preferredFoot">Preferred Foot</label>
                    <select class="form__input" v-model="item.demographics.preferredFoot">
                      <option value="">Select foot</option>
                      <option value="Right">Right</option>
                      <option value="Left">Left</option>
                      <option value="Both">Both</option>
                    </select>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form__item form__item--half">
                    <label class="form__label" for="height">Height (cm)</label>
                    <input class="form__input" type="number" v-model="item.demographics.height" min="150" max="210" />
                  </div>

                  <div class="form__item form__item--half">
                    <label class="form__label" for="weight">Weight (kg)</label>
                    <input class="form__input" type="number" v-model="item.demographics.weight" min="45" max="120" />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form__item form__item--half">
                    <label class="form__label" for="previousClub">Previous Club</label>
                    <input class="form__input" type="text" v-model="item.demographics.previousClub" />
                  </div>

                  <div class="form__item form__item--half">
                    <label class="form__label" for="contractStatus">Contract Status</label>
                    <select class="form__input" v-model="item.demographics.contractStatus">
                      <option value="">Select status</option>
                      <option value="Free Agent">Free Agent</option>
                      <option value="Under Contract">Under Contract</option>
                      <option value="Youth Contract">Youth Contract</option>
                      <option value="Amateur">Amateur</option>
                    </select>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form__item">
                    <label class="form__label" for="emergencyContact">Emergency Contact</label>
                    <input class="form__input" type="text" v-model="item.demographics.emergencyContact" />
                  </div>
                </div>
              </div>

              <!-- Physical Attributes Section -->
              <div class="physical-attributes-section">
                <h5 class="section-title">
                  <i class="bi bi-speedometer2" aria-hidden="true"></i> Physical
                  Attributes
                </h5>

                <div class="rating-grid">
                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="pace">Pace</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.physicalAttributes.pace" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="strength">Strength</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.physicalAttributes.strength" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="stamina">Stamina</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.physicalAttributes.stamina" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="agility">Agility</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.physicalAttributes.agility" :star-size="20" />
                    </div>
                  </div>
                </div>

                <div class="rating-grid">
                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="balance">Balance</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.physicalAttributes.balance" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="jumping">Jumping</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.physicalAttributes.jumping" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--half">
                    <label class="form__label" for="fitnessLevel">Fitness Level</label>
                    <select class="form__input" v-model="item.physicalAttributes.fitness_level">
                      <option value="">Select level</option>
                      <option value="Excellent">Excellent</option>
                      <option value="Good">Good</option>
                      <option value="Progressing well">Progressing well</option>
                      <option value="⁠⁠Requires improvement">⁠⁠Requires improvement</option>
                      <option value="Unsatisfactory">Unsatisfactory</option>
                    </select>
                  </div>
                </div>

                <div class="form__item">
                  <label class="form__label" for="injuryHistory">Injury History</label>
                  <b-form-textarea v-model="item.physicalAttributes.injury_history"
                    placeholder="Any known injuries or concerns..." rows="2"></b-form-textarea>
                </div>
              </div>

              <!-- Technical Skills Section -->
              <div class="technical-skills-section">
                <h5 class="section-title">
                  <i class="bi bi-gear-wide-connected" aria-hidden="true"></i>
                  Technical Skills
                </h5>

                <div class="rating-grid">
                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="passing">Passing</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.technicalSkills.passing" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="shooting">Shooting</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.technicalSkills.shooting" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="dribbling">Dribbling</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.technicalSkills.dribbling" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="crossing">Crossing</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.technicalSkills.crossing" :star-size="20" />
                    </div>
                  </div>
                </div>

                <div class="rating-grid">
                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="tackling">Tackling</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.technicalSkills.tackling" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="heading">Heading</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.technicalSkills.heading" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="firstTouch">First Touch</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.technicalSkills.firstTouch" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="weakFoot">Weak Foot</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.technicalSkills.weakFootAbility" :star-size="20" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Mental Attributes Section -->
              <div class="mental-attributes-section">
                <h5 class="section-title">
                  <i class="bi bi-brain" aria-hidden="true"></i> Mental
                  Attributes
                </h5>

                <div class="rating-grid">
                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="vision">Vision</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.mentalAttributes.vision" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="decisionMaking">Decision Making</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.mentalAttributes.decisionMaking" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="composure">Composure</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.mentalAttributes.composure" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="workRate">Work Rate</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.mentalAttributes.workRate" :star-size="20" />
                    </div>
                  </div>
                </div>

                <div class="rating-grid">
                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="teamwork">Teamwork</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.mentalAttributes.teamwork" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="communication">Communication</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.mentalAttributes.communication" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="coachability">Coachability</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.mentalAttributes.coachability" :star-size="20" />
                    </div>
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="competitiveness">Competitiveness</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.mentalAttributes.competitiveness" :star-size="20" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Match Performance Section -->
              <div class="match-performance-section">
                <h5 class="section-title">
                  <i class="bi bi-trophy" aria-hidden="true"></i> Match
                  Performance
                </h5>

                <div class="form-row">
                  <div class="form__item form__item--third">
                    <label class="form__label" for="minutesPlayed">Minutes Played</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.minutesPlayed" min="0"
                      max="120" />
                  </div>

                  <div class="form__item form__item--third">
                    <label class="form__label" for="goals">Goals</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.goals" min="0" />
                  </div>

                  <div class="form__item form__item--third">
                    <label class="form__label" for="assists">Assists</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.assists" min="0" />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form__item form__item--third">
                    <label class="form__label" for="keyPasses">Key Passes</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.keyPasses" min="0" />
                  </div>

                  <div class="form__item form__item--third">
                    <label class="form__label" for="tackles">Tackles</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.tackles" min="0" />
                  </div>

                  <div class="form__item form__item--third">
                    <label class="form__label" for="interceptions">Interceptions</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.interceptions" min="0" />
                  </div>
                </div>

                <div class="form-row">
                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="fouls">Fouls</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.foulsCommitted" min="0" />
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="yellowCards">Yellow Cards</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.yellowCards" min="0" />
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="redCards">Red Cards</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.redCards" min="0" />
                  </div>

                  <div class="form__item form__item--quarter">
                    <label class="form__label" for="saves">Saves (GK)</label>
                    <input class="form__input" type="number" v-model="item.matchPerformance.saves" min="0" />
                  </div>
                </div>
              </div>

              <!-- Development Potential Section -->
              <div class="development-potential-section">
                <h5 class="section-title">
                  <i class="bi bi-graph-up-arrow" aria-hidden="true"></i>
                  Development Potential
                </h5>

                <div class="form-row">
                  <div class="form__item form__item--half">
                    <label class="form__label" for="overallRating">Overall Rating</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.developmentPotential.overallRating" :star-size="25" />
                    </div>
                  </div>

                  <div class="form__item form__item--half">
                    <label class="form__label" for="potentialRating">Potential Rating</label>
                    <div class="ratingStars">
                      <vue3-star-ratings v-model="item.developmentPotential.potentialRating" :star-size="25" />
                    </div>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form__item form__item--half">
                    <label class="form__label" for="strengths">Key Strengths</label>
                    <b-form-textarea v-model="item.developmentPotential.strengths"
                      placeholder="What are the player's main strengths?" rows="3"></b-form-textarea>
                  </div>

                  <div class="form__item form__item--half">
                    <label class="form__label" for="improvementAreas">Areas for Improvement</label>
                    <b-form-textarea v-model="item.developmentPotential.improvementAreas"
                      placeholder="What areas need development?" rows="3"></b-form-textarea>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form__item form__item--half">
                    <label class="form__label" for="nextSteps">Next Steps</label>
                    <b-form-textarea v-model="item.developmentPotential.nextSteps"
                      placeholder="Recommended next steps for the player..." rows="2"></b-form-textarea>
                  </div>

                  <div class="form__item form__item--half">
                    <label class="form__label" for="recommendedTraining">Training Recommendations</label>
                    <b-form-textarea v-model="item.developmentPotential.recommendedTraining"
                      placeholder="Specific training recommendations..." rows="2"></b-form-textarea>
                  </div>
                </div>

                <div class="form__item">
                  <label class="form__label" for="coachNotes">Additional Coach Notes</label>
                  <b-form-textarea v-model="item.developmentPotential.coachNotes"
                    placeholder="Any additional observations or notes..." rows="3"></b-form-textarea>
                </div>
              </div>
            </div>
          </div>

          <b-button v-b-tooltip.hover v-if="editMode === false" title="Save" variant="success"
            @click="saveScoutGameAttendanceReport()">Save</b-button>

          <b-button v-b-tooltip.hover v-else title="Save" variant="success"
            @click="updateScoutGameAttendanceReport()">Edit</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showEditAcademyReportModal" v-model="showEditAcademyReportModal" size="lg" :hide-footer="true">
      <div class="performance-target-modal-content">
        <h3>Edit Player report</h3>

        <div class="form__item">
          <label class="form__label" for="coaches_comments">Coaches comments:</label>

          <b-form-textarea id="coaches_comments" v-model="academyReport.coaches_comments"
            placeholder="Enter your comments..." rows="3" max-rows="6"></b-form-textarea>
        </div>

        <div class="form__item">
          <label class="form__label" for="contact">Coaches average score:</label>
          <input class="form__input" type="number" id="average_score" v-model="academyReport.average_score" />
          / 30
        </div>

        <div class="buttons-row">
          <b-button size="lg" variant="outline-warning" @click="updateTheAcademyReport(academyReport.id)">
            Update Player report</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showExtendTrialModal" v-model="showExtendTrialModal" size="lg" :hide-footer="true">
      <div class="performance-target-modal-content form">
        <h3>Extend trial</h3>

        <div class="form__item">
          <label class="form__label" for="coaches_comments">Players name</label>
          <p>{{ extendTrial.name }}</p>
        </div>

        <div class="form__item">
          <label class="form__label" for="coachSelect">Select trial length (weeks)</label>

          <input class="form__input" type="number" value="0" min="0" max="20" step="1"
            v-model="extendTrial.trial_length" id="trailLength" />
        </div>

        <div class="form__item uk-form">
          <label class="form__label" for="trialStartDate">Trial extention start date</label>
          <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="extendTrial.trial_start_date" name="trialStartDate" id="trialStartDate" :enable-time-picker="false"
            @update:model-value="
              updateDate('extendTrial', 'trial_start_date', $event)
              " />
        </div>

        <div class="buttons-row">
          <b-button size="lg" variant="outline-success" @click="extendTrialSubmit()">
            Extend Trial</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showViewPlayersCV" v-model="showViewPlayersCV" size="lg" :hide-footer="true">
      <div class="player-cv-content">
        <iframe id="thePlayerCVPDF_preview" src="" />
      </div>
    </b-modal>

    <b-modal v-if="showPlayerCVpersonalProfileModal" v-model="showPlayerCVpersonalProfileModal" size="lg"
      :hide-footer="true" title="Personal profile">
      <div class="player-cv-content">
        <b-form-textarea id="personal_profile_text_box" v-model="playerCvData.personal_profile"
          placeholder="Enter your personal proflie..." rows="8"></b-form-textarea>

        <b-button v-b-tooltip.hover @click="savePersonalProfile()" title="Save" variant="success">Save Personal
          profile</b-button>
      </div>
    </b-modal>

    <b-modal v-if="showPlayerCVPlayingExperenceModal" v-model="showPlayerCVPlayingExperenceModal" size="lg"
      :hide-footer="true" @close="playerCVPlayingExperenceModalClose()" title="Playing experence">
      <div class="player-cv-content">
        <div class="playing-experence-wrapper">
          <div class="each-target playing-experence-item">
            <div class="form__item">
              <label class="form__label" for="contact">Club name:</label>
              <input class="form__input" type="text" id="club" v-model="selectedPlayingExperenceData.club" />
            </div>

            <div class="form__item">
              <label class="form__label" for="contact">Start date at the club:</label>
              <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
                v-model="selectedPlayingExperenceData.start_date_at_club" name="startDateAtClub"
                :enable-time-picker="false" id="startDateAtClub" />
            </div>

            <div class="form__item">
              <label class="form__label" for="contact">End date at the club:</label>
              <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
                v-model="selectedPlayingExperenceData.end_date_at_club" name="endDateAtClub" id="endDateAtClub"
                :enable-time-picker="false" />
            </div>

            <div class="form__item">
              <label class="form__label" for="perfomance-targets"> </label>

              <b-form-textarea id="textarea" v-model="selectedPlayingExperenceData.playing_info"
                placeholder="Enter Playing information..." rows="3" max-rows="6"></b-form-textarea>
            </div>
          </div>

          <b-button v-b-tooltip.hover title="Save" variant="success" v-if="selectedPlayingExperenceEdit === false"
            @click="savePlayerPlayingExperence()">Save playing experence</b-button>

          <b-button v-b-tooltip.hover title="Update" variant="success" v-else @click="editPlayingExperence()">Update
            playing
            experence</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showOpenPlayerEducationModal" v-model="showOpenPlayerEducationModal" size="lg" :hide-footer="true"
      @close="playerEducationModalClose()" title="Player Education">
      <div class="player-cv-content">
        <div class="playing-experence-wrapper">
          <div class="each-target playing-experence-item">
            <div class="form__item">
              <label class="form__label" for="contact">Qualification name:</label>
              <input class="form__input" type="text" id="Qualification_name"
                v-model="selectedPlayerEducationData.title" />
            </div>

            <div class="form__item">
              <label class="form__label" for="contact">Start date:</label>
              <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
                v-model="selectedPlayerEducationData.start_date" name="start_date" id="start_date"
                :enable-time-picker="false" />
            </div>

            <div class="form__item">
              <label class="form__label" for="contact">End date:</label>
              <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
                v-model="selectedPlayerEducationData.end_date" name="end_date" id="end_date"
                :enable-time-picker="false" />
            </div>

            <div class="form__item">
              <label class="form__label" for="perfomance-targets">Information
              </label>

              <b-form-textarea id="textarea" v-model="selectedPlayerEducationData.more_info"
                placeholder="Enter more information..." rows="3" max-rows="6"></b-form-textarea>
            </div>
          </div>

          <b-button v-b-tooltip.hover title="Save" variant="success" v-if="selectedPlayerEducationDataEdit === false"
            @click="savePlayerEducation()">Save education</b-button>

          <b-button v-b-tooltip.hover title="Update" variant="success" v-else @click="editPlayerEducation()">Update
            education</b-button>
        </div>
      </div>
    </b-modal>

    <b-modal v-if="showOpenPlayerInterestsHobbiesModal" v-model="showOpenPlayerInterestsHobbiesModal" size="lg"
      :hide-footer="true" @close="playerInterestsHobbiesClose()" title="Player Interests and Hobbies">
      <div class="player-cv-content">
        <div class="form__item">
          <label class="form__label" for="contact">Interest or Hobbie:</label>
          <input class="form__input" type="text" id="interests"
            v-model="selectedPlayerInterestsHobbiesData.interests" />
        </div>

        <div class="form__item">
          <label class="form__label" for="contact">More information:</label>

          <b-form-textarea id="textarea" v-model="selectedPlayerInterestsHobbiesData.more_info"
            placeholder="Enter more information..." rows="3" max-rows="6"></b-form-textarea>
        </div>

        <b-button v-b-tooltip.hover title="Save" variant="success"
          v-if="selectedPlayerInterestsHobbiesDataEdit === false" @click="saveInterestsHobbies()">Save
          interest</b-button>

        <b-button v-b-tooltip.hover title="Update" variant="success" v-else @click="editPlayerInterestsHobbies()">Update
          interest</b-button>
      </div>
    </b-modal>

    <b-modal v-if="showMergeAssessmentsModal" v-model="showMergeAssessmentsModal" size="lg"
      title="Link prior scout reports?" :hide-footer="true">
      <p>
        We found <strong>{{ pendingMergeMatches.length }}</strong> other scout
        report(s) on a player with this name and team. Tick which ones belong
        to this lead — they will be linked so the full history shows in the
        Player Information modal.
      </p>
      <p class="text-warning" style="font-size: 0.85rem;">
        <i class="bi bi-exclamation-triangle"></i>
        Players with the same name on the same team can collide — please review
        each match before linking.
      </p>

      <div class="merge-matches-list">
        <div class="merge-match-row" v-for="m in pendingMergeMatches" :key="m.id"
          style="border-bottom: 1px solid #eee; padding: 0.75rem 0;">
          <label style="display: flex; align-items: flex-start; gap: 0.75rem; cursor: pointer;">
            <input type="checkbox" v-model="pendingMergeSelected[m.id]" style="margin-top: 0.4rem;" />
            <div style="flex: 1;">
              <div style="font-weight: 600;">
                {{ m.matchDate ? formatDateForHuman(m.matchDate) : formatDateForHuman(m.created_date) }}
                — {{ m.homeTeam || "?" }} vs {{ m.awayTeam || "?" }}
              </div>
              <div style="font-size: 0.85rem; color: #666;">
                Position: {{ m.position || "—" }} ·
                Age group: {{ m.age_group || "—" }}
              </div>
              <div style="font-size: 0.85rem; margin-top: 0.25rem;">
                Att {{ m.attitude || 0 }} ·
                Skill {{ m.skill || 0 }} ·
                Phys {{ m.physique || 0 }} ·
                Ment {{ m.mentality || 0 }} ·
                Lead {{ m.leadership || 0 }} ·
                Prof {{ m.professionalism || 0 }}
              </div>
              <div v-if="m.comments" style="font-size: 0.85rem; margin-top: 0.25rem; color: #444;">
                <em>{{ m.comments }}</em>
              </div>
            </div>
          </label>
        </div>
      </div>

      <div class="player-game-responce" style="margin-top: 1rem;">
        <b-button variant="success" @click="confirmMergeAssessments()">
          Link selected
        </b-button>
        <b-button variant="outline-secondary" @click="cancelMergeAssessments()">
          Skip
        </b-button>
      </div>
    </b-modal>

    <b-modal v-if="showAddAssessmentModal" v-model="showAddAssessmentModal" size="lg"
      title="Add scout report" :hide-footer="true">
      <div class="performance-target-modal-content form">
        <div class="form__item">
          <label class="form__label" for="newAssessmentMatchDate">Match date</label>
          <VueDatePicker :locale="'en-GB'" format="dd/MM/yyyy" text-input-options="{ format: 'dd/MM/yyyy' }"
            v-model="newAssessment.matchDate" name="newAssessmentMatchDate" id="newAssessmentMatchDate"
            input-class="input-class" :enable-time-picker="false" />
        </div>

        <div class="form__item">
          <label class="form__label" for="newAssessmentHomeTeam">Home team</label>
          <input class="form__input" type="text" v-model="newAssessment.homeTeam" id="newAssessmentHomeTeam" />
        </div>

        <div class="form__item">
          <label class="form__label" for="newAssessmentAwayTeam">Away team</label>
          <input class="form__input" type="text" v-model="newAssessment.awayTeam" id="newAssessmentAwayTeam" />
        </div>

        <div class="form__item">
          <label class="form__label" for="newAssessmentPosition">Position played</label>
          <input class="form__input" type="text" v-model="newAssessment.position" id="newAssessmentPosition" />
        </div>

        <div class="form__item">
          <label class="form__label">Attitude</label>
          <vue3-star-ratings v-model="newAssessment.attitude" :star-size="25" />
        </div>
        <div class="form__item">
          <label class="form__label">Skill</label>
          <vue3-star-ratings v-model="newAssessment.skill" :star-size="25" />
        </div>
        <div class="form__item">
          <label class="form__label">Physique</label>
          <vue3-star-ratings v-model="newAssessment.physique" :star-size="25" />
        </div>
        <div class="form__item">
          <label class="form__label">Mentality</label>
          <vue3-star-ratings v-model="newAssessment.mentality" :star-size="25" />
        </div>
        <div class="form__item">
          <label class="form__label">Leadership</label>
          <vue3-star-ratings v-model="newAssessment.leadership" :star-size="25" />
        </div>
        <div class="form__item">
          <label class="form__label">Professionalism</label>
          <vue3-star-ratings v-model="newAssessment.professionalism" :star-size="25" />
        </div>

        <div class="form__item">
          <label class="form__label" for="newAssessmentComments">Notes</label>
          <b-form-textarea id="newAssessmentComments" v-model="newAssessment.comments" rows="3"
            placeholder="What did you see?"></b-form-textarea>
        </div>

        <div class="player-game-responce" style="margin-top: 1rem;">
          <b-button variant="success" @click="saveNewAssessment()">Save report</b-button>
          <b-button variant="outline-secondary" @click="showAddAssessmentModal = false">Cancel</b-button>
        </div>
      </div>
    </b-modal>

  </div>
</template>
<script>
import SpinnerLoader from "../../components/LoadingSpinning";
import AppTemplate from "../layouts/App";
import SectionNav from "@/components/SectionNav/index.vue";
import { mapGetters } from "vuex";
import { USER_POPULATE } from "@/store/actions/user.js";
import dayjs from "dayjs";
import { Users } from "@/services/user-service.js";
import { Trialists } from "@/services/trialist-service.js";
import { Documents } from "@/services/documents-service.js";
import { PlayerCv } from "@/services/player-cv-service.js";
import { Games } from "@/services/games-service.js";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import { Teams } from "@/services/teams-service.js";
import { AcademyReport } from "@/services/academy-report-service.js";
import ImageUploader from "../../components/ImageUploader";
import { Radar, Bar } from "vue-chartjs";
import Chart from "chart.js/auto";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
);
import AttributeRadarChart from "../../components/AttributeRadarChart.vue";
import VoiceDictationButton from "../../components/VoiceDictationButton.vue";
import moment from "moment";
import removeDuplicatesMixin from "@/mixin/removeDuplicates.js";
import DocumentUploader from "../../components/doucmentUploader";
import JsPDF from "jspdf";
import extendSeasonEndDateToFullYearMixin from "@/mixin/extendSeasonEndDateToFullYear.js";
import VueGoodTable from "vue-good-table-next";
import "vue-good-table-next/dist/vue-good-table-next.css";
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import PerformanceBenchmarking from "../../components/analytics/PerformanceBenchmarking.vue";
import AnalyticsDashboardOverview from "../../components/analytics/AnalyticsDashboardOverview.vue";
import PlayerDemographicsAnalysis from "../../components/analytics/PlayerDemographicsAnalysis.vue";
import AIInsightsPanel from "../../components/ai/AIInsightsPanel.vue";
import ScoutActivityAnalytics from "../../components/analytics/ScoutActivityAnalytics.vue";
import StatusIcon from "@/components/StatusIcon.vue";
import TenantLogo from "@/components/TenantLogo/index.vue";
import TenantImage from "@/components/TenantImage/index.vue";
import { BBadge } from "bootstrap-vue-next";
import { PlayerScoutSearch } from "@/services/player-scout-search-service.js";
export default {
  name: "Trialists",
  mixins: [removeDuplicatesMixin, extendSeasonEndDateToFullYearMixin],
  components: {
    AppTemplate,
    SectionNav,
    SpinnerLoader,
    VueDatePicker,
    ImageUploader,
    AttributeRadarChart,
    VoiceDictationButton,
    DocumentUploader,
    QuillEditor,
    PerformanceBenchmarking,
    AnalyticsDashboardOverview,
    PlayerDemographicsAnalysis,
    AIInsightsPanel,
    ScoutActivityAnalytics,
    TenantLogo,
    TenantImage,
    StatusIcon,
    Radar,
    Bar,
    "b-badge": BBadge,
  },
  data() {
    return {
      loadingStuff: false,
      activeLeadTab: "hot",
      filterPriority: "",
      filterStatus: "",
      filterScout: "",
      gamesFilterScout: "",
      gamesFilterAgeGroup: "",
      trialistsFilterCoach: "",
      trialistsFilterTeam: "",
      trialistsFilterStatus: "",
      trialistsSortBy: "ending-soon",
      priorityOptions: [
        { value: "", text: "All priorities" },
        { value: "hot", text: "Hot" },
        { value: "warm", text: "Warm" },
        { value: "cold", text: "Cold" },
      ],
      statusOptions: [
        { value: "", text: "All statuses" },
        { value: "Still to Watch", text: "Still to Watch" },
        { value: "Invited for trial", text: "Invited for trial" },
        { value: "Need to watch again", text: "Need to watch again" },
        { value: "Trial invite refused", text: "Trial invite refused" },
        {
          value: "Not at the standard required",
          text: "Not at the standard required",
        },
        { value: "Hot lead", text: "Hot lead" },
        { value: "Developing talent", text: "Developing talent" },
        { value: "Long term watch", text: "Long term watch" },
        { value: "Moved on", text: "Moved on" },
      ],
      scoutOptions: [
        { value: "", text: "All scouts" },
        // Populate dynamically from backend or data
      ],
      britishLocaleData: {
        name: "en-GB", // British English locale
        weekdays: [
          "Sunday",
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
        ],
        weekdaysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        weekdaysMin: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
        months: [
          "January",
          "February",
          "March",
          "April",
          "May",
          "June",
          "July",
          "August",
          "September",
          "October",
          "November",
          "December",
        ],
        monthsShort: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        weekStart: 1, // Monday first (British standard)
        yearFirst: false, // DD/MM/YYYY format
        format: "dd/MM/yyyy",
      },
      userTeams: [
        {
          name: "Falkirk FC",
          team_name: "Falkirk FC",
          id: 41,
          team_id: 41,
          formation: "4-4-2",
        },
        {
          name: "Partick Thistle FC",
          team_name: "Partick Thistle FC",
          id: 1016,
          team_id: 1016,
          formation: "4-4-2",
        },
      ],
      selectedUserTeamId: null,
      compareList: [],
      columnsScoutGameAttendance: [
        {
          label: "Match Date",
          field: "matchDate",
          type: "date",
          dateInputFormat: "yyyy-MM-dd",
          dateOutputFormat: "dd MMM yyyy",
          tdClass: "vgt-text-left",
          thClass: "vgt-text-left",
        },
        {
          label: "Game Information",
          field: "teams",
        },
        {
          label: "Score",
          field: "result",
          sortable: false,
        },
        {
          label: "Competition",
          field: "competition",
          sortable: false,
        },
        {
          label: "Age Group",
          field: "age_group",
        },
        {
          label: "Players assessed",
          field: "players_assessed",
          type: "number",
          tdClass: "vgt-text-left",
          thClass: "vgt-text-left",
        },
        {
          label: "Assigned Scout",
          field: "scoutId",
        },
        {
          label: "Actions",
          width: "6rem",
          field: "actions",
          sortable: false,
        },
      ],
      leads: [], // Populate from backend
      columnsPlayerLeads: [
        {
          label: "",
          width: "6rem",
          field: "userImage",
        },
        {
          label: "Date",
          field: "date",
        },
        {
          label: "Player",
          field: "name",
        },
        {
          label: "Boys Club",
          field: "club_name",
        },
        {
          label: "Phone",
          field: "phone",
        },
        {
          label: "Age group",
          field: "age_group",
        },
        {
          label: "Status",
          field: "state",
        },
        {
          label: "Priority",
          field: "priority",
        },
        {
          label: "Watch category",
          field: "status",
        },
        {
          label: "Assigned Scout",
          field: "scout_name",
        },
        {
          label: "Last contact",
          field: "last_contact_date",
        },
        {
          label: "Next follow-up",
          field: "next_follow_up_date",
        },
        {
          label: "Actions",
          width: "8rem",
          field: "actions",
        },
      ],
      selectedFilterData: null,
      toggleFilters: false,
      hideRemoveTeamFilterButton: true,
      liveTrialistsDataFiltered: [],
      extendTrial: {
        id: null,
        name: "",
        trial_length: "",
      },
      selectedPlayerInterestsHobbiesData: {},
      selectedPlayerInterestsHobbiesDataEdit: false,
      selectedPlayerEducationData: {},
      selectedPlayerEducationDataEdit: false,
      selectedPlayingExperenceData: {},
      selectedPlayingExperenceEdit: false,
      selectedGameFullPerformanceReportGPSdataForUser: {},
      playerCvData: {
        personal_profile: "",
        playingExperence: [],
        education: [],
        interests_hobbies: [],
        videos: [],
      },
      allScoutGameAttendanceReports: [],
      selectedUserData: {},
      allPlayersLeadData: [],
      selectedLeadData: {},
      allPlayerLeadsData: [],
      DatePickerFormat: "yyyy",
      DatePickerFormatFull: "dd-mm-yyyy",
      needsValidation: [],
      validationMessage: null,
      validationValues: {
        name: {
          state: false,
          text: "Please add a name please!",
        },
        email: {
          state: false,
          text: "Please add an email address",
        },
        password: {
          state: false,
          text: "Please add password please!",
        },
        dob: {
          state: false,
          text: "Please add a date of birth please!",
        },
      },
      editMode: false,
      trialistsData: [],
      liveTrialistsData: [],
      filteredTrialistsData: [],
      signedtrialistsData: [],
      archivedtrialistsData: [],
      coachesUsersData: [],
      selectedTab: "trialist-tab",
      navCollapsed: false,
      // Analytics Dashboard data
      analyticsData: {},
      analyticsView: "overview", // 'overview', 'performance-benchmarking', 'player-demographics'
      allAssessmentsData: [],
      dateRange: [],
      analyticsFilters: {},
      teamData: [],
      clubTeams: [],
      userData: {
        name: "",
        userType: "",
      },
      userHeaderData: {
        name: "",
        userType: "",
      },
      selectedUserDataForHeader: {},
      selectedUserValue: "",
      weekSelectedNumber: "",
      selectedTrialistsData: {
        dob: "",
        trial_length: "6",
        trial_start_date: "",
        preferred_foot: "Right",
        email: "",
        name: "",
        position: "",
        attitude: 0,
        skill: 0,
        physique: 0,
        mentality: 0,
        leadership: 0,
        professionalism: 0,
        comments: "",
        outcome: "0",
        clubId: "",
        teamId: "",
        userType: "User",
      },
      imageFile: null,
      imageToBeRemoved: null,
      imageURL: "",
      image: {
        imageName: "",
        path: "",
        file: null,
      },
      registerUser: {
        img: "",
        name: "",
        email: "",
        password: "",
        dob: "",
        position: "",
        phone: "",
        emergency_contact_one: "",
        userType: "User",
        action: "register",
        teamId: 0,
        permission: 0,
        clubId: null,
        coach_id: 0,
        team_module: 1,
        preferred_foot: 0,
        joined_date: "",
        medical: "",
      },
      coachData: [],
      selectedPlayerInfoTab: "lead-tab",
      selectedScottishPlayerTab: "player-details",
      uploadedDocuments: [],
      documentData: {
        name: "",
        text: "",
        created_date: moment().format("YYYY-MM-DD"),
        updated_date: null,
        created_by: null,
        edited_by: null,
        signature: false,
        approvedAccessList: {
          teams: [],
          users: [],
        },
      },
      metaDataReults: [],
      selectedDocumentData: {
        approvedAccessList: {
          teams: [],
          users: [],
          category: "player",
        },
      },
      selectedPlayerVideoData: {},
      selectedPlayerVideoDataEdit: false,
      selectedScottishPlayer: {},
      playerAttributesHistory: [], // Historical attributes for timeline
      selectedAttributeYear: null, // Currently selected year for attributes
      loadingAttributesHistory: false, // Loading state for attributes
      playerMedia: [], // Stores fetched media items
      loadingPlayerMedia: false, // Loading state for media
      selectedMediaTab: "all", // Filter: 'all', 'video', 'short'
      scottishPlayerHeatmap: null,
      selectedHeatmapSeason: null,
      heatmapLoading: false,
      // Scottish Player Heatmap properties (Opposition Analysis style)
      heatmapPoints: [],
      heatmapError: null,
      heatmapViewMode: "heatmap", // 'heatmap', 'positions', 'average'
      heatmapIntensityFilter: 1,
      tooltip: {
        visible: false,
        x: 0,
        y: 0,
        data: {},
      },
      showReport: false,
      // AI Scout Report properties
      aiScoutReport: null,
      loadingAIReport: false,
      aiReportError: null,
      aiReportLimitInfo: null,
      // Modal visibility properties for Vue 3 pattern
      showViewLead: false,
      showAddEditLead: false,
      showAddEditTrialist: false,
      invite: {
        teamId: "",
        trialLength: "6",
        startDate: "",
        token: "",
        link: "",
        loading: false,
        copied: false,
        error: "",
        recipientEmail: "",
        emailSending: false,
        emailSent: false,
      },
      showViewTrialistLeadPlayerProfile: false,
      showViewTrialist: false,
      showPlayerVideosModal: false,
      showGameAttendanceReportModal: false,
      showAdvancedGameDetails: false,
      showEditAcademyReportModal: false,
      showExtendTrialModal: false,
      showViewPlayersCV: false,
      showPlayerCVpersonalProfileModal: false,
      showPlayerCVPlayingExperenceModal: false,
      showOpenPlayerEducationModal: false,
      showOpenPlayerInterestsHobbiesModal: false,
      // Match-stitching: when a new lead is created, prompt the scout to link
      // any prior unlinked observations of the same player.
      showMergeAssessmentsModal: false,
      pendingMergeLeadId: null,
      pendingMergeMatches: [],
      pendingMergeSelected: {},
      // Lead-tab assessments list
      leadAssessments: [],
      // "Add report" form on the Lead tab
      showAddAssessmentModal: false,
      newAssessment: {
        matchDate: null,
        homeTeam: "",
        awayTeam: "",
        position: "",
        attitude: 0,
        skill: 0,
        physique: 0,
        mentality: 0,
        leadership: 0,
        professionalism: 0,
        comments: "",
      },
      academyReport: {
        id: null,
        player_id: null,
        seasion: null,
        average_score: 0,
        coaches_comments: "",
      },
      season_start_date: "",
      season_end_date: "",
      gamePerformanceData: {
        gameId: null,
        postion_played: [],
        goals: 0,
        assists: 0,
        clean_sheet: null,
        player_self_rating: null,
        yellowCardTotal: 0,
        redCardTotal: 0,
        timeOnPitch: null,
        gamesPlayed: null,
      },
      allGamesData: [],
      playerPhysicalPerformanceData: {},
      trainingPerformanceData: {},
      seasonsgamePerformanceReportsForUser: [],
      seasonsTraningPerformanceReportsForUser: [],
      allScoutsData: [],
      selectedMatchAttendedData: {
        clubId: "",
        homeTeam: "",
        awayTeam: "",
        age_group: "",
        matchDate: "",
        weather_conditions: "",
        game_comments: "",
        playersGameData: [],
        scoutId: null,
        scoutName: "",
        // Enhanced Game Context & Demographics
        gameContext: {
          competition: "",
          division: "",
          gameType: "",
          venue: {
            name: "",
            address: "",
            capacity: null,
            surface: "",
          },
          gameResult: {
            homeScore: null,
            awayScore: null,
            attendance: null,
          },
          gameImportance: "",
          broadcastLevel: "",
        },
      },
    };
  },
  computed: {
    // mix the getters into computed with object spread operator
    ...mapGetters(["getProfile", "getCoachesUsersData"]),
    // Left drawer nav items (see SectionNav); icons are Material Symbols names.
    trialistNavItems() {
      const items = [
        { key: "trialist-tab", label: "Trialist", icon: "person" },
        { key: "leads-tab", label: "Player leads", icon: "person_add" },
        { key: "gamesAttened-tab", label: "Games attended", icon: "stadium" },
        { key: "signed-tab", label: "Signed", icon: "how_to_reg" },
        { key: "archived-tab", label: "Archived", icon: "archive" },
        { key: "id-tab", label: "Scout id", icon: "badge" },
        { key: "fixtures-tab", label: "Fixtures", icon: "calendar_month" },
      ];
      // Admin-only for now. Reported as freezing some computers and phones,
      // and not yet reproduced — this limits who can hit it while that is
      // still open, rather than pretending it is fixed. Note it also takes
      // the Scout Activity view away from scouts, who are its subject.
      if (this.isAnalyticsDashboardVisible) {
        items.push({
          key: "analytics-tab",
          label: "Analytics Dashboard",
          icon: "analytics",
        });
      }
      return items;
    },

    // Page heading follows the selected drawer item (it used to be a fixed
    // "Trialists" whichever section was open).
    activeTabTitle() {
      const item = this.trialistNavItems.find((i) => i.key === this.selectedTab);
      return item ? item.label : "Trialists";
    },

    /** Who may see the trialist Analytics Dashboard. */
    isAnalyticsDashboardVisible() {
      return this.userData && this.userData.userType === "Admin";
    },

    // AI Scout Report computed properties
    canGenerateReport() {
      return this.aiReportLimitInfo && this.aiReportLimitInfo.remaining > 0;
    },

    parsedReportData() {
      if (!this.aiScoutReport || !this.aiScoutReport.report_content) {
        return {
          hasMetrics: false,
          strengths: [],
          development: [],
          recommendation: "",
          attributes: {},
        };
      }

      // PRIORITY 1: Check if we have structured JSON metrics from the backend
      if (this.aiScoutReport.report_metrics) {
        try {
          const metrics =
            typeof this.aiScoutReport.report_metrics === "string"
              ? JSON.parse(this.aiScoutReport.report_metrics)
              : this.aiScoutReport.report_metrics;

          // Check if confidence_rating is 0 (indicates no real data from Claude)
          if (metrics.confidence_rating === 0) {
            return {
              hasMetrics: false,
              strengths: [],
              development: [],
              recommendation: "",
              recommendationReason: "",
              confidenceRating: null,
              attributes: {},
              overallScore: null,
            };
          }

          // Return structured data from JSON
          return {
            hasMetrics: true,
            strengths: metrics.strengths || [],
            development: metrics.development_areas || [],
            recommendation: metrics.recommendation || "",
            recommendationReason: "",
            confidenceRating: metrics.confidence_rating || null,
            attributes: metrics.attributes || {},
            overallScore: metrics.overall_score || null,
          };
        } catch (error) {
          console.error("Error parsing report_metrics JSON:", error);
          // Fall through to markdown parsing
        }
      }

      // FALLBACK: Parse markdown content (legacy reports or if JSON parsing fails)
      const content = this.aiScoutReport.report_content;
      const data = {
        hasMetrics: false,
        strengths: [],
        development: [],
        recommendation: "",
        recommendationReason: "",
        confidenceRating: null,
        attributes: {},
      };

      // Extract key strengths
      const strengthsMatch = content.match(
        /\*\*KEY STRENGTHS[\s\S]*?(?=\n\n\*\*|$)/i,
      );
      if (strengthsMatch) {
        const strengthsList = strengthsMatch[0].match(
          /(?:^|\n)(?:\d+\.\s|[-•]\s)\*\*([^*]+)\*\*/g,
        );
        if (strengthsList) {
          data.strengths = strengthsList.map((s) =>
            s.replace(/(?:^|\n)(?:\d+\.\s|[-•]\s)\*\*([^*]+)\*\*/, "$1").trim(),
          );
          data.hasMetrics = true;
        }
      }

      // Extract development areas
      const devMatch = content.match(
        /\*\*AREAS FOR DEVELOPMENT[\s\S]*?(?=\n\n\*\*|$)/i,
      );
      if (devMatch) {
        const devList = devMatch[0].match(
          /(?:^|\n)(?:\d+\.\s|[-•]\s)\*\*([^*]+)\*\*/g,
        );
        if (devList) {
          data.development = devList.map((d) =>
            d.replace(/(?:^|\n)(?:\d+\.\s|[-•]\s)\*\*([^*]+)\*\*/, "$1").trim(),
          );
          data.hasMetrics = true;
        }
      }

      // Extract recommendation
      const recMatch = content.match(
        /\*\*(?:Recommendation|FINAL RECOMMENDATION)[:\s]*\*\*\s*([^\n]+)/i,
      );
      if (recMatch) {
        data.recommendation = recMatch[1].trim();
        data.hasMetrics = true;
      }

      // Extract confidence rating
      const confMatch = content.match(
        /(?:Confidence|Rating)[:\s]*(\d+)[\/\s]*(?:out of\s*)?10/i,
      );
      if (confMatch) {
        data.confidenceRating = parseInt(confMatch[1]);
      }

      // Extract recommendation reason
      const reasonMatch = content.match(
        /\*\*FINAL RECOMMENDATION[\s\S]*?(?:Confidence|Rating)[^\n]*\n\n([^\n]+)/i,
      );
      if (reasonMatch) {
        data.recommendationReason =
          reasonMatch[1].trim().substring(0, 100) + "...";
      }

      // Extract attribute ratings (looking for patterns like "Technical: 7/10" or "Pace: 8")
      const attrPatterns = [
        { key: "Technical", regex: /technical[^\d]*(\d+)(?:\/10)?/i },
        { key: "Physical", regex: /physical[^\d]*(\d+)(?:\/10)?/i },
        { key: "Mental", regex: /mental[^\d]*(\d+)(?:\/10)?/i },
        { key: "Tactical", regex: /tactical[^\d]*(\d+)(?:\/10)?/i },
        { key: "Defensive", regex: /defensive[^\d]*(\d+)(?:\/10)?/i },
        { key: "Attacking", regex: /attacking[^\d]*(\d+)(?:\/10)?/i },
      ];

      attrPatterns.forEach(({ key, regex }) => {
        const match = content.match(regex);
        if (match) {
          const rating = parseInt(match[1]);
          if (rating >= 1 && rating <= 10) {
            data.attributes[key] = rating;
          }
        }
      });

      // Don't generate placeholder data - return empty attributes if none found
      // The v-if="parsedReportData.hasMetrics" will hide charts when no data exists

      return data;
    },

    radarChartData() {
      const attrs = this.parsedReportData?.attributes;
      if (!attrs || Object.keys(attrs).length === 0) {
        return null;
      }
      return {
        labels: Object.keys(attrs),
        datasets: [
          {
            label: "Player Attributes",
            data: Object.values(attrs),
            backgroundColor: "rgba(102, 126, 234, 0.2)",
            borderColor: "rgba(102, 126, 234, 1)",
            borderWidth: 2,
            pointBackgroundColor: "rgba(102, 126, 234, 1)",
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: "rgba(102, 126, 234, 1)",
          },
        ],
      };
    },

    radarChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          r: {
            beginAtZero: true,
            max: 10,
            min: 0,
            ticks: {
              stepSize: 2,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
        },
      };
    },
    barChartData() {
      const attrs = this.parsedReportData?.attributes;
      if (!attrs || Object.keys(attrs).length === 0) {
        return null;
      }
      return {
        labels: Object.keys(attrs),
        datasets: [
          {
            label: "Rating (out of 10)",
            data: Object.values(attrs),
            backgroundColor: [
              "rgba(102, 126, 234, 0.8)",
              "rgba(118, 75, 162, 0.8)",
              "rgba(52, 152, 219, 0.8)",
              "rgba(46, 204, 113, 0.8)",
              "rgba(241, 196, 15, 0.8)",
              "rgba(231, 76, 60, 0.8)",
            ],
            borderColor: [
              "rgba(102, 126, 234, 1)",
              "rgba(118, 75, 162, 1)",
              "rgba(52, 152, 219, 1)",
              "rgba(46, 204, 113, 1)",
              "rgba(241, 196, 15, 1)",
              "rgba(231, 76, 60, 1)",
            ],
            borderWidth: 2,
          },
        ],
      };
    },
    barChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: true,
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: {
              stepSize: 2,
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
        },
      };
    },
    formattedScoutReport() {
      if (!this.aiScoutReport || !this.aiScoutReport.report_content) {
        return "";
      }

      // If the report is already in HTML format (from Claude), return it directly
      // Claude will generate properly formatted HTML with all styling classes
      if (this.aiScoutReport.report_content.trim().startsWith("<")) {
        return this.aiScoutReport.report_content;
      }

      // Fallback: If still receiving markdown (backward compatibility)
      // This can be removed once the backend is updated to request HTML from Claude
      return this.convertMarkdownToHtml(this.aiScoutReport.report_content);
    },

    filteredPlayerMedia() {
      if (this.selectedMediaTab === "all") {
        return this.playerMedia;
      } else if (this.selectedMediaTab === "video") {
        return this.playerMedia.filter((m) => m.type !== "short");
      } else if (this.selectedMediaTab === "short") {
        return this.playerMedia.filter((m) => m.type === "short");
      }
      return this.playerMedia;
    },

    filteredLeads() {
      let leads = this.allPlayerLeadsData;

      // Quick filters
      if (this.filterPriority)
        leads = leads.filter((l) => l.priority === this.filterPriority);
      if (this.filterStatus)
        leads = leads.filter((l) => l.state === this.filterStatus);
      if (this.filterScout)
        leads = leads.filter((l) => l.scout_name === this.filterScout);
      return leads;
    },

    filteredGameAttendanceReports() {
      let games = this.allScoutGameAttendanceReports || [];
      // scoutOptions stores scout name as the value (same shape used by the
      // leads tab) — resolve g.scoutId to a name before comparing.
      if (this.gamesFilterScout) {
        games = games.filter(
          (g) => this.getScoutName(g.scoutId) === this.gamesFilterScout,
        );
      }
      if (this.gamesFilterAgeGroup) {
        games = games.filter(
          (g) => String(g.age_group) === String(this.gamesFilterAgeGroup),
        );
      }
      return games;
    },

    gamesAgeGroupOptions() {
      const set = new Set();
      (this.allScoutGameAttendanceReports || []).forEach((g) => {
        if (g.age_group !== null && g.age_group !== undefined && g.age_group !== "") {
          set.add(String(g.age_group));
        }
      });
      const sorted = Array.from(set).sort();
      return [
        { value: "", text: "All age groups" },
        ...sorted.map((ag) => ({ value: ag, text: ag })),
      ];
    },

    trialistCoachOptions() {
      const set = new Set();
      (this.liveTrialistsData || []).forEach((t) => {
        if (t.coach_name) set.add(t.coach_name);
      });
      const sorted = Array.from(set).sort();
      return [
        { value: "", text: "All coaches" },
        ...sorted.map((c) => ({ value: c, text: c })),
      ];
    },

    trialistTeamOptions() {
      return [
        { value: "", text: "All teams" },
        ...(this.clubTeams || []).map((t) => ({
          value: t.id,
          text: t.teamName,
        })),
      ];
    },

    trialistStatusOptions() {
      return [
        { value: "", text: "All statuses" },
        { value: "active", text: "Active" },
        { value: "ending-soon", text: "Ending soon (≤7 days)" },
        { value: "ended", text: "Ended" },
      ];
    },

    trialistSortOptions() {
      return [
        { value: "ending-soon", text: "Trial ending soonest" },
        { value: "newest", text: "Newest added" },
        { value: "name", text: "Player name (A–Z)" },
      ];
    },

    filteredTrialistsList() {
      let list = [...(this.liveTrialistsData || [])];

      if (this.trialistsFilterCoach) {
        list = list.filter((t) => t.coach_name === this.trialistsFilterCoach);
      }
      if (this.trialistsFilterTeam) {
        list = list.filter(
          (t) => Number(t.teamId) === Number(this.trialistsFilterTeam),
        );
      }
      if (this.trialistsFilterStatus) {
        list = list.filter(
          (t) => this.trialStatusFor(t) === this.trialistsFilterStatus,
        );
      }

      if (this.trialistsSortBy === "ending-soon") {
        list.sort(
          (a, b) => this.trialDaysRemaining(a) - this.trialDaysRemaining(b),
        );
      } else if (this.trialistsSortBy === "newest") {
        list.sort((a, b) => Number(b.id) - Number(a.id));
      } else if (this.trialistsSortBy === "name") {
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      }

      return list;
    },

    groupedTrialists() {
      const groups = { "ending-soon": [], active: [], ended: [] };
      this.filteredTrialistsList.forEach((t) => {
        const status = this.trialStatusFor(t);
        if (status === "ending-soon") groups["ending-soon"].push(t);
        else if (status === "ended") groups["ended"].push(t);
        else groups["active"].push(t);
      });
      return groups;
    },

    // Get player initials for avatar fallback
    scottishPlayerInitials() {
      if (!this.selectedScottishPlayer || !this.selectedScottishPlayer.name)
        return "?";
      const names = this.selectedScottishPlayer.name.trim().split(" ");
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    },

    // Generate random color for avatar based on player name
    scottishPlayerAvatarColor() {
      if (!this.selectedScottishPlayer || !this.selectedScottishPlayer.name)
        return "#6c757d";
      const colors = [
        "#3498db",
        "#2ecc71",
        "#e74c3c",
        "#f39c12",
        "#9b59b6",
        "#1abc9c",
        "#34495e",
        "#e67e22",
      ];
      const hash = this.selectedScottishPlayer.name
        .split("")
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    },
    hasPerformanceStats() {
      // Check if any player in the compare list has performance stats
      return this.compareList.some((player) => {
        return (
          player.goals !== undefined ||
          player.assists !== undefined ||
          player.appearances !== undefined ||
          player.minutes_played !== undefined ||
          player.yellow_cards !== undefined ||
          player.red_cards !== undefined ||
          (player.stats && Object.keys(player.stats).length > 0)
        );
      });
    },
    hasAdvancedMetrics() {
      // Check if any player has advanced metrics
      return this.compareList.some((player) => {
        return (
          player.pass_accuracy !== undefined ||
          player.shots_per_game !== undefined ||
          player.key_passes_per_game !== undefined ||
          player.dribbles_per_game !== undefined ||
          (player.metrics && Object.keys(player.metrics).length > 0)
        );
      });
    },

    // Opposition Analysis style heatmap computed properties
    filteredHeatmapPoints() {
      return this.heatmapPoints.filter(
        (point) => point.intensity >= this.heatmapIntensityFilter,
      );
    },

    averagePosition() {
      if (this.heatmapPoints.length === 0) {
        return { x: 50, y: 34 }; // Center of pitch
      }

      const totalTouches = this.heatmapPoints.reduce(
        (sum, point) => sum + point.touch_count,
        0,
      );
      const weightedX =
        this.heatmapPoints.reduce(
          (sum, point) => sum + point.x_coordinate * point.touch_count,
          0,
        ) / totalTouches;
      const weightedY =
        this.heatmapPoints.reduce(
          (sum, point) => sum + point.y_coordinate * point.touch_count,
          0,
        ) / totalTouches;

      return { x: weightedX, y: weightedY };
    },

    movementRange() {
      if (this.heatmapPoints.length === 0) return 0;

      const avgPos = this.averagePosition;
      const maxDistance = Math.max(
        ...this.heatmapPoints.map((point) => {
          const dx = point.x_coordinate - avgPos.x;
          const dy = point.y_coordinate - avgPos.y;
          return Math.sqrt(dx * dx + dy * dy);
        }),
      );

      return maxDistance;
    },

    totalTouches() {
      return this.heatmapPoints.reduce(
        (sum, point) => sum + point.touch_count,
        0,
      );
    },

    zoneDistribution() {
      const total = this.totalTouches;
      if (total === 0) return { defensive: 0, midfield: 0, attacking: 0 };

      let defensive = 0,
        midfield = 0,
        attacking = 0;

      this.heatmapPoints.forEach((point) => {
        const touches = point.touch_count;
        if (point.x_coordinate < 33) {
          defensive += touches;
        } else if (point.x_coordinate < 67) {
          midfield += touches;
        } else {
          attacking += touches;
        }
      });

      return {
        defensive: Math.round((defensive / total) * 100),
        midfield: Math.round((midfield / total) * 100),
        attacking: Math.round((attacking / total) * 100),
      };
    },

    mostActiveZone() {
      const zones = this.zoneDistribution;
      if (
        zones.defensive >= zones.midfield &&
        zones.defensive >= zones.attacking
      ) {
        return "Defensive Third";
      } else if (zones.midfield >= zones.attacking) {
        return "Midfield";
      } else {
        return "Attacking Third";
      }
    },

    tacticalInsights() {
      const insights = [];
      const zones = this.zoneDistribution;
      const avgPos = this.averagePosition;

      // Zone analysis
      if (zones.attacking > 40) {
        insights.push(
          "Primarily operates in attacking areas - focus on defensive marking",
        );
      } else if (zones.defensive > 50) {
        insights.push("Mainly defensive positioning - watch for forward runs");
      } else if (zones.midfield > 60) {
        insights.push("Central midfielder role - key playmaker to disrupt");
      }

      // Width analysis
      if (avgPos.x < 25 || avgPos.x > 75) {
        insights.push("Tends to play wide - susceptible to isolation");
      }

      // Movement range
      if (this.movementRange > 15) {
        insights.push("High mobility - difficult to mark consistently");
      } else if (this.movementRange < 8) {
        insights.push("Limited movement range - predictable positioning");
      }

      // Position-specific insights
      if (this.selectedScottishPlayer.position) {
        const pos = this.selectedScottishPlayer.position.toLowerCase();
        if (pos.includes("forward") && zones.defensive > 20) {
          insights.push(
            "Drops deep to collect ball - follow when tracking back",
          );
        }
      }

      if (insights.length === 0) {
        insights.push("Standard positioning pattern for this role");
      }

      return insights;
    },

    // Trialist Heatmap computed properties (legacy)
    filteredTrialistHeatmapPoints() {
      return this.heatmapPoints.filter(
        (point) => point.intensity >= this.heatmapIntensityFilter,
      );
    },

    trialistAveragePosition() {
      if (this.heatmapPoints.length === 0) {
        return { x: 50, y: 34 }; // Center of pitch
      }

      const totalTouches = this.heatmapPoints.reduce(
        (sum, point) => sum + point.touch_count,
        0,
      );
      const weightedX =
        this.heatmapPoints.reduce(
          (sum, point) => sum + point.x_coordinate * point.touch_count,
          0,
        ) / totalTouches;
      const weightedY =
        this.heatmapPoints.reduce(
          (sum, point) => sum + point.y_coordinate * point.touch_count,
          0,
        ) / totalTouches;

      return { x: weightedX, y: weightedY };
    },

    trialistMovementRange() {
      if (this.heatmapPoints.length === 0) return 0;

      const avgPos = this.trialistAveragePosition;
      const maxDistance = Math.max(
        ...this.heatmapPoints.map((point) => {
          const dx = point.x_coordinate - avgPos.x;
          const dy = point.y_coordinate - avgPos.y;
          return Math.sqrt(dx * dx + dy * dy);
        }),
      );

      return maxDistance;
    },

    trialistTotalTouches() {
      return this.heatmapPoints.reduce(
        (sum, point) => sum + point.touch_count,
        0,
      );
    },

    trialistZoneDistribution() {
      const total = this.trialistTotalTouches;
      if (total === 0) return { defensive: 0, midfield: 0, attacking: 0 };

      let defensive = 0,
        midfield = 0,
        attacking = 0;

      this.heatmapPoints.forEach((point) => {
        const touches = point.touch_count;
        if (point.x_coordinate < 33) {
          defensive += touches;
        } else if (point.x_coordinate < 67) {
          midfield += touches;
        } else {
          attacking += touches;
        }
      });

      return {
        defensive: Math.round((defensive / total) * 100),
        midfield: Math.round((midfield / total) * 100),
        attacking: Math.round((attacking / total) * 100),
      };
    },

    trialistMostActiveZone() {
      const zones = this.trialistZoneDistribution;
      if (
        zones.defensive >= zones.midfield &&
        zones.defensive >= zones.attacking
      ) {
        return "Defensive Third";
      } else if (zones.midfield >= zones.attacking) {
        return "Midfield";
      } else {
        return "Attacking Third";
      }
    },

    trialistTacticalInsights() {
      const insights = [];
      const zones = this.trialistZoneDistribution;
      const avgPos = this.trialistAveragePosition;

      // Zone analysis
      if (zones.attacking > 40) {
        insights.push(
          "Primarily operates in attacking areas - shows goal threat",
        );
      } else if (zones.defensive > 50) {
        insights.push("Mainly defensive positioning - solid foundation player");
      } else if (zones.midfield > 60) {
        insights.push("Central midfielder role - key playmaker");
      }

      // Width analysis
      if (avgPos.x < 25 || avgPos.x > 75) {
        insights.push("Tends to play wide - good wing presence");
      }

      // Movement range
      if (this.trialistMovementRange > 15) {
        insights.push("High mobility - covers lots of ground");
      } else if (this.trialistMovementRange < 8) {
        insights.push("Limited movement range - disciplined positioning");
      }

      if (insights.length === 0) {
        insights.push("Standard positioning pattern for this role");
      }

      return insights;
    },
  },
  watch: {
    selectedPlayerInfoTab(newTab) {
      if (newTab === "player-heatmap-tab") {
        // Load heatmap data when the heatmap tab is selected
        console.log("Trialist heatmap tab selected - loading data");
        this.loadTrialistPositionData();
      }
    },

    selectedScottishPlayerTab(newTab) {
      if (newTab === "player-heatmap") {
        // Load heatmap data when the Scottish player heatmap tab is selected
        console.log("Scottish player heatmap tab selected - loading data");
        this.loadHeatmapData();
      }
    },
  },
  created() {
    this.weekSelectedNumber = moment().isoWeek();
    this.setMonToSundayWeekDates();

    const clientId = JSON.parse(localStorage.getItem("the_w_selectedClientId"));
    this.getUserByIdForHeader(clientId);
    this.getUserById(clientId).then((response) => {
      if (response === true) {
        this.getTeamsByUserId();
        this.getTrialists();
        this.loadPlayerLeads();
        this.loadScouts();
        this.loadAssessments();
        this.getScoutGameAttendanceReport();
      }
    });
  },
  methods: {
    // Serve documents via the PHP endpoint (resolves the uploads/documents
    // folder + legacy root). Mirrors documents/index.vue.
    getDocumentUrl(filename) {
      if (!filename) return "";
      if (window.location.href.indexOf("8080") > -1) {
        return `http://www.localhost:8888/serve-document.php?file=${encodeURIComponent(filename)}`;
      }
      return `/personal-training-BE/serve-document.php?file=${encodeURIComponent(filename)}`;
    },
    // Append a dictated transcript onto a player's Quill HTML assessment.
    // Quill's content is HTML; the voice button gives us plain text, so we
    // wrap it in a <p> and append rather than overwriting whatever's there.
    appendTranscriptToPlayerComments(item, transcript) {
      if (!transcript) return;
      const safe = transcript
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      const existing = item.comments || "";
      item.comments = `${existing}<p>${safe}</p>`;
    },
    // Same merge pattern for the lead form's Scout notes Quill editor.
    appendTranscriptToLeadNotes(transcript) {
      if (!transcript) return;
      const safe = transcript
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      const existing = this.selectedLeadData.notes || "";
      this.selectedLeadData.notes = `${existing}<p>${safe}</p>`;
    },
    // AI Scout Report formatting methods
    convertMarkdownToHtml(markdown) {
      // Legacy markdown conversion for backward compatibility
      // TODO: Remove once backend consistently returns HTML
      let html = markdown;

      html = html.replace(
        /^#\s*PROFESSIONAL\s+SCOUTING\s+REPORT/im,
        '<h1 class="main-report-title">Professional Scouting Report</h1>',
      );
      html = html.replace(
        /^\*\*PROFESSIONAL\s+SCOUTING\s+REPORT\*\*/im,
        '<h1 class="main-report-title">Professional Scouting Report</h1>',
      );

      html = html.replace(/\*\*([A-Z][A-Z\s&:'()]+)\*\*/g, (match, title) => {
        if (html.includes('<h1 class="main-report-title">')) {
          return match;
        }
        const sectionClass = this.getSectionClass(title);
        const icon = this.getSectionIcon(title);
        return `</div></div><div class="report-section ${sectionClass}"><div class="section-header"><i class="bi ${icon}"></i><h3>${title}</h3></div><div class="section-content">`;
      });

      html =
        '<div class="report-section ai-scouting-report"><div class="section-content">' +
        html +
        "</div></div>";
      html = html.replace(
        /\*\*([^*]+?):\*\*/g,
        '<strong class="subsection-title">$1:</strong>',
      );
      html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(
        /^[-•]\s+(.+)$/gm,
        '<li><span class="bullet-icon">●</span> $1</li>',
      );
      html = html.replace(
        /(<li>.*?<\/li>\n?)+/gs,
        (match) => `<ul class="report-list">${match}</ul>`,
      );
      html = html.replace(
        /^(\d+)\.\s+(.+)$/gm,
        '<li><span class="number-badge">$1</span> $2</li>',
      );
      html = html.replace(/\n\n+/g, '</p><p class="report-paragraph">');
      html = html.replace(/\n/g, "<br>");

      return html;
    },
    getSectionClass(title) {
      const titleLower = title.toLowerCase();
      if (titleLower.includes("executive") || titleLower.includes("summary"))
        return "section-summary";
      if (titleLower.includes("strength")) return "section-strengths";
      if (titleLower.includes("development") || titleLower.includes("weakness"))
        return "section-development";
      if (titleLower.includes("recommendation"))
        return "section-recommendation";
      if (titleLower.includes("statistics") || titleLower.includes("career"))
        return "section-stats";
      return "section-default";
    },
    getSectionIcon(title) {
      const titleLower = title.toLowerCase();
      if (titleLower.includes("executive") || titleLower.includes("summary"))
        return "bi-file-text";
      if (titleLower.includes("playing style")) return "bi-diagram-3";
      if (titleLower.includes("technical")) return "bi-cpu";
      if (titleLower.includes("physical")) return "bi-lightning";
      if (titleLower.includes("defensive")) return "bi-shield-check";
      if (titleLower.includes("mental") || titleLower.includes("tactical"))
        return "bi-brain";
      if (titleLower.includes("strength")) return "bi-star-fill";
      if (titleLower.includes("development") || titleLower.includes("weakness"))
        return "bi-arrow-up-circle";
      if (titleLower.includes("statistics") || titleLower.includes("career"))
        return "bi-graph-up";
      if (titleLower.includes("form") || titleLower.includes("performance"))
        return "bi-activity";
      if (titleLower.includes("potential")) return "bi-rocket-takeoff";
      if (titleLower.includes("recommendation")) return "bi-flag-fill";
      return "bi-chevron-right";
    },
    handleImageError(event) {
      // Hide broken image and show initials fallback
      event.target.style.display = "none";
      if (event.target.nextElementSibling) {
        event.target.nextElementSibling.style.display = "flex";
      }
    },
    handleClubLogoError(event) {
      // Hide broken club logo image
      event.target.style.display = "none";
      console.warn("Failed to load club logo:", event.target.src);
    },
    getPlayerInitials(name) {
      if (!name) return "?";
      const names = name.trim().split(" ");
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return names[0].substring(0, 2).toUpperCase();
    },
    getPlayerAvatarColor(name) {
      if (!name) return "#6c757d";
      const colors = [
        "#3498db",
        "#2ecc71",
        "#e74c3c",
        "#f39c12",
        "#9b59b6",
        "#1abc9c",
        "#34495e",
        "#e67e22",
      ];
      const hash = name
        .split("")
        .reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[hash % colors.length];
    },
    async getFixtures(league, season) {
      const data = {
        action: "fixtures",
        league,
        season,
      };
      const response = await PlayerScoutSearch.find(data);
      console.log(response.data);
    },
    // DEPRECATED: Use showPlayerDetails() instead - kept for backwards compatibility
    async viewScottishPlayerDetails(player) {
      return this.showPlayerDetails(player);
    },

    // Fetch historical player attributes
    async fetchPlayerAttributesHistory(playerId) {
      this.loadingAttributesHistory = true;
      try {
        const response = await fetch(
          `http://localhost:8888/scottish-football-data/api/index.php?path=players/${playerId}/attributes`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data = await response.json();

        if (data && data.attributes && Array.isArray(data.attributes)) {
          this.playerAttributesHistory = data.attributes;
          // Set the most recent year as default (year_shift = 0)
          const currentYearAttr = data.attributes.find(
            (attr) => attr.year_shift === 0,
          );
          if (currentYearAttr) {
            this.selectedAttributeYear = currentYearAttr.attribute_year;
          }
        } else {
          this.playerAttributesHistory = [];
        }
      } catch (error) {
        console.error("Error fetching player attributes history:", error);
        this.playerAttributesHistory = [];
      } finally {
        this.loadingAttributesHistory = false;
      }
    },

    async fetchPlayerMedia(playerId) {
      this.loadingPlayerMedia = true;
      this.playerMedia = [];
      try {
        const response = await fetch(
          `http://localhost:8888/scottish-football-data/api/player_media_api.php?action=player&player_id=${playerId}&limit=50`,
        );
        const data = await response.json();

        if (data.success && data.data) {
          this.playerMedia = data.data;
        }
      } catch (error) {
        console.error("Error fetching player media:", error);
      } finally {
        this.loadingPlayerMedia = false;
      }
    },

    // Select a specific year for attribute display
    selectAttributeYear(year) {
      this.selectedAttributeYear = year;
      const yearAttributes = this.playerAttributesHistory.find(
        (attr) => attr.attribute_year === year,
      );

      if (yearAttributes) {
        // Update the selected player with the chosen year's attributes
        this.selectedScottishPlayer.attacking = yearAttributes.attacking;
        this.selectedScottishPlayer.technical = yearAttributes.technical;
        this.selectedScottishPlayer.tactical = yearAttributes.tactical;
        this.selectedScottishPlayer.defending = yearAttributes.defending;
        this.selectedScottishPlayer.creativity = yearAttributes.creativity;

        // Re-render the radar chart
        this.$nextTick(() => {
          this.renderPlayerAttributesChart();
        });
      }
    },

    // Convert EUR to GBP
    convertEURtoGBP(eurAmount) {
      const exchangeRate = 0.85; // Approximate exchange rate
      return (eurAmount * exchangeRate).toFixed(2);
    },

    // Get formatted market value with both currencies
    getMarketValueFormatted() {
      const value =
        this.selectedScottishPlayer.market_value ||
        this.selectedScottishPlayer.proposed_market_value;
      const currency =
        this.selectedScottishPlayer.market_value_currency || "EUR";

      if (!value) return "N/A";

      if (currency === "EUR") {
        const gbpValue = this.convertEURtoGBP(value);
        return `€${value} (£${gbpValue})`;
      }

      return `${currency === "GBP" ? "£" : "€"}${value}`;
    },

    // Map detailed position codes to coordinates on the pitch
    getPositionCoordinates(detailedPositions) {
      // Pitch dimensions from SVG: width=1000, height=634
      // Coordinate system: x (0-1000), y (0-634)
      const positionMap = {
        // Goalkeepers
        GK: { x: 100, y: 317 },

        // Defenders
        DC: { x: 250, y: 317 }, // Center-back
        CB: { x: 250, y: 317 },
        DL: { x: 250, y: 150 }, // Left-back
        LB: { x: 250, y: 150 },
        DR: { x: 250, y: 484 }, // Right-back
        RB: { x: 250, y: 484 },
        DWL: { x: 280, y: 120 }, // Defensive Left Wing
        DWR: { x: 280, y: 514 }, // Defensive Right Wing

        // Midfielders
        MC: { x: 500, y: 317 }, // Central Midfield
        DMC: { x: 350, y: 317 }, // Defensive Midfield
        ML: { x: 500, y: 150 }, // Left Midfield
        LM: { x: 500, y: 150 },
        MR: { x: 500, y: 484 }, // Right Midfield
        RM: { x: 500, y: 484 },
        AMC: { x: 650, y: 317 }, // Attacking Midfield
        AML: { x: 650, y: 200 }, // Attacking Left Midfield
        AMR: { x: 650, y: 434 }, // Attacking Right Midfield

        // Forwards
        FW: { x: 800, y: 317 }, // Forward
        ST: { x: 800, y: 317 }, // Striker
        CF: { x: 800, y: 317 }, // Center Forward
        LW: { x: 750, y: 150 }, // Left Winger
        RW: { x: 750, y: 484 }, // Right Winger
      };

      // Parse detailed positions if it's a JSON string
      let positions = detailedPositions;
      if (typeof detailedPositions === "string") {
        try {
          positions = JSON.parse(detailedPositions);
        } catch (e) {
          // If it's not JSON, try to extract position code
          positions = [detailedPositions];
        }
      }

      if (Array.isArray(positions) && positions.length > 0) {
        const positionCode = positions[0]; // Use first position
        return positionMap[positionCode] || { x: 500, y: 317 }; // Default to center
      }

      return { x: 500, y: 317 }; // Default to center
    },

    // Get position label from detailed position code
    getPositionLabel(detailedPositions) {
      const labelMap = {
        GK: "Goalkeeper",
        DC: "Center-Back",
        CB: "Center-Back",
        DL: "Left-Back",
        LB: "Left-Back",
        DR: "Right-Back",
        RB: "Right-Back",
        DWL: "Defensive Left Wing",
        DWR: "Defensive Right Wing",
        MC: "Central Midfield",
        DMC: "Defensive Midfield",
        ML: "Left Midfield",
        LM: "Left Midfield",
        MR: "Right Midfield",
        RM: "Right Midfield",
        AMC: "Attacking Midfield",
        AML: "Attacking Left Midfield",
        AMR: "Attacking Right Midfield",
        FW: "Forward",
        ST: "Striker",
        CF: "Center Forward",
        LW: "Left Winger",
        RW: "Right Winger",
      };

      let positions = detailedPositions;
      if (typeof detailedPositions === "string") {
        try {
          positions = JSON.parse(detailedPositions);
        } catch (e) {
          positions = [detailedPositions];
        }
      }

      if (Array.isArray(positions) && positions.length > 0) {
        return labelMap[positions[0]] || positions[0];
      }

      return "Unknown";
    },

    // AI Scout Report Methods
    async loadAIScoutReport() {
      // Check if we already have a report loaded AND displayed for this specific player
      if (
        this.aiScoutReport &&
        this.aiScoutReport.report_content &&
        this.aiScoutReport.player_id == this.selectedScottishPlayer.player_id
      ) {
        console.log(
          "Report already loaded and displayed for:",
          this.aiScoutReport.player_name,
        );
        return; // Already loaded and displayed for this player
      }

      // Clear any previous report data
      this.aiScoutReport = null;
      this.aiReportError = null;

      // Check usage limits and load cached report
      await this.checkAIReportLimit();
    },

    async checkAIReportLimit() {
      try {
        const response = await fetch(
          `http://localhost:8888/back-end-endpoint-ai-scout-report.php?action=check_limit&user_id=${this.userData.id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data = await response.json();

        if (data.success) {
          this.aiReportLimitInfo = {
            used: data.limit_info.used,
            remaining: data.limit_info.remaining,
            limit: data.limit_info.limit,
          };

          // Check if we have a cached report
          await this.checkCachedReport();
        } else {
          console.error("Error checking AI report limit:", data.message);
        }
      } catch (error) {
        console.error("Error checking AI report limit:", error);
      }
    },

    async checkCachedReport() {
      if (
        !this.selectedScottishPlayer ||
        !this.selectedScottishPlayer.player_id
      ) {
        return;
      }

      try {
        const response = await fetch(
          `http://localhost:8888/back-end-endpoint-ai-scout-report.php?action=get_cached&player_id=${this.selectedScottishPlayer.player_id}&user_id=${this.userData.id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        const data = await response.json();

        console.log("Cache check response:", data);

        // CRITICAL: Verify the cached report is for the CURRENT player (use loose equality)
        if (
          data.success &&
          data.cached &&
          data.report &&
          data.report.player_id == this.selectedScottishPlayer.player_id
        ) {
          this.aiScoutReport = {
            ...data.report,
            report_metrics: data.report.report_metrics || null,
            cached: true,
          };
          console.log(
            "✓ Loaded cached report for player:",
            data.report.player_name,
          );
        } else {
          console.log("No cached report found for this player");
        }
      } catch (error) {
        console.error("Error checking cached report:", error);
      }
    },

    async generateAIReport(forceRegenerate = false) {
      if (!this.canGenerateReport && !forceRegenerate) {
        this.$toast.error(
          "Daily report limit reached. Please try again tomorrow.",
          {
            title: "Limit Reached",
            toaster: "b-toaster-bottom-right",
            solid: true,
            appendToast: true,
            variant: "warning",
          },
        );
        return;
      }

      // Confirm regeneration if forcing
      if (forceRegenerate) {
        const confirmed = window.confirm(
          "This will generate a new report and count towards your daily limit. Continue?",
        );

        if (!confirmed) return;
      }

      this.loadingAIReport = true;
      this.aiReportError = null;

      try {
        // Prepare player data for the AI
        const playerData = this.formatPlayerDataForAI();

        console.log("=== GENERATE AI REPORT DEBUG ===");
        console.log("Current player:", this.selectedScottishPlayer.name);
        console.log(
          "Current player_id:",
          this.selectedScottishPlayer.player_id,
        );
        console.log("Player data being sent:", playerData);

        const response = await fetch(
          `http://localhost:8888/back-end-endpoint-ai-scout-report.php`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              action: "generate",
              user_id: this.userData.id,
              player_data: playerData,
              force_regenerate: forceRegenerate,
            }),
          },
        );

        const data = await response.json();

        console.log("Response received:", data);
        console.log("Response player_id:", data.report?.player_id);
        console.log(
          "Expected player_id:",
          this.selectedScottishPlayer.player_id,
        );

        if (data.success) {
          // CRITICAL: Verify the generated report is for the CURRENT player
          if (data.report.player_id != this.selectedScottishPlayer.player_id) {
            console.error("Report player_id mismatch!", {
              expected: this.selectedScottishPlayer.player_id,
              expectedType: typeof this.selectedScottishPlayer.player_id,
              received: data.report.player_id,
              receivedType: typeof data.report.player_id,
            });
            this.aiReportError =
              "Report generated for wrong player. Please try again.";
            this.$toast.error(this.aiReportError, {
              title: "Error",
              toaster: "b-toaster-bottom-right",
              solid: true,
              appendToast: true,
              variant: "danger",
            });
            this.loadingAIReport = false;
            return;
          }

          this.aiScoutReport = {
            ...data.report,
            report_metrics: data.report.report_metrics || null,
            cached: false,
          };

          console.log(
            "Generated new report for player:",
            data.report.player_name,
          );

          // Update limit info
          if (data.usage) {
            this.aiReportLimitInfo = {
              used: data.usage.used,
              remaining: data.usage.remaining,
              limit: data.usage.limit,
            };
          }

          this.$toast.success("Scout report generated successfully!", {
            title: "Success",
            toaster: "b-toaster-bottom-right",
            solid: true,
            appendToast: true,
            variant: "success",
          });
        } else {
          this.aiReportError = data.message || "Failed to generate report";
          this.$toast.error(this.aiReportError, {
            title: "Error",
            toaster: "b-toaster-bottom-right",
            solid: true,
            appendToast: true,
            variant: "danger",
          });
        }
      } catch (error) {
        console.error("Error generating AI report:", error);
        this.aiReportError =
          "An unexpected error occurred while generating the report";
        this.$toast.error(this.aiReportError, {
          title: "Error",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "danger",
        });
      } finally {
        this.loadingAIReport = false;
      }
    },

    formatPlayerDataForAI() {
      const player = this.selectedScottishPlayer;

      return {
        player_id: player.player_id,
        name: player.name,
        age: player.age,
        position: player.position,
        team_name: player.team_name,
        league_name: player.league_name,
        nationality: player.nationality,
        height_cm: player.height_cm,
        weight_kg: player.weight_kg,
        preferred_foot: player.preferred_foot,

        // Performance stats
        appearances: player.appearances || 0,
        starts: player.starts || 0,
        minutes_played: player.minutes_played || 0,
        goals: player.goals || 0,
        assists: player.assists || 0,
        overall_rating: player.overall_rating || 0,

        // Nested stats objects
        shooting: player.shooting || {},
        passing: player.passing || {},
        crossing: player.crossing || {},
        dribbling: player.dribbling || {},
        duels: player.duels || {},
        defensive: player.defensive || {},
        possession: player.possession || {},
        penalties: player.penalties || {},
        goalkeeper: player.goalkeeper || {},
        ratings: player.ratings || {},
        advanced: player.advanced || {},
      };
    },

    formatReportDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatViewCount(count) {
      if (!count) return "0";
      if (count >= 1000000) {
        return (count / 1000000).toFixed(1) + "M";
      } else if (count >= 1000) {
        return (count / 1000).toFixed(1) + "K";
      }
      return count.toString();
    },

    formatPublishDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    },

    getRecommendationClass(recommendation) {
      if (!recommendation) return "rec-default";
      const rec = recommendation.toLowerCase();
      if (rec.includes("strong sign") || rec.includes("highly recommend"))
        return "rec-strong-sign";
      if (rec.includes("trial")) return "rec-trial";
      if (rec.includes("monitor")) return "rec-monitor";
      if (rec.includes("pass")) return "rec-pass";
      return "rec-default";
    },

    exportScoutReport() {
      // TODO: Implement PDF export functionality
      this.$toast.info("PDF export feature coming soon", {
        title: "Coming Soon",
        toaster: "b-toaster-bottom-right",
        solid: true,
        appendToast: true,
        variant: "info",
      });
    },

    async addScottishPlayerAsLead(player) {
      // Add Scottish player as a lead in the trialist system
      try {
        this.selectedLeadData = {
          name: player.name,
          club_name: player.team_name,
          age_group: new Date().getFullYear() - player.age + 18, // Estimate age group
          playing_position: player.position,
          state: "Still to Watch",
          scout_name: this.userData.name,
          dob: null, // Would need to be calculated from age
          notes: `Scottish Football Data import - League: ${player.league_name}, Team: ${player.team_name}, Overall Rating: ${player.overall_rating}`,
        };

        this.openLeadsModal("new");

        this.$toast.info(`Added ${player.name} as a new lead`, {
          title: "Lead Added",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      } catch (error) {
        console.error("Error adding player as lead:", error);
        this.$toast.info("Error adding player as lead", {
          title: "Error",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "danger",
        });
      }
    },
    // DEPRECATED: Use showPlayerDetails() instead - kept for backwards compatibility
    async viewPlayerDetails(player) {
      return this.showPlayerDetails(player);
    },
    capitalize(str) {
      if (!str) return "";
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    priorityBadgeVariant(priority) {
      if (priority === "hot") return "danger";
      if (priority === "warm") return "warning";
      if (priority === "cold") return "info";
      return "secondary";
    },
    updatePlayerLeadPriority(lead) {
      this.updatePlayerLeadState(lead);
    },
    archiveLead(lead) {
      lead.archived = true;
      this.updatePlayerLeadState(lead);
    },
    formatRelativeDate(value) {
      if (!value) return "—";
      const d = dayjs(value);
      if (!d.isValid()) return "—";
      const days = dayjs().startOf("day").diff(d.startOf("day"), "day");
      if (days === 0) return "Today";
      if (days === 1) return "Yesterday";
      if (days === -1) return "Tomorrow";
      if (days > 1 && days < 7) return `${days} days ago`;
      if (days < -1 && days > -7) return `In ${Math.abs(days)} days`;
      if (days >= 7 && days < 30) return `${Math.floor(days / 7)}w ago`;
      if (days <= -7 && days > -30) return `In ${Math.floor(Math.abs(days) / 7)}w`;
      return d.format("DD MMM YYYY");
    },
    lastContactClass(value) {
      if (!value) return "lead-cell-empty";
      const days = dayjs().startOf("day").diff(dayjs(value).startOf("day"), "day");
      if (days >= 30) return "lead-date stale";
      if (days >= 14) return "lead-date warning";
      return "lead-date fresh";
    },
    followUpClass(value) {
      if (!value) return "lead-cell-empty";
      const days = dayjs().startOf("day").diff(dayjs(value).startOf("day"), "day");
      if (days > 0) return "lead-date overdue";
      if (days === 0) return "lead-date due-today";
      if (days >= -3) return "lead-date due-soon";
      return "lead-date";
    },
    async markContactedToday(lead) {
      const today = dayjs().format("YYYY-MM-DD");

      // Optimistic update so the green "Today" pill appears immediately.
      const idx = this.allPlayerLeadsData.findIndex(
        (l) => Number(l.id) === Number(lead.id),
      );
      if (idx >= 0) {
        this.allPlayerLeadsData = [
          ...this.allPlayerLeadsData.slice(0, idx),
          { ...this.allPlayerLeadsData[idx], last_contact_date: today },
          ...this.allPlayerLeadsData.slice(idx + 1),
        ];
      }

      const response = await Trialists.find({
        action: "updateLeadContactDate",
        leadId: lead.id,
        last_contact_date: today,
      }).catch((error) => {
        console.error("markContactedToday request failed:", error);
        return null;
      });

      const ok = response && response.data && response.data.success;
      if (!ok) {
        const errMsg =
          (response && response.data && response.data.error) ||
          "Could not save the contact date.";
        this.$toast.error(errMsg, {
          title: "Update failed",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "danger",
        });
        this.loadPlayerLeads();
        return;
      }

      this.$toast.info(`Marked ${lead.name} as contacted today.`, {
        title: "Updated",
        toaster: "b-toaster-bottom-right",
        solid: true,
        appendToast: true,
        variant: "success",
      });
      this.loadPlayerLeads();
    },
    async updatePlayerLeadState(lead) {
      const data = {
        action: "updatePlayerLeadState",
        leadData: lead,
      };
      const response = await Trialists.find(data).catch((error) => {
        console.log(error);
      });

      if (response && response.status === 200) {
        this.$toast.info("Lead updated.", {
          title: "Updated",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
        this.loadPlayerLeads();
      }
    },
    getGameTypeClass(gameType) {
      const typeClasses = {
        League: "badge-primary",
        Cup: "badge-danger",
        Friendly: "badge-success",
        Tournament: "badge-warning",
        Playoff: "badge-info",
      };
      return typeClasses[gameType] || "badge-secondary";
    },
    updateDate(object, field, value) {
      this[object] = {
        ...this[object],
        [field]: this.formatDateForDB(value),
      };

      console.log(`Updated ${object} ${field} to: ${this[object][field]}`);
    },
    getDayAndDate(date) {
      return moment(date).format("dddd, MMMM Do YYYY");
    },
    formatISOStringDateAndTime(timeDateString) {
      return moment(timeDateString).utc().format("h:mm a");
    },
    async populatefixtures() {
      const data = {
        action: "getAllGames",
      };
      const responce = await Games.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.allGamesData = responce.data;
      }
    },
    openPlayerInterestsHobbiesModal(index) {
      this.playerInterestsHobbiesClose();
      if (index !== undefined) {
        this.selectedPlayerInterestsHobbiesData =
          this.playerCvData.interests_hobbies[index];
        this.selectedPlayerInterestsHobbiesDataEdit = true;
      }
      this.showOpenPlayerInterestsHobbiesModal = true;
    },
    async editPlayerInterestsHobbies() {
      const data = {
        action: "editPlayerInterestsHobbies",
        playerInterestsHobbies: this.selectedPlayerInterestsHobbiesData,
      };
      const responce = await PlayerCv.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.populatePlayerPlayingExperenceData();
        this.$toast.info("Player Interests and Hobbies updated.", {
          title: "saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });

        this.showOpenPlayerInterestsHobbiesModal = false;
      }
    },
    openPlayerEducationModal(index) {
      this.playerEducationModalClose();
      if (index !== undefined) {
        this.selectedPlayerEducationData = this.playerCvData.education[index];
        this.selectedPlayerEducationDataEdit = true;
      }
      this.showOpenPlayerEducationModal = true;
    },
    async populatePlayerPlayingExperenceData() {
      const data = {
        action: "populatePlayerPlayingExperenceData",
        userId: this.selectedUserData.id,
      };
      const responce = await PlayerCv.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.playerCvData.playingExperence = responce.data;
      }
    },
    formatDateForDB(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    updateDatesForDB(objectName, itemName) {
      if (this[objectName][itemName] !== null) {
        this[objectName][itemName] = this.formatDateForDB(
          this[objectName][itemName],
        );
      }
    },
    async savePlayerPlayingExperence() {
      this.updateDatesForDB(
        "selectedPlayingExperenceData",
        "start_date_at_club",
      );
      this.updateDatesForDB("selectedPlayingExperenceData", "end_date_at_club");
      this.selectedPlayingExperenceData.player_id = this.selectedUserData.id;

      const data = {
        playingExperence: this.selectedPlayingExperenceData,
        action: "savePlayerPlayingExperence",
      };
      const responce = await PlayerCv.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.populatePlayerPlayingExperenceData();
        this.$toast.info("Player Playing Experence saved.", {
          title: "saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });

        this.showPlayerCVPlayingExperenceModal = false;
      }
    },
    async editPlayingExperence() {
      this.updateDatesForDB(
        "selectedPlayingExperenceData",
        "start_date_at_club",
      );
      this.updateDatesForDB("selectedPlayingExperenceData", "end_date_at_club");
      const data = {
        action: "editPlayingExperence",
        playingExperence: this.selectedPlayingExperenceData,
      };
      const responce = await PlayerCv.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.populatePlayerPlayingExperenceData();
        this.$toast.info("Player Playing Experence updated.", {
          title: "saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });

        this.showPlayerCVPlayingExperenceModal = false;
      }
    },
    playerPlayerVideosClose() {
      this.selectedPlayerVideoData = {};
      this.selectedPlayerVideoDataEdit = false;
    },
    playerCVPlayingExperenceModalClose() {
      this.selectedPlayingExperenceData = {};
      this.selectedPlayingExperenceEdit = false;
    },
    playerEducationModalClose() {
      this.selectedPlayerEducationData = {};
      this.selectedPlayerEducationDataEdit = false;
    },
    playerInterestsHobbiesClose() {
      this.selectedPlayerInterestsHobbiesData = {};
      this.selectedPlayerInterestsHobbiesDataEdit = false;
    },
    openPlayerCVPlayingExperenceModal(index) {
      console.log(index);
      this.playerCVPlayingExperenceModalClose();
      if (index !== undefined) {
        this.selectedPlayingExperenceData =
          this.playerCvData.playingExperence[index];
        this.selectedPlayingExperenceEdit = true;
      }
      this.showPlayerCVPlayingExperenceModal = true;
    },
    openPlayerCVpersonalProfileModal() {
      this.showPlayerCVpersonalProfileModal = true;
    },
    async savePersonalProfile() {
      const data = {
        personalProfileData: this.playerCvData.personal_profile,
        userId: this.selectedUserData.id,
        action: "savePersonalProfile",
      };
      const responce = await Users.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.$toast.info("Player Personal Profile saved.", {
          title: "saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });

        this.showPlayerCVpersonalProfileModal = false;
      }
    },
    produceThePlayerCV() {
      this.showViewPlayersCV = true;
      setTimeout(() => {
        let pdf = this.buildThePlayerCVPDF();
        pdf.previewLink.src = pdf.pdfData.output("datauristring");
        this.showCVReport = true;
      }, 500);
    },
    addPlayingExperience(pdf, height) {
      pdf.setFontSize(10).setFont(undefined, "normal");
      height = height + 85;
      height = Math.round(height);

      this.playerCvData.playingExperence.map((item) => {
        let name = pdf.splitTextToSize(item.club, 50);

        pdf.text(5, height, name);
        pdf.text(70, height, this.formatDateForHuman(item.start_date_at_club));
        pdf.text(
          70,
          height + 5,
          this.formatDateForHuman(item.end_date_at_club),
        );

        let paraOneLines = pdf.splitTextToSize(item.playing_info, 90);
        pdf.text(115, height, paraOneLines);

        let dynamicHeight = pdf.getTextDimensions(paraOneLines);
        height = this.newPageCheck(height, dynamicHeight, pdf);
      });

      return height;
    },
    addEducation(pdf, height) {
      pdf.setFontSize(10).setFont(undefined, "normal");
      height = Math.round(height);

      this.playerCvData.education.map((item) => {
        let name = pdf.splitTextToSize(item.title, 50);
        pdf.text(5, height, name);
        pdf.text(70, height, this.formatDateForHuman(item.start_date));
        pdf.text(70, height + 5, this.formatDateForHuman(item.end_date));

        let paraOneLines = pdf.splitTextToSize(item.more_info, 90);
        pdf.text(115, height, paraOneLines);

        let dynamicHeight = pdf.getTextDimensions(paraOneLines);
        height = this.newPageCheck(height, dynamicHeight, pdf);
      });

      return height;
    },
    addInterest(pdf, height) {
      pdf.setFontSize(10).setFont(undefined, "normal");
      height = Math.round(height);

      this.playerCvData.interests_hobbies.map((item) => {
        let interest = pdf.splitTextToSize(item.interests, 50);

        pdf.text(5, height, interest);
        let paraOneLines = pdf.splitTextToSize(item.more_info, 130);
        pdf.text(70, height, paraOneLines);

        let dynamicHeight = pdf.getTextDimensions(paraOneLines);
        height = this.newPageCheck(height, dynamicHeight, pdf);
      });

      return height;
    },
    buildThePlayerCVPDF() {
      let previewContainer = document.getElementById("thePlayerCVPDF_preview");

      // load player image //
      let img = document.querySelector("#user_photo");
      let pdf = new JsPDF("p", "mm", "a4");

      pdf.setTextColor(3, 86, 158);
      if (img && img.complete && img.naturalWidth > 0) {
        try {
          pdf.addImage(img, "PNG", 1, 0, "40", "40");
        } catch (e) {
          console.warn("Could not embed player photo in CV PDF:", e);
        }
      }

      pdf.setFontSize(23).setFont(undefined, "bold");
      pdf.text(45, 18, this.selectedUserData.name, { maxWidth: 70 });

      pdf.setFontSize(16);
      pdf.text(5, 55, this.selectedUserData.dob);

      pdf.setFontSize(16).setFont(undefined, "bold");
      pdf.text(5, 65, "Personal profile");

      pdf.setFontSize(14).setFont(undefined, "normal");

      let paraOne = this.selectedUserData.personal_profile;

      let paraOneLines = pdf.splitTextToSize(paraOne, 185);
      pdf.text(5, 75, paraOneLines);

      let dynamicHeight = pdf.getTextDimensions(paraOneLines);

      dynamicHeight.h = dynamicHeight.h + 10;
      pdf.setFontSize(16).setFont(undefined, "bold");
      pdf.text(5, 75 + dynamicHeight.h, "Playing experience");

      dynamicHeight.h = dynamicHeight.h + 10;
      pdf.setFontSize(12).setFont(undefined, "bold");
      pdf.text(5, 75 + dynamicHeight.h, "Club");
      pdf.text(70, 75 + dynamicHeight.h, "Time at the club");
      pdf.text(115, 75 + dynamicHeight.h, "Playing stats");

      dynamicHeight.h = this.addPlayingExperience(pdf, dynamicHeight.h);

      pdf.setFontSize(16).setFont(undefined, "bold");
      pdf.text(5, dynamicHeight.h + 10, "Education");

      dynamicHeight.h = this.addEducation(pdf, dynamicHeight.h + 20);

      pdf.setFontSize(16).setFont(undefined, "bold");
      pdf.text(5, dynamicHeight.h + 10, "Interest hobbies");

      dynamicHeight.h = dynamicHeight.h + 20;
      pdf.setFontSize(12).setFont(undefined, "bold");
      pdf.text(5, dynamicHeight.h, "Interest hobbies");
      pdf.text(70, dynamicHeight.h, "More information");

      dynamicHeight.h = this.addInterest(pdf, dynamicHeight.h + 10);

      dynamicHeight.h = dynamicHeight.h + 20;
      pdf.setFontSize(16).setFont(undefined, "bold");
      pdf.text(5, dynamicHeight.h, "Playing videos");

      dynamicHeight.h = this.addPlayerVideos(pdf, dynamicHeight.h + 10);

      return { pdfData: pdf, previewLink: previewContainer };
    },
    checkUserPosition() {
      return Object.keys(this.selectedUserData).length === 0
        ? this.userData.position
        : this.selectedUserData.position;
    },
    removeTrialistTeamFilter() {
      this.liveTrialistsDataFiltered = this.liveTrialistsData;
      this.hideRemoveTeamFilterButton = true;
    },
    filterBy(arrayToFilter, filterBy) {
      const filterResult = this[arrayToFilter].filter(
        (player) => player[filterBy] === this.selectedFilterData,
      );

      this.liveTrialistsDataFiltered = filterResult;
      this.hideRemoveTeamFilterButton = false;
    },
    getScoutNameFromId(id) {
      const scout = this.allScoutsData.find((scout) => scout.id === Number(id));
      return scout ? scout.name : "";
    },
    lookUpSearch(value) {
      if (value.length > 2) {
        this.filteredTrialistsData = [];

        const results = Array.from(value).reduce(
          (a, v, i) => `${a}[^${value.substr(i)}]*?${v}`,
          "",
        );
        const regEx = RegExp(results);

        this.liveTrialistsData.filter((v) => {
          console.log(v);
          if (v.name.match(regEx)) {
            this.filteredTrialistsData.push(v);
          }
        });
      } else {
        this.filteredTrialistsData = [];
      }
    },
    async getAllCoachesAttachedToTeam(teamId) {
      const data = {
        teamId: teamId,
        action: "getAllCoachesAttachedToTeam",
      };
      const responce = await Teams.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        return responce.data;
      }
    },
    async selectTeamSelectCoach(key, teamId) {
      const coaches = await this.getAllCoachesAttachedToTeam(teamId);
      this[key].coach_id = coaches[0].id;
    },
    async extendTrialSubmit() {
      this.selectedTrialistsData = this.extendTrial;
      await this.getUserByTrialistId(this.selectedTrialistsData.id);
      this.saveTrialistPerformanceReview();
      this.showExtendTrialModal = false;
    },
    extendTrialModal(player) {
      this.extendTrial = { ...player };
      this.extendTrial.trial_length = 0;
      this.showExtendTrialModal = true;
    },
    addItem() {
      const selectedMatchAttendedPlayerData = {
        name: "",
        position: "",
        attitude: 0,
        skill: 0,
        physique: 0,
        mentality: 0,
        leadership: 0,
        professionalism: 0,
        comments: "",
        trialistId: null,
        leadId: null,
        showAdvancedProfiling: false,
        // Phase 2: Advanced Player Demographics & Profiling
        demographics: {
          age: null,
          nationality: "",
          preferredFoot: "",
          height: null,
          weight: null,
          previousClub: "",
          contractStatus: "",
          agentContact: "",
          parentGuardian: "",
          emergencyContact: "",
        },
        physicalAttributes: {
          pace: 0,
          strength: 0,
          stamina: 0,
          agility: 0,
          balance: 0,
          jumping: 0,
          injury_history: "",
          fitness_level: "",
        },
        technicalSkills: {
          passing: 0,
          shooting: 0,
          dribbling: 0,
          crossing: 0,
          tackling: 0,
          heading: 0,
          firstTouch: 0,
          weakFootAbility: 0,
        },
        mentalAttributes: {
          vision: 0,
          decisionMaking: 0,
          composure: 0,
          workRate: 0,
          teamwork: 0,
          communication: 0,
          coachability: 0,
          competitiveness: 0,
        },
        matchPerformance: {
          minutesPlayed: null,
          goals: 0,
          assists: 0,
          keyPasses: 0,
          tackles: 0,
          interceptions: 0,
          foulsCommitted: 0,
          yellowCards: 0,
          redCards: 0,
          cleanSheets: 0,
          saves: 0,
        },
        developmentPotential: {
          overallRating: 0,
          potentialRating: 0,
          improvementAreas: "",
          strengths: "",
          nextSteps: "",
          recommendedTraining: "",
          coachNotes: "",
        },
      };
      this.selectedMatchAttendedData.playersGameData = [
        ...this.selectedMatchAttendedData.playersGameData,
        selectedMatchAttendedPlayerData,
      ];
    },

    // Toggle advanced profiling sections with proper Vue 3 reactivity
    toggleAdvancedProfiling(item) {
      item.showAdvancedProfiling = !item.showAdvancedProfiling;
      // Force reactivity update
      this.$forceUpdate();
    },

    removeItem(index, id) {
      this.selectedMatchAttendedData.playersGameData.splice(index, 1);
      this.removedPlayersBeingAssessed(id);
    },
    async removedPlayersBeingAssessed(id) {
      const data = {
        action: "removedPlayersBeingAssessed",
        id: id,
      };

      const responce = await Trialists.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.showGameAttendanceReportModal = false;

        this.$toast.info("Game attendance player report removed.", {
          title: "removed",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async addEditGameAttendanceModel(id, type) {
      if (type === "edit") {
        this.editMode = true;
        let scoutGameReportData = this.allScoutGameAttendanceReports.find(
          (scout) => Number(scout.id) === Number(id),
        );
        scoutGameReportData.playersGameData = [];

        Object.assign(this.selectedMatchAttendedData, {
          age_group: scoutGameReportData.age_group,
          awayTeam: scoutGameReportData.awayTeam,
          clubId: scoutGameReportData.clubId,
          game_comments: scoutGameReportData.game_comments,
          homeTeam: scoutGameReportData.homeTeam,
          id: scoutGameReportData.id,
          matchDate: scoutGameReportData.matchDate,
          playersGameData: scoutGameReportData.playersGameData,
          scoutId: scoutGameReportData.scoutId,
          weather_conditions: scoutGameReportData.weather_conditions,
        });

        const response = await this.getplayersBeingassesedByScoutReportId(id);

        if (response !== undefined) {
          this.selectedMatchAttendedData.playersGameData = response;
          this.getScoutGameAttendanceReport();

          if (type !== "edit") {
            this.$toast.info("Game attendance added.", {
              title: "added",
              toaster: "b-toaster-bottom-right",
              solid: true,
              appendToast: true,
              variant: "success",
            });
          }
        }
      } else {
        this.editMode = false;
        this.selectedMatchAttendedData = {
          clubId: "",
          homeTeam: "",
          awayTeam: "",
          age_group: "",
          matchDate: "",
          weather_conditions: "",
          game_comments: "",
          playersGameData: [],
          scoutId: null,
          scoutName: "",
          // Enhanced Game Context & Demographics
          gameContext: {
            competition: "",
            division: "",
            gameType: "",
            venue: {
              name: "",
              address: "",
              capacity: null,
              surface: "",
            },
            gameResult: {
              homeScore: null,
              awayScore: null,
              attendance: null,
            },
            gameImportance: "",
            broadcastLevel: "",
          },
        };
      }
      this.showGameAttendanceReportModal = true;
    },
    resetInviteState() {
      this.invite = {
        teamId: "",
        trialLength: "6",
        startDate: "",
        token: "",
        link: "",
        loading: false,
        copied: false,
        error: "",
        recipientEmail: "",
        emailSending: false,
        emailSent: false,
      };
    },
    async emailTrialInvite() {
      this.invite.emailSending = true;
      this.invite.error = "";

      const data = {
        action: "sendTrialistInvite",
        clubId: this.userData.clubId,
        leadId:
          this.selectedLeadData.id !== undefined ? this.selectedLeadData.id : 0,
        teamId: this.invite.teamId,
        trial_length: this.invite.trialLength,
        trial_start_date:
          this.invite.startDate !== "" && this.invite.startDate !== null
            ? moment(this.invite.startDate).format("YYYY-MM-DD")
            : "",
        player_name:
          this.selectedLeadData.name !== undefined
            ? this.selectedLeadData.name
            : "",
        recipientEmail: this.invite.recipientEmail,
      };

      const responce = await Trialists.find(data).catch((error) => {
        return error !== undefined && error.response !== undefined
          ? error.response
          : null;
      });

      if (responce !== null && responce.status === 200 && responce.data.sent) {
        this.invite.emailSent = true;
        this.$toast.success(`Invite emailed to ${this.invite.recipientEmail}`, {
          title: "Invite sent",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
        setTimeout(() => {
          this.invite.emailSent = false;
        }, 4000);
      } else {
        this.invite.error =
          responce !== null && responce.data && responce.data.error
            ? responce.data.error
            : "The invitation could not be emailed. Please try again.";
      }
      this.invite.emailSending = false;
    },
    async generateTrialInvite() {
      this.invite.loading = true;
      this.invite.error = "";
      this.invite.copied = false;

      const data = {
        action: "createTrialistInvite",
        clubId: this.userData.clubId,
        leadId:
          this.selectedLeadData.id !== undefined ? this.selectedLeadData.id : 0,
        teamId: this.invite.teamId,
        trial_length: this.invite.trialLength,
        trial_start_date:
          this.invite.startDate !== "" && this.invite.startDate !== null
            ? moment(this.invite.startDate).format("YYYY-MM-DD")
            : "",
        player_name:
          this.selectedLeadData.name !== undefined
            ? this.selectedLeadData.name
            : "",
      };

      const responce = await Trialists.find(data).catch(() => null);

      if (
        responce !== null &&
        responce.status === 200 &&
        responce.data.token !== undefined
      ) {
        this.invite.token = responce.data.token;
        this.invite.link = `${window.location.origin}/trialists/invite/${responce.data.token}`;
      } else {
        this.invite.error =
          "The invite link could not be created. Please try again.";
      }
      this.invite.loading = false;
    },
    copyInviteLink() {
      navigator.clipboard
        .writeText(this.invite.link)
        .then(() => {
          this.invite.copied = true;
          this.$toast.success("Invite link copied to clipboard.", {
            title: "Copied",
            toaster: "b-toaster-bottom-right",
            solid: true,
            appendToast: true,
            variant: "success",
          });
          setTimeout(() => {
            this.invite.copied = false;
          }, 3000);
        })
        .catch(() => {
          this.invite.error =
            "Couldn't copy automatically — select the link text and copy it manually.";
        });
    },
    getScoutName(id) {
      const scoutData = this.allScoutsData.find(
        (scout) => Number(scout.id) === Number(id),
      );
      return scoutData !== undefined ? scoutData.name : "";
    },
    populateAssignedScout(scoutId, type) {
      if (type === "game attendance") {
        const scoutData = this.allScoutsData.find(
          (scout) => Number(scout.id) === Number(scoutId.target.value),
        );
        this.selectedMatchAttendedData.scoutId = scoutId.target.value;
        this.selectedMatchAttendedData.scoutName = scoutData.name;
      } else {
        if (scoutId.target.value === "") {
          this.selectedLeadData.scout_name = "";
          this.selectedLeadData.scout_contact = "";
          this.selectedLeadData.scoutId = "";
        }
        const scoutData = this.allScoutsData.find(
          (scout) => Number(scout.id) === Number(scoutId.target.value),
        );
        this.selectedLeadData.scout_name = scoutData.name;
        this.selectedLeadData.scout_contact = scoutData.email;
        this.selectedLeadData.scoutId = scoutId.target.value;
      }
    },
    async getplayersBeingassesedByScoutReportId(id) {
      const data = {
        action: "getplayersBeingassesedByScoutReportId",
        id: id,
      };

      const responce = await Trialists.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        // Map the flat database structure back to nested objects
        return responce.data.map((player) =>
          this.mapPlayerDataFromDatabase(player),
        );
      }
    },

    // Phase 2: Map flat database structure to nested object structure
    mapPlayerDataFromDatabase(dbPlayer) {
      return {
        ...dbPlayer,
        showAdvancedProfiling: false, // Initialize toggle state
        demographics: {
          age: dbPlayer.demographics_age,
          nationality: dbPlayer.demographics_nationality || "",
          preferredFoot: dbPlayer.demographics_preferred_foot || "",
          height: dbPlayer.demographics_height,
          weight: dbPlayer.demographics_weight,
          previousClub: dbPlayer.demographics_previous_club || "",
          contractStatus: dbPlayer.demographics_contract_status || "",
          emergencyContact: dbPlayer.demographics_emergency_contact || "",
        },
        physicalAttributes: {
          pace: dbPlayer.physical_pace || 0,
          strength: dbPlayer.physical_strength || 0,
          stamina: dbPlayer.physical_stamina || 0,
          agility: dbPlayer.physical_agility || 0,
          balance: dbPlayer.physical_balance || 0,
          jumping: dbPlayer.physical_jumping || 0,
          injury_history: dbPlayer.physical_injury_history || "",
          fitness_level: dbPlayer.physical_fitness_level || "",
        },
        technicalSkills: {
          passing: dbPlayer.technical_passing || 0,
          shooting: dbPlayer.technical_shooting || 0,
          dribbling: dbPlayer.technical_dribbling || 0,
          crossing: dbPlayer.technical_crossing || 0,
          tackling: dbPlayer.technical_tackling || 0,
          heading: dbPlayer.technical_heading || 0,
          firstTouch: dbPlayer.technical_first_touch || 0,
          weakFootAbility: dbPlayer.technical_weak_foot || 0,
        },
        mentalAttributes: {
          vision: dbPlayer.mental_vision || 0,
          decisionMaking: dbPlayer.mental_decision_making || 0,
          composure: dbPlayer.mental_composure || 0,
          workRate: dbPlayer.mental_work_rate || 0,
          teamwork: dbPlayer.mental_teamwork || 0,
          communication: dbPlayer.mental_communication || 0,
          coachability: dbPlayer.mental_coachability || 0,
          competitiveness: dbPlayer.mental_competitiveness || 0,
        },
        matchPerformance: {
          minutesPlayed: dbPlayer.match_minutes_played,
          goals: dbPlayer.match_goals || 0,
          assists: dbPlayer.match_assists || 0,
          keyPasses: dbPlayer.match_key_passes || 0,
          tackles: dbPlayer.match_tackles || 0,
          interceptions: dbPlayer.match_interceptions || 0,
          foulsCommitted: dbPlayer.match_fouls_committed || 0,
          yellowCards: dbPlayer.match_yellow_cards || 0,
          redCards: dbPlayer.match_red_cards || 0,
          cleanSheets: dbPlayer.match_clean_sheets || 0,
          saves: dbPlayer.match_saves || 0,
        },
        developmentPotential: {
          overallRating: dbPlayer.development_overall_rating || 0,
          potentialRating: dbPlayer.development_potential_rating || 0,
          strengths: dbPlayer.development_strengths || "",
          improvementAreas: dbPlayer.development_improvement_areas || "",
          nextSteps: dbPlayer.development_next_steps || "",
          recommendedTraining:
            dbPlayer.development_training_recommendations || "",
          coachNotes: dbPlayer.development_coach_notes || "",
        },
      };
    },
    async updateScoutGameAttendanceReport() {
      if (typeof this.selectedMatchAttendedData.matchDate === "object") {
        this.selectedMatchAttendedData.matchDate = dayjs(
          this.selectedMatchAttendedData.matchDate,
        ).format("YYYY-MM-DD");
      }
      if (typeof this.selectedMatchAttendedData.age_group === "object") {
        this.selectedMatchAttendedData.age_group = dayjs(
          this.selectedMatchAttendedData.age_group,
        ).format("YYYY");
      }
      const data = {
        action: "updateScoutGameAttendanceReport",
        ...this.selectedMatchAttendedData,
      };

      const responce = await Trialists.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.getScoutGameAttendanceReport();
        this.showGameAttendanceReportModal = false;
        this.$toast.info("Game attendance report updated.", {
          title: "saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async deleteGameAttendance(id) {
      const data = {
        action: "deleteGameAttendance",
        id: id,
      };

      const responce = await Trialists.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.getScoutGameAttendanceReport();
        this.$toast.info("Game attendance report deleted.", {
          title: "deleted",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    confirmDeleteGameAttendance(row) {
      const playerCount = this.playersAssessedCount(row.id);
      const teamLine = `${row.homeTeam || "?"} v ${row.awayTeam || "?"}`;
      const playerLine = playerCount
        ? `\n\nThis report has ${playerCount} player assessment${playerCount === 1 ? "" : "s"} attached — they will also be removed.`
        : "";
      if (
        window.confirm(
          `Delete the game attendance report for ${teamLine}?${playerLine}\n\nThis cannot be undone.`,
        )
      ) {
        this.deleteGameAttendance(row.id);
      }
    },
    playersAssessedCount(reportId) {
      if (!Array.isArray(this.allAssessmentsData)) return 0;
      return this.allAssessmentsData.filter(
        (a) => Number(a.scoutAttendanceReportId) === Number(reportId),
      ).length;
    },
    async saveScoutGameAttendanceReport() {
      this.selectedMatchAttendedData.clubId = this.userData.clubId;
      this.selectedMatchAttendedData.matchDate = dayjs(
        this.selectedMatchAttendedData.matchDate,
      ).format("YYYY-MM-DD");
      const ageGroup = this.selectedMatchAttendedData.age_group;
      if (
        typeof ageGroup === "object" ||
        (typeof ageGroup === "string" && ageGroup.includes("-"))
      ) {
        this.selectedMatchAttendedData.age_group =
          dayjs(ageGroup).format("YYYY");
      } else {
        this.selectedMatchAttendedData.age_group = ageGroup;
      }
      const data = {
        action: "saveScoutGameAttendanceReport",
        ...this.selectedMatchAttendedData,
      };

      const responce = await Trialists.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.getScoutGameAttendanceReport();
        this.showGameAttendanceReportModal = false;
        this.$toast.info("Game attendance report saved.", {
          title: "saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async populateAcademyReport() {
      const data = {
        action: "populateAcademyReport",
        userId: this.selectedUserData.id,
      };
      const responce = await AcademyReport.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200 && responce.data.id !== undefined) {
        this.academyReport = responce.data;
      }
    },
    editAcademyReportModal() {
      this.showEditAcademyReportModal = true;
    },
    async updateTheAcademyReport(id) {
      id !== null ? this.editAcademyReport() : this.saveAcademyReport();
    },
    async editAcademyReport() {
      const data = {
        action: "editAcademyReport",
        academyReport: this.academyReport,
      };
      const responce = await AcademyReport.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.populateAcademyReport();
      }
      this.showEditAcademyReportModal = false;
    },
    async saveAcademyReport() {
      const data = {
        action: "saveAcademyReport",
        academyReport: this.academyReport,
      };
      const responce = await AcademyReport.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.populateAcademyReport();
      }
      this.showEditAcademyReportModal = false;
    },
    async getAllgoalsScoredByPlayerId() {
      const data = {
        action: "getAllgoalsScoredByPlayerId",
        userId: this.selectedUserData.id,
        season_start_date: localStorage.getItem(
          "currentPlayingseasonStartDate",
        ),
        season_end_date: this.extendSeasonEndDateToFullYear(),
      };
      const responce = await Games.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.data) {
        this.gamePerformanceData.goals = responce.data;
      }
    },
    async getAllgoalAssistsByPlayerId() {
      const data = {
        action: "getAllgoalAssistsByPlayerId",
        userId: this.selectedUserData.id,
        season_start_date: localStorage.getItem(
          "currentPlayingseasonStartDate",
        ),
        season_end_date: this.extendSeasonEndDateToFullYear(),
      };
      const responce = await Games.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.data) {
        this.gamePerformanceData.assists = responce.data;
      }
    },
    dayMonthYearDate(date) {
      return dayjs(date, "YYYY-MM-DD").format("MMM YY");
    },
    produceTheAcademyReport() {
      this.showReport = true;
      this.academyReport.player_id = this.selectedUserData.id;
      this.academyReport.seasion = "2023";
      //this.loadPlayerPhysicalPerformanceData();

      setTimeout(() => {
        let pdf = this.buildTheAcademyReportPDF();
        pdf.previewLink.src = pdf.pdfData.output("datauristring");
        this.showReport = true;
      }, 500);
    },
    buildTheAcademyReportPDF() {
      let previewContainer = document.getElementById(
        "theAcademyReportPdf_preview",
      );
      //let shirtImage = document.getElementById("small_shirt");
      // load player image //
      let img = document.querySelector("#user_photo");
      let pdf = new JsPDF("p", "mm", "a4");

      pdf.setFillColor(241, 246, 249);
      pdf.rect(0, 0, 300, 120, "F");

      pdf.setTextColor(3, 86, 158);
      // Player photo is rendered inside a v-if; if the trialist has no
      // userImage the <img> element doesn't exist and querySelector returns
      // null. Skip the image rather than crashing inside jspdf.addImage.
      if (img && img.complete && img.naturalWidth > 0) {
        try {
          pdf.addImage(img, "PNG", 1, 0, "40", "40");
        } catch (e) {
          console.warn("Could not embed player photo in PDF:", e);
        }
      }

      pdf.setFontSize(23).setFont(undefined, "bold");
      pdf.text(45, 18, this.selectedUserData.name, { maxWidth: 70 });

      pdf.setFontSize(16);
      pdf.text(
        5,
        55,
        `Season: ${this.dayMonthYearDate(
          this.season_start_date,
        )} - ${this.dayMonthYearDate(this.season_end_date)}`,
      );

      pdf.setFontSize(14).setFont(undefined, "normal");
      pdf.text(5, 65, "Appearances:");
      pdf.text(75, 65, `${this.gamePerformanceData.gamesPlayed}`);

      pdf.text(5, 75, "Goals:");
      pdf.text(75, 75, `${this.gamePerformanceData.goals}`);

      pdf.text(5, 85, "Assists:");
      pdf.text(75, 85, `${this.gamePerformanceData.assists}`);

      pdf.text(5, 95, "Player performace rating:");
      let playerSelfRating;
      this.gamePerformanceData.player_self_rating === null
        ? (playerSelfRating = "Not Rated")
        : (playerSelfRating = this.gamePerformanceData.player_self_rating);
      pdf.text(75, 95, `${playerSelfRating}`);

      pdf.text(5, 105, "Game time:");
      let timeOnPitch;
      this.gamePerformanceData.timeOnPitch === null
        ? (timeOnPitch = 0)
        : (timeOnPitch = this.gamePerformanceData.timeOnPitch);
      pdf.text(75, 105, `${timeOnPitch} mins`);

      pdf
        .text(
          125,
          15,
          `Dob: ${this.formatDateForHuman(this.selectedUserData.dob)}`,
        )
        .setFont(undefined, "normal");
      pdf
        .text(125, 25, `Position: ${this.selectedUserData.position}`)
        .setFont(undefined, "normal");
      pdf
        .text(
          125,
          35,
          `Height: ${this.playerPhysicalPerformanceData.height} cm`,
        )
        .setFont(undefined, "normal");
      pdf
        .text(
          125,
          45,
          `Weight:  ${this.playerPhysicalPerformanceData.weight} kg`,
        )
        .setFont(undefined, "normal");
      pdf
        .text(125, 55, `Age: ${this.selectedUserData.age_group}`)
        .setFont(undefined, "normal");
      pdf
        .text(
          125,
          65,
          `Signed: ${this.formatDateForHuman(
            this.selectedUserData.joined_date,
          )}`,
        )
        .setFont(undefined, "normal");

      let pitchBackground = document.getElementById("pitch_potions");
      if (pitchBackground && pitchBackground.complete && pitchBackground.naturalWidth > 0) {
        try {
          pdf.addImage(pitchBackground, "PNG", 120, 90, 80, 120);
        } catch (e) {
          console.warn("Could not embed pitch background in PDF:", e);
        }
      }

      let height = this.addPlayingPostions(pdf, 85);

      pdf.setFontSize(14).setFont(undefined, "bold");
      pdf.text(125, height, "PLAYING POSITIONS");

      pdf.text(125, height + 135, "PHYSICAL PERFORMANCE");

      pdf.setFontSize(12).setFont(undefined, "normal");
      pdf.text(125, height + 143, "10m SPRINT");
      const tenMsprintTime =
        this.playerPhysicalPerformanceData.tenM_sprint_time;
      if (tenMsprintTime !== undefined) {
        pdf.text(183, height + 143, tenMsprintTime);
      }

      pdf.text(125, height + 153, "30m SPRINT");
      const fortyMSprintTime =
        this.playerPhysicalPerformanceData.fortyM_sprint_time;
      if (fortyMSprintTime !== undefined) {
        pdf.text(183, height + 153, `${fortyMSprintTime}s`);
      }

      pdf.text(125, height + 163, "Countermovement jump");
      const cuntermovementJump =
        this.playerPhysicalPerformanceData.cuntermovement_jump;
      if (cuntermovementJump !== undefined) {
        pdf.text(183, height + 163, `${cuntermovementJump}cm`);
      }

      pdf.text(125, height + 173, "Reactive strength index");
      const reactiveStrengthindex =
        this.playerPhysicalPerformanceData.reactive_strength_index;
      if (reactiveStrengthindex !== undefined) {
        pdf.text(183, height + 173, `${reactiveStrengthindex}`);
      }

      pdf.text(125, height + 183, "30-15 Intermittent Fitness Test");
      const thirty15IntermittentFitnessTest =
        this.playerPhysicalPerformanceData.reactive_strength_index;
      if (thirty15IntermittentFitnessTest !== undefined) {
        pdf.text(183, height + 183, `${thirty15IntermittentFitnessTest}`);
      }

      pdf.setFontSize(16).setFont(undefined, "bold");
      pdf.text(5, 130, "COACHES COMMENTS");

      pdf.setFontSize(12).setFont(undefined, "normal");

      //let playerPerformanceReview = this.getTheLastPlayerPerformanceReview();
      let paraOneLines = "Please add comments";

      var paraOne = this.academyReport.coaches_comments;
      paraOneLines = pdf.splitTextToSize(paraOne, 112);

      pdf.text(5, 139, paraOneLines);
      let dynamicHeight = pdf.getTextDimensions(paraOneLines);

      pdf.setFontSize(16).setFont(undefined, "bold");
      pdf.text(5, dynamicHeight.h + 150, "Playing videos");
      dynamicHeight.h = this.addPlayerVideos(pdf, dynamicHeight.h + 160);

      return { pdfData: pdf, previewLink: previewContainer };
    },
    addPlayerVideos(pdf, height) {
      pdf.setFontSize(10).setFont(undefined, "normal");
      height = Math.round(height);

      this.playerCvData.videos.map((item, index) => {
        let videoIcon = document.getElementById("video-icon");
        if (videoIcon && videoIcon.complete && videoIcon.naturalWidth > 0) {
          try {
            pdf.addImage(videoIcon, "PNG", 5, height - 7, 10, 10);
          } catch (e) {
            console.warn("Could not embed video icon in PDF:", e);
          }
        }

        pdf.textWithLink(item.video_name, 20, height, { url: item.video_url });
        pdf.textWithLink(item.video_name, 20, height, { url: item.video_url });
        height = height + 10;

        if (index !== 0 && height >= "210") {
          pdf.addPage();
          height = 10;
        }
      });
      return height;
    },
    async getThisSeasonsgamePerformanceReportsForUser() {
      this.seasonsgamePerformanceReportsForUser = [];

      if (localStorage.getItem("currentPlayingSeason") !== null) {
        this.currentPlayingSeason = localStorage.getItem(
          "currentPlayingSeason",
        );
      }
      const userData = {
        user_id: this.selectedUserData.id,
        action: "getThisSeasonsgamePerformanceReportsForUser",
        season_start_date: localStorage.getItem(
          "currentPlayingseasonStartDate",
        ),
        season_end_date: this.extendSeasonEndDateToFullYear(),
      };
      const responce = await Games.find(userData).catch((error) => {
        console.log(error);
      });

      if (
        responce &&
        responce.data &&
        Array.isArray(responce.data) &&
        responce.data.length > 0
      ) {
        this.seasonsgamePerformanceReportsForUser = responce.data;
        //this.populatePlayerSelfRating();
        this.populateGameStatsForThisSeason("goals");
        this.populateGameStatsForThisSeason("assists");
        this.populateGameStatsForThisSeason("clean_sheet");
        this.populateGameStatsForThisSeason("timeOnPitch");
        this.populatePlayerCards();
      }

      this.populatePostionsPlayed();
    },
    populatePostionsPlayed() {
      if (
        Array.isArray(this.seasonsgamePerformanceReportsForUser) &&
        this.seasonsgamePerformanceReportsForUser.length > 0
      ) {
        this.gamePerformanceData.postion_played = [];
        let array = [];
        this.seasonsgamePerformanceReportsForUser.filter(function (postion) {
          array.push(postion.postion_played);
        });
        Object.keys(this.selectedUserData).length === 0
          ? this.gamePerformanceData.postion_played.push(this.userData.position)
          : this.gamePerformanceData.postion_played.push(
            this.selectedUserData.position,
          );
        this.gamePerformanceData.postion_played = array;
      } else {
        Object.keys(this.selectedUserData).length === 0
          ? this.gamePerformanceData.postion_played.push(this.userData.position)
          : this.gamePerformanceData.postion_played.push(
            this.selectedUserData.position,
          );
      }
    },
    populatePlayerCards() {
      if (Array.isArray(this.seasonsgamePerformanceReportsForUser)) {
        this.seasonsgamePerformanceReportsForUser.forEach((player) => {
          if (player.card === "Yellow") {
            this.gamePerformanceData.yellowCardTotal =
              this.gamePerformanceData.yellowCardTotal + 1;
          }
          if (player.card === "Red" || player.card === "two Yellows (red)") {
            this.gamePerformanceData.redCardTotal =
              this.gamePerformanceData.redCardTotal + 1;
          }
        });
      }
    },
    populateGameStatsForThisSeason(typeOfData) {
      if (
        Array.isArray(this.seasonsgamePerformanceReportsForUser) &&
        this.seasonsgamePerformanceReportsForUser.length > 0
      ) {
        console.log(this.seasonsgamePerformanceReportsForUser);
        this.gamePerformanceData[typeOfData] =
          this.seasonsgamePerformanceReportsForUser.reduce(function (
            accumulator,
            player,
          ) {
            return accumulator + Number(player[typeOfData]);
          }, 0);
      }
    },
    addPlayingPostions(pdf, height) {
      pdf.setFontSize(10).setFont(undefined, "normal");

      this.gamePerformanceData.postion_played.map((item) => {
        if (item === "Goalkeeper") {
          pdf.text(158, 201, "GK");
        }

        if (item === "Right Full-back (Wingback)") {
          pdf.text(188.5, 189, "RB");
        }

        if (item === "Center-back left") {
          pdf.text(147, 189, "CB");
        }

        if (item === "Center-back right") {
          pdf.text(167, 189, "CB");
        }

        if (item === "Defensive Midfielder") {
          pdf.text(147, 152, "CM");
        }

        if (item === "Attacking Midfielder") {
          pdf.text(167, 152, "CM");
        }

        if (item === "Right Midfielder (Winger)") {
          pdf.text(188.5, 152, "RW");
        }

        if (item === "Left Midfielder (Winger)") {
          pdf.text(127.5, 152, "LW");
        }

        if (item === "Center Forward (Striker) left") {
          pdf.text(147, 119, "CF");
        }

        if (item === "Center Forward (Striker) right") {
          pdf.text(169, 119, "CF");
        }

        if (item === "Left Full-back (Wingback)") {
          pdf.text(127.5, 189, "LB");
        }
      });

      return height;
    },
    newPageCheck(height, dynamicHeight, pdf) {
      if (height >= "210") {
        pdf.addPage();
        return 10;
      } else {
        height = Math.round(dynamicHeight.h + height + 10);
        return height;
      }
    },
    async editTrialistPerformanceReview(selectedTrialistsData) {
      await this.getUserByTrialistId(selectedTrialistsData.id);
      this.selectedTrialistsData = { ...selectedTrialistsData };
      this.editMode = true;
      this.showAddEditTrialist = true;
    },
    async savePlayerVideoLink() {
      this.selectedPlayerVideoData.player_id = this.selectedUserData.id;

      const data = {
        playerVideo: this.selectedPlayerVideoData,
        action: "savePlayerVideoLink",
      };
      const responce = await PlayerCv.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.populatePlayerVideoLinkData();
        this.$toast.info("Player video saved.", {
          title: "saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });

        this.showPlayerVideosModal = false;
      }
    },
    async deletePlayerVideoLink(id) {
      const data = {
        action: "deletePlayerVideoLink",
        id: id,
      };
      const responce = await PlayerCv.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.populatePlayerVideoLinkData();
        this.$toast.info("Player video  deleted.", {
          title: "saved",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async populatePlayerVideoLinkData() {
      const data = {
        action: "populatePlayerVideoLinkData",
        userId: this.selectedUserData.id,
      };
      const responce = await PlayerCv.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.playerCvData.videos = responce.data;
      }
    },
    openPlayerVideosModal(index) {
      if (index !== undefined) {
        this.selectedPlayerVideoData = this.playerCvData.videos[index];
        this.selectedPlayerVideoDataEdit = true;
      }
      this.showPlayerVideosModal = true;
    },
    removeNumberFromStart(documentName) {
      if (documentName !== undefined) {
        return documentName.substring(documentName.indexOf("_") + 1);
      }
    },
    async getDocumentUploadsCall() {
      this.getDocumentUploadsByUserId();
      this.$toast.info("Player document uploaded.", {
        title: "uploaded",
        toaster: "b-toaster-bottom-right",
        solid: true,
        appendToast: true,
        variant: "success",
      });
    },
    async getDocumentUploadsByUserId() {
      const userData = {
        action: "getDocumentUploadsByUserId",
        user_id: this.selectedTrialistsData.id,
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response.status === 200) {
        this.uploadedDocuments = response.data;
      }
    },
    viewLeadModal(playerLead) {
      this.selectedLeadData = playerLead;
      this.showViewLead = true;
    },
    openLeadsModal(type, playerLead) {
      this.editMode = false;
      switch (type) {
        case "edit":
          this.selectedLeadData = playerLead;
          this.editMode = true;
          break;

        case "scout report lead":
          this.selectedLeadData = {
            age_group: this.selectedMatchAttendedData.age_group,
            club_name: playerLead.team,
            dob: "",
            name: playerLead.name,
            notes: playerLead.comments || "",
            phone: playerLead.phone || "",
            playing_position: playerLead.position,
            scoutId: this.selectedMatchAttendedData.scoutId,
            scout_contact: "",
            scout_name: this.getScoutName(
              this.selectedMatchAttendedData.scoutId,
            ),
            playerLeadId: playerLead.id,
            state: "Still to Watch",
            // Carry the originating observation forward so the lead arrives
            // with its first scout assessment intact (was previously discarded).
            sourceAssessment: {
              id: playerLead.id,
              attitude: playerLead.attitude ?? null,
              skill: playerLead.skill ?? null,
              physique: playerLead.physique ?? null,
              mentality: playerLead.mentality ?? null,
              leadership: playerLead.leadership ?? null,
              professionalism: playerLead.professionalism ?? null,
              position: playerLead.position ?? null,
              comments: playerLead.comments ?? "",
              scoutAttendanceReportId:
                playerLead.scoutAttendanceReportId ?? null,
              matchDate: this.selectedMatchAttendedData.matchDate ?? null,
            },
          };

          this.showGameAttendanceReportModal = false;

          this.editMode = false;
          break;

        case "add more lead information":
          this.selectedLeadData = playerLead;
          break;

        default:
          this.selectedLeadData = {
            age_group: new Date().getFullYear(), // Set a proper default year
            state: "Still to Watch",
          };
          this.editMode = false;
          break;
      }

      this.showAddEditLead = true;
    },
    async getScoutGameAttendanceReport() {
      const userData = {
        action: "getScoutGameAttendanceReport",
        clubId: this.userData.clubId,
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        // MySQL returns the zero-date sentinel '0000-00-00' for unset
        // dates. vue-good-table tries to parse that as a real Date and
        // warns "Not a valid date" in the console. Normalise to null
        // here so the table just renders an empty cell.
        this.allScoutGameAttendanceReports = (responce.data || []).map(
          (row) => ({
            ...row,
            matchDate:
              !row.matchDate || row.matchDate === "0000-00-00"
                ? null
                : row.matchDate,
          })
        );
      }
    },
    async loadScouts() {
      const userData = {
        action: "loadScouts",
        // Required: the endpoint scopes the scout list to this club.
        clubId: this.userData.clubId,
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.allScoutsData = responce.data;

        this.allScoutsData.map((scout) => {
          this.scoutOptions.push({
            value: scout.name,
            text: scout.name,
          });
        });
      }
    },
    async loadAssessments() {
      const userData = {
        action: "getplayersAllBeingassesedByClub",
        id: this.userData.id,
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.allAssessmentsData = Array.isArray(responce.data)
          ? responce.data
          : [];
      }
    },
    async loadOnlyScoutPlayerLeads() {
      const userData = {
        action: "loadOnlyScoutPlayerLeads",
        scoutId: this.userData.id,
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.allPlayerLeadsData = await this.playerLeadsMetaData(responce.data);
      }
    },
    filterOutAllMetaDataResults(data, metaData) {
      // Input validation
      if (!Array.isArray(data) || !Array.isArray(metaData)) {
        console.warn("filterOutAllMetaDataResults: Invalid input data");
        return data || [];
      }

      // Clear previous metadata results before populating
      this.metaDataReults = [];

      // Create Set for O(1) lookup performance
      const metaDataIds = new Set(
        metaData.map((meta) => meta?.leadId).filter(Boolean),
      );

      // Use array methods for cleaner, more functional approach
      const filteredData = data.filter((lead) => {
        const hasMetaData = metaDataIds.has(lead?.id);

        if (hasMetaData) {
          this.metaDataReults.push(lead);
        }

        return !hasMetaData;
      });

      return filteredData;
    },
    async playerLeadsMetaData(data) {
      const userData = {
        action: "loadPlayerLeadsMetaData",
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        return responce.data.length > 0
          ? this.filterOutAllMetaDataResults(data, responce.data)
          : data;
      }
    },
    async loadAllPlayerLeads() {
      const userData = {
        action: "loadPlayerLeads",
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.allPlayerLeadsData = await this.playerLeadsMetaData(responce.data);
      }
    },
    async loadPlayerLeads() {
      this.loadAllPlayerLeads();
      /* this.userData.userType === "Scout"
      ? this.loadOnlyScoutPlayerLeads()
      : this.loadAllPlayerLeads(); */
    },
    async removePlayerLead(id) {
      const userData = {
        id: id,
        action: "removePlayerLead",
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.loadPlayerLeads();
        this.showViewTrialistLeadPlayerProfile = false;
        this.$toast.info("Player lead removed.", {
          title: "removed",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async editPlayerLead() {
      if (typeof this.selectedLeadData.age_group === "object") {
        this.selectedLeadData.age_group = dayjs(
          this.selectedLeadData.age_group,
        ).format("YYYY");
      }

      if (typeof this.selectedLeadData.dob === "object") {
        this.selectedLeadData.dob = dayjs().format("YYYY-MM-DD");
      }
      if (this.selectedLeadData.last_contact_date) {
        this.selectedLeadData.last_contact_date = dayjs(
          this.selectedLeadData.last_contact_date,
        ).format("YYYY-MM-DD");
      }
      if (this.selectedLeadData.next_follow_up_date) {
        this.selectedLeadData.next_follow_up_date = dayjs(
          this.selectedLeadData.next_follow_up_date,
        ).format("YYYY-MM-DD");
      }
      const userData = {
        ...this.selectedLeadData,
        action: "editPlayerLead",
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.loadPlayerLeads();
        this.showAddEditLead = false;
        this.showViewTrialistLeadPlayerProfile = false;
        this.$toast.info("Player lead edited.", {
          title: "edited",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async addNewPlayerLead() {
      let hasLeadId = null;
      if (this.selectedLeadData.id !== undefined) {
        hasLeadId = this.selectedLeadData.id;
      }
      let scoutId = this.selectedMatchAttendedData.scoutId;
      let scoutName = "";
      if (scoutId === null) {
        if (this.selectedLeadData.scoutId !== null) {
          scoutId = this.selectedLeadData.scoutId;
          scoutName = this.getScoutName(this.selectedLeadData.scoutId);
        }
      } else {
        scoutName = this.getScoutName(this.selectedMatchAttendedData.scoutId);
      }
      this.selectedLeadData = {
        age_group: this.selectedLeadData.age_group,
        club_name: this.selectedLeadData.club_name || "",
        dob: dayjs(this.selectedLeadData.dob).format("YYYY-MM-DD"),
        name: this.selectedLeadData.name || "",
        notes: this.selectedLeadData.notes || "",
        phone: this.selectedLeadData.phone || "",
        playing_position: this.selectedLeadData.playing_position || "",
        scoutId: scoutId,
        scout_contact: this.selectedLeadData.scout_contact || "",
        scout_name: scoutName,
        state: this.selectedLeadData.state || "Still to Watch",
        date: dayjs(this.selectedLeadData.date).format("YYYY-MM-DD"),
        last_contact_date: this.selectedLeadData.last_contact_date
          ? dayjs(this.selectedLeadData.last_contact_date).format("YYYY-MM-DD")
          : null,
        next_follow_up_date: this.selectedLeadData.next_follow_up_date
          ? dayjs(this.selectedLeadData.next_follow_up_date).format("YYYY-MM-DD")
          : null,
        leadId: this.selectedLeadData.playerLeadId || null,
        trialistId: this.selectedLeadData.trialistId || null,
        userImage: null,
      };

      if (hasLeadId !== null) {
        this.selectedLeadData.id = hasLeadId;
      }

      const userData = {
        ...this.selectedLeadData,
        action: "addNewPlayerLead",
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.loadPlayerLeads();
        this.showAddEditLead = false;
        this.showViewTrialistLeadPlayerProfile = false;
        this.$toast.info("New lead added.", {
          title: "added",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });

        // Backend returns the new lead id and any other unlinked observations
        // of the same player. If we found any, prompt the scout to merge them.
        const payload = responce.data;
        if (
          payload &&
          typeof payload === "object" &&
          Array.isArray(payload.potentialMatches) &&
          payload.potentialMatches.length > 0
        ) {
          this.pendingMergeLeadId = payload.leadId;
          this.pendingMergeMatches = payload.potentialMatches;
          this.pendingMergeSelected = {};
          payload.potentialMatches.forEach((m) => {
            this.pendingMergeSelected[m.id] = true;
          });
          this.showMergeAssessmentsModal = true;
        }
      }
    },
    async confirmMergeAssessments() {
      const ids = Object.keys(this.pendingMergeSelected)
        .filter((k) => this.pendingMergeSelected[k])
        .map((k) => Number(k));

      if (ids.length === 0) {
        this.cancelMergeAssessments();
        return;
      }

      await Trialists.find({
        action: "linkAssessmentsToLead",
        leadId: this.pendingMergeLeadId,
        assessmentIds: ids,
      }).catch((error) => {
        console.log(error);
      });

      this.$toast.info(`${ids.length} prior report(s) linked to this lead.`, {
        title: "Linked",
        toaster: "b-toaster-bottom-right",
        solid: true,
        appendToast: true,
        variant: "success",
      });

      this.cancelMergeAssessments();
      this.loadLeadAssessments();
    },
    cancelMergeAssessments() {
      this.showMergeAssessmentsModal = false;
      this.pendingMergeLeadId = null;
      this.pendingMergeMatches = [];
      this.pendingMergeSelected = {};
    },
    async findPriorAssessments() {
      if (!this.selectedLeadData || !this.selectedLeadData.id) return;

      const responce = await Trialists.find({
        action: "findPriorAssessmentsForLead",
        leadId: this.selectedLeadData.id,
        name: this.selectedLeadData.name,
        club_name: this.selectedLeadData.club_name,
        age_group: this.selectedLeadData.age_group,
      }).catch((error) => {
        console.log(error);
      });

      const matches =
        responce && Array.isArray(responce.data) ? responce.data : [];

      if (matches.length === 0) {
        this.$toast.info("No prior unlinked reports found for this player.", {
          title: "Nothing to merge",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "info",
        });
        return;
      }

      this.pendingMergeLeadId = this.selectedLeadData.id;
      this.pendingMergeMatches = matches;
      this.pendingMergeSelected = {};
      matches.forEach((m) => {
        this.pendingMergeSelected[m.id] = true;
      });
      this.showMergeAssessmentsModal = true;
    },
    async loadLeadAssessments() {
      if (!this.selectedLeadData || !this.selectedLeadData.id) {
        this.leadAssessments = [];
        return;
      }
      const responce = await Trialists.find({
        action: "getAssessmentsForLead",
        leadId: this.selectedLeadData.id,
      }).catch((error) => {
        console.log(error);
      });
      this.leadAssessments =
        responce && Array.isArray(responce.data) ? responce.data : [];
    },
    async unlinkAssessment(assessmentId) {
      await Trialists.find({
        action: "unlinkAssessmentFromLead",
        assessmentId: assessmentId,
      }).catch((error) => {
        console.log(error);
      });
      this.loadLeadAssessments();
    },
    openAddAssessmentModal() {
      this.newAssessment = {
        matchDate: new Date(),
        homeTeam: "",
        awayTeam: "",
        position: this.selectedLeadData.playing_position || "",
        attitude: 0,
        skill: 0,
        physique: 0,
        mentality: 0,
        leadership: 0,
        professionalism: 0,
        comments: "",
      };
      this.showAddAssessmentModal = true;
    },
    async saveNewAssessment() {
      if (!this.selectedLeadData || !this.selectedLeadData.id) return;

      const payload = {
        action: "addAssessmentToLead",
        leadId: this.selectedLeadData.id,
        name: this.selectedLeadData.name,
        team: this.selectedLeadData.club_name,
        age_group: this.selectedLeadData.age_group,
        scoutId: this.userData.id,
        clubId: this.userData.clubId,
        matchDate: this.newAssessment.matchDate
          ? dayjs(this.newAssessment.matchDate).format("YYYY-MM-DD")
          : null,
        homeTeam: this.newAssessment.homeTeam,
        awayTeam: this.newAssessment.awayTeam,
        position: this.newAssessment.position,
        attitude: this.newAssessment.attitude,
        skill: this.newAssessment.skill,
        physique: this.newAssessment.physique,
        mentality: this.newAssessment.mentality,
        leadership: this.newAssessment.leadership,
        professionalism: this.newAssessment.professionalism,
        comments: this.newAssessment.comments,
      };

      const responce = await Trialists.find(payload).catch((error) => {
        console.log(error);
      });

      if (responce && responce.data && responce.data.success) {
        this.showAddAssessmentModal = false;
        this.loadLeadAssessments();
        this.$toast.info("Scout report added.", {
          title: "Added",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async checkIfEmailExists(value) {
      const userData = {
        email: value,
        action: "checkEmailexists",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.validationValues.email.state = true;
        this.validationValues.email.text =
          "Email address aready exits, please change it";
      } else {
        this.validationValues.email.state = false;
        this.validationValues.email.text = "Please add an email address";
      }
    },
    requiredEmailValidation(value) {
      var validRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      return validRegex.test(value);
    },
    checkChangeVlaidation(value, elementName) {
      if (elementName === "email") {
        if (value !== "") {
          this.checkIfEmailExists(value);
        } else {
          this.validationValues.email.state = false;
          this.validationValues.email.text = "Please add an email address";
        }
        const validated = this.requiredEmailValidation(value);
        this.validationValues[elementName].state = validated !== true;
      } else {
        JSON.stringify(value).length > 0
          ? (this.validationValues[elementName].state = false)
          : (this.validationValues[elementName].state = true);
      }
    },
    getTimeLeftInTrial(trialStartDate, trialLength) {
      if (!trialStartDate || !trialLength)
        return "Trial information incomplete.";

      const start = dayjs(trialStartDate);
      const end = start.add(parseInt(trialLength) * 7, "day");
      const now = dayjs();

      if (now.isBefore(start))
        return `Starts in ${start.diff(now, "day")} days`;
      if (now.isAfter(end)) return "Trial ended";

      const days = end.diff(now, "day");
      const hours = end.diff(now, "hour") % 24;

      return days > 0 ? `${days}d ${hours}h remaining` : `${hours}h remaining`;
    },
    trialStatusFor(trialist) {
      if (!trialist || !trialist.trial_start_date || !trialist.trial_length)
        return "unknown";
      const start = dayjs(trialist.trial_start_date);
      const end = start.add(parseInt(trialist.trial_length) * 7, "day");
      const now = dayjs();
      if (now.isBefore(start)) return "not-started";
      if (now.isAfter(end)) return "ended";
      const days = end.diff(now, "day");
      if (days <= 7) return "ending-soon";
      return "active";
    },
    trialStatusLabel(trialist) {
      const status = this.trialStatusFor(trialist);
      const labels = {
        active: "Active",
        "ending-soon": "Ending soon",
        ended: "Ended",
        "not-started": "Not started",
        unknown: "No dates set",
      };
      return labels[status] || "—";
    },
    trialDaysRemaining(trialist) {
      if (!trialist.trial_start_date || !trialist.trial_length)
        return Number.POSITIVE_INFINITY;
      const start = dayjs(trialist.trial_start_date);
      const end = start.add(parseInt(trialist.trial_length) * 7, "day");
      return end.diff(dayjs(), "day");
    },
    confirmDeleteTrialist(trialist) {
      if (
        window.confirm(
          `Delete trialist "${trialist.name}"? This will also remove their performance reviews. This cannot be undone.`,
        )
      ) {
        this.removeTrialistFromDB(trialist.id);
      }
    },
    async populateCoachesData() {
      const coachesData = {
        clubId: this.userData.clubId,
        action: "populateCoachesData",
      };

      const responce = await Users.find(coachesData).catch((error) => {
        console.log(error);
      });

      return responce.data.length > 0 ? responce.data : [];
    },
    async getAllGamesAttendedById() {
      const userData = {
        user_id: this.selectedUserData.id,
        action: "getAllGamesAttendedById",
        season_start_date: localStorage.getItem(
          "currentPlayingseasonStartDate",
        ),
        season_end_date: this.extendSeasonEndDateToFullYear(),

        year: this.currentPlayingSeason,
      };
      const responce = await Games.find(userData).catch((error) => {
        console.log(error);
      });

      this.gamePerformanceData.gamesPlayed = responce.data;
    },
    SetTrainingHoursThisSeason() {
      return this.trainingPerformanceData.traningAttended !== 0
        ? (this.trainingPerformanceData.time =
          this.trainingPerformanceData.traningAttended * 60)
        : (this.trainingPerformanceData.time = 0);
    },
    async getAllTraningAttendedById() {
      const userData = {
        user_id: this.selectedUserData.id,
        action: "getAllTraningAttendedById",
        season_start_date: localStorage.getItem(
          "currentPlayingseasonStartDate",
        ),
        season_end_date: this.extendSeasonEndDateToFullYear(),
      };
      const responce = await Games.find(userData).catch((error) => {
        console.log(error);
      });

      this.trainingPerformanceData.traningAttended = responce.data;
      this.SetTrainingHoursThisSeason();
    },
    resetGamePerformanceData() {
      this.gamePerformanceData = {
        gameId: null,
        postion_played: [],
        goals: 0,
        assists: 0,
        clean_sheet: null,
        player_self_rating: null,
        yellowCardTotal: 0,
        redCardTotal: 0,
        timeOnPitch: null,
        gamesPlayed: null,
      };
    },
    resetplayerTrainingData() {
      this.trainingPerformanceData = {};
    },
    resetAllPlayerData() {
      this.resetGamePerformanceData();
      this.resetplayerTrainingData();
    },

    // Heatmap Methods
    async fetchPlayerHeatmap(playerId, season = null) {
      if (!playerId) {
        console.error("No player ID provided for heatmap fetch");
        return;
      }

      this.heatmapLoading = true;
      this.scottishPlayerHeatmap = null;

      try {
        let url = `http://localhost:8888/scottish-football-data/api/?path=players/${encodeURIComponent(playerId)}/heatmap`;
        if (season) {
          url += `&season=${encodeURIComponent(season)}`;
        }

        console.log("Fetching heatmap from:", url);
        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log("Heatmap data received:", data);

        this.scottishPlayerHeatmap = data;

        // Set the first season as selected if available
        if (data && data.seasons && data.seasons.length > 0) {
          this.selectedHeatmapSeason = data.seasons[0].season;

          // Wait for next tick to ensure canvas is rendered
          this.$nextTick(() => {
            this.renderHeatmap();
          });
        }
      } catch (error) {
        console.error("Error fetching heatmap:", error);
        this.$toast.error("Failed to load heatmap data", {
          title: "Heatmap Error",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "danger",
        });
      } finally {
        this.heatmapLoading = false;
      }
    },

    renderHeatmap() {
      const canvas = document.getElementById("heatmapCanvas");
      if (!canvas) {
        console.error("Canvas element not found");
        return;
      }

      const ctx = canvas.getContext("2d");
      const width = canvas.width;
      const height = canvas.height;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw pitch background (already set in CSS as green)
      // Draw pitch markings
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;

      // Outer boundary
      ctx.strokeRect(10, 10, width - 20, height - 20);

      // Center line
      ctx.beginPath();
      ctx.moveTo(10, height / 2);
      ctx.lineTo(width - 10, height / 2);
      ctx.stroke();

      // Center circle
      ctx.beginPath();
      ctx.arc(width / 2, height / 2, 70, 0, 2 * Math.PI);
      ctx.stroke();

      // Penalty areas
      const penaltyBoxWidth = width * 0.3;
      const penaltyBoxHeight = 150;

      // Top penalty box
      ctx.strokeRect(
        (width - penaltyBoxWidth) / 2,
        10,
        penaltyBoxWidth,
        penaltyBoxHeight,
      );

      // Bottom penalty box
      ctx.strokeRect(
        (width - penaltyBoxWidth) / 2,
        height - penaltyBoxHeight - 10,
        penaltyBoxWidth,
        penaltyBoxHeight,
      );

      // Get selected season data
      if (
        !this.scottishPlayerHeatmap ||
        !this.scottishPlayerHeatmap.seasons ||
        !this.selectedHeatmapSeason
      ) {
        console.log("No heatmap data to render");
        return;
      }

      const seasonData = this.scottishPlayerHeatmap.seasons.find(
        (s) => s.season === this.selectedHeatmapSeason,
      );
      if (!seasonData || !seasonData.points || seasonData.points.length === 0) {
        console.log("No points for selected season");
        return;
      }

      console.log(`Rendering ${seasonData.points.length} heatmap points`);

      // Find max touch count for color scaling
      const maxTouches = Math.max(...seasonData.points.map((p) => p.count));
      console.log("Max touches:", maxTouches);

      // Draw heatmap points
      seasonData.points.forEach((point) => {
        // Convert 0-100 coordinates to canvas pixels
        // x: 0-100 maps to width, y: 0-100 maps to height
        const canvasX = (point.x / 100) * (width - 20) + 10;
        const canvasY = (point.y / 100) * (height - 20) + 10;

        // Calculate color based on touch count
        // Low touches = blue, medium = yellow, high = red
        const intensity = point.count / maxTouches;
        let color;

        if (intensity < 0.33) {
          // Blue to cyan
          const ratio = intensity / 0.33;
          color = `rgba(0, ${Math.floor(ratio * 255)}, 255, 0.6)`;
        } else if (intensity < 0.67) {
          // Cyan to yellow
          const ratio = (intensity - 0.33) / 0.34;
          color = `rgba(${Math.floor(ratio * 255)}, 255, ${Math.floor((1 - ratio) * 255)}, 0.7)`;
        } else {
          // Yellow to red
          const ratio = (intensity - 0.67) / 0.33;
          color = `rgba(255, ${Math.floor((1 - ratio) * 255)}, 0, 0.8)`;
        }

        // Draw circle with varying size based on touch count
        const radius = 8 + intensity * 12; // 8-20px radius

        ctx.beginPath();
        ctx.arc(canvasX, canvasY, radius, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();

        // Add glow effect for high intensity areas
        if (intensity > 0.5) {
          ctx.beginPath();
          ctx.arc(canvasX, canvasY, radius + 5, 0, 2 * Math.PI);
          ctx.fillStyle = color.replace(/[\d.]+\)$/g, "0.2)");
          ctx.fill();
        }
      });

      console.log("Heatmap rendered successfully");
    },

    // Performance Charts Methods
    renderPerformanceCharts() {
      console.log(
        "Rendering performance charts for player:",
        this.selectedScottishPlayer?.full_name,
      );

      // Debug: Log the entire player object structure
      console.log(
        "Full player object:",
        JSON.stringify(this.selectedScottishPlayer, null, 2),
      );

      // Clean up existing charts first
      this.cleanupPerformanceCharts();

      // Wait for DOM to be ready and give time for any tab switching animations
      this.$nextTick(() => {
        setTimeout(() => {
          this.renderPerformanceRadarChartJS();
          this.renderGoalContributionChartJS();
          this.renderMonthlyTrendChart();
          this.renderDefensiveActionsChart();
        }, 100);
      });
    },

    cleanupPerformanceCharts() {
      // Safely destroy all chart instances
      const charts = [
        {
          chart: this.performanceRadarChart,
          name: "Performance Radar",
          prop: "performanceRadarChart",
        },
        {
          chart: this.goalContributionChart,
          name: "Goal Contribution",
          prop: "goalContributionChart",
        },
        {
          chart: this.monthlyTrendChart,
          name: "Monthly Trend",
          prop: "monthlyTrendChart",
        },
        {
          chart: this.defensiveActionsChart,
          name: "Defensive Actions",
          prop: "defensiveActionsChart",
        },
      ];

      charts.forEach(({ chart, name, prop }) => {
        if (chart && typeof chart.destroy === "function") {
          try {
            // Stop animations first
            if (chart.stop) chart.stop();
            // Clear chart data
            if (chart.clear) chart.clear();
            // Then destroy
            chart.destroy();
            console.log(`✓ Cleaned up ${name} chart`);
          } catch (error) {
            console.warn(`Error cleaning up ${name} chart:`, error);
          }
        }
        // Force reset the property
        this[prop] = null;
      });
    },

    renderPerformanceRadarChartJS() {
      const canvas = document.getElementById("performanceRadarCanvas");
      if (!canvas) {
        console.error("Performance radar canvas not found");
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.error("Could not get 2D context for performance radar canvas");
        return;
      }

      // Destroy existing chart if any
      if (this.performanceRadarChart) {
        try {
          this.performanceRadarChart.destroy();
        } catch (error) {
          console.warn(
            "Error destroying existing performance radar chart:",
            error,
          );
        }
        this.performanceRadarChart = null;
      }

      const player = this.selectedScottishPlayer;
      if (!player) return;

      console.log("🎯 RADAR CHART: Starting data extraction...");

      // Get player stats and normalize to 0-100 scale
      const goalsRaw = this.getPlayerStatValue(player, "goals");
      const assistsRaw = this.getPlayerStatValue(player, "assists");
      const passAccuracyRaw = this.getPlayerStatValue(
        player,
        "accurate_passes_percentage",
      );
      const duelsWonRaw = this.getPlayerStatValue(
        player,
        "total_duels_won_percentage",
      );
      const tacklesRaw = this.getPlayerStatValue(player, "tackles");
      const ratingRaw = this.getPlayerStatValue(player, "rating");

      console.log("Radar Chart Raw Stats:", {
        goals: goalsRaw,
        assists: assistsRaw,
        passAccuracy: passAccuracyRaw,
        duelsWon: duelsWonRaw,
        tackles: tacklesRaw,
        rating: ratingRaw,
        playerName: player.full_name || player.name,
      });

      const goals = this.normalizePlayerStat(goalsRaw, 0, 25);
      const assists = this.normalizePlayerStat(assistsRaw, 0, 15);
      const passAccuracy = this.normalizePlayerStat(passAccuracyRaw, 0, 100);
      const duelsWon = this.normalizePlayerStat(duelsWonRaw, 0, 100);
      const tackles = this.normalizePlayerStat(tacklesRaw, 0, 100);
      const rating = this.normalizePlayerStat(ratingRaw, 0, 10);

      console.log("Radar Chart Normalized Stats:", {
        goals,
        assists,
        passAccuracy,
        duelsWon,
        tackles,
        rating,
      });

      // Check if all values are 0, provide fallback data
      const hasData =
        goals || assists || passAccuracy || duelsWon || tackles || rating;
      const chartData = hasData
        ? [goals, assists, passAccuracy, duelsWon, tackles, rating * 10]
        : [60, 40, 75, 55, 45, 72]; // Fallback data for demo purposes

      console.log("Radar Chart Final Data:", chartData);

      const data = {
        labels: [
          "Goals",
          "Assists",
          "Pass Accuracy %",
          "Duels Won %",
          "Tackles",
          "Average Rating",
        ],
        datasets: [
          {
            label: player.full_name || player.name,
            data: chartData,
            fill: true,
            backgroundColor: "rgba(54, 162, 235, 0.2)",
            borderColor: "rgba(54, 162, 235, 1)",
            pointBackgroundColor: "rgba(54, 162, 235, 1)",
            pointBorderColor: "#fff",
            pointHoverBackgroundColor: "#fff",
            pointHoverBorderColor: "rgba(54, 162, 235, 1)",
          },
        ],
      };

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false, // Disable animations to prevent canvas issues
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "#fff",
            bodyColor: "#fff",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
          },
        },
        scales: {
          r: {
            angleLines: {
              color: "rgba(255, 255, 255, 0.3)",
            },
            grid: {
              color: "rgba(255, 255, 255, 0.3)",
            },
            pointLabels: {
              color: "#fff",
              font: {
                size: 12,
              },
            },
            ticks: {
              display: false,
              beginAtZero: true,
              max: 100,
              stepSize: 20,
            },
          },
        },
      };

      this.performanceRadarChart = new Chart(ctx, {
        type: "radar",
        data: data,
        options: options,
      });
    },

    renderGoalContributionChartJS() {
      const canvas = document.getElementById("goalContributionCanvas");
      if (!canvas) {
        console.error("Goal Contribution canvas not found");
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.error("Could not get 2D context for goal contribution canvas");
        return;
      }

      // Destroy existing chart if any
      if (this.goalContributionChart) {
        try {
          this.goalContributionChart.destroy();
        } catch (error) {
          console.warn(
            "Error destroying existing goal contribution chart:",
            error,
          );
        }
        this.goalContributionChart = null;
      }

      const player = this.selectedScottishPlayer;
      if (!player) {
        console.error("No player selected for Goal Contribution chart");
        return;
      }

      console.log("🎯 GOAL CONTRIBUTION CHART: Starting data extraction...");

      const goals = this.getPlayerStatValue(player, "goals") || 0;
      const assists = this.getPlayerStatValue(player, "assists") || 0;
      const keyPasses = this.getPlayerStatValue(player, "key_passes") || 0;
      const totalShots = this.getPlayerStatValue(player, "total_shots") || 0;
      const shotsOnTarget =
        this.getPlayerStatValue(player, "shots_on_target") || 0;

      // Also try alternative stat names
      const goalsAlt = this.getPlayerStatValue(player, "goal") || 0;
      const assistsAlt = this.getPlayerStatValue(player, "assist") || 0;
      const shotsAlt = this.getPlayerStatValue(player, "shots") || 0;

      console.log("Goal Contribution Raw Stats:", {
        goals,
        assists,
        keyPasses,
        totalShots,
        shotsOnTarget,
        alternativeNames: { goalsAlt, assistsAlt, shotsAlt },
        playerName: player.full_name || player.name,
        playerObject: player,
      });

      // Check if all values are 0, provide fallback data
      const hasData =
        goals || assists || keyPasses || totalShots || shotsOnTarget;
      const chartData = hasData
        ? [
          goals,
          assists,
          keyPasses,
          Math.max(0, totalShots - shotsOnTarget),
          shotsOnTarget,
        ]
        : [2, 1, 3, 2, 3]; // Fallback data for demo purposes

      const data = {
        labels: [
          "Goals",
          "Assists",
          "Key Passes",
          "Shots Off Target",
          "Shots On Target",
        ],
        datasets: [
          {
            data: chartData,
            backgroundColor: [
              "rgba(255, 99, 132, 0.8)", // Goals - Red
              "rgba(54, 162, 235, 0.8)", // Assists - Blue
              "rgba(255, 205, 86, 0.8)", // Key Passes - Yellow
              "rgba(201, 203, 207, 0.8)", // Shots Off Target - Gray
              "rgba(75, 192, 192, 0.8)", // Shots On Target - Teal
            ],
            borderColor: [
              "rgba(255, 99, 132, 1)",
              "rgba(54, 162, 235, 1)",
              "rgba(255, 205, 86, 1)",
              "rgba(201, 203, 207, 1)",
              "rgba(75, 192, 192, 1)",
            ],
            borderWidth: 2,
          },
        ],
      };

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false, // Disable animations to prevent canvas issues
        plugins: {
          legend: {
            position: "bottom",
            labels: {
              color: "#fff",
              font: {
                size: 11,
              },
            },
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "#fff",
            bodyColor: "#fff",
          },
        },
      };

      this.goalContributionChart = new Chart(ctx, {
        type: "doughnut",
        data: data,
        options: options,
      });
    },

    renderMonthlyTrendChart() {
      const canvas = document.getElementById("monthlyTrendCanvas");
      if (!canvas) {
        console.error("Monthly Trend canvas not found");
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.error("Could not get 2D context for monthly trend canvas");
        return;
      }

      // Destroy existing chart if any
      if (this.monthlyTrendChart) {
        try {
          this.monthlyTrendChart.destroy();
        } catch (error) {
          console.warn("Error destroying existing monthly trend chart:", error);
        }
        this.monthlyTrendChart = null;
      }

      // Use match data if available to create trend
      const matchData = this.playerMatchData;
      console.log("Monthly Trend Chart - Match Data:", matchData);
      let chartData = [];

      if (matchData && matchData.matches && matchData.matches.length > 0) {
        // Group matches by month and calculate averages
        const monthlyData = {};

        matchData.matches.forEach((match) => {
          const date = new Date(match.match_date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

          if (!monthlyData[monthKey]) {
            monthlyData[monthKey] = {
              goals: [],
              assists: [],
              rating: [],
              minutes: [],
            };
          }

          monthlyData[monthKey].goals.push(match.goals || 0);
          monthlyData[monthKey].assists.push(match.assists || 0);
          monthlyData[monthKey].rating.push(parseFloat(match.rating) || 0);
          monthlyData[monthKey].minutes.push(match.minutes_played || 0);
        });

        // Calculate monthly averages
        chartData = Object.keys(monthlyData)
          .sort()
          .map((monthKey) => {
            const data = monthlyData[monthKey];
            return {
              month: monthKey,
              avgGoals:
                data.goals.reduce((a, b) => a + b, 0) / data.goals.length,
              avgAssists:
                data.assists.reduce((a, b) => a + b, 0) / data.assists.length,
              avgRating:
                data.rating.reduce((a, b) => a + b, 0) / data.rating.length,
              avgMinutes:
                data.minutes.reduce((a, b) => a + b, 0) / data.minutes.length,
            };
          });
      } else {
        // Fallback to dummy data if no match data available
        chartData = [
          {
            month: "2024-08",
            avgGoals: 2,
            avgAssists: 1,
            avgRating: 7.2,
            avgMinutes: 75,
          },
          {
            month: "2024-09",
            avgGoals: 3,
            avgAssists: 2,
            avgRating: 7.5,
            avgMinutes: 80,
          },
          {
            month: "2024-10",
            avgGoals: 1,
            avgAssists: 3,
            avgRating: 7.1,
            avgMinutes: 85,
          },
          {
            month: "2024-11",
            avgGoals: 2,
            avgAssists: 1,
            avgRating: 7.3,
            avgMinutes: 78,
          },
        ];
      }

      const data = {
        labels: chartData.map((d) => d.month),
        datasets: [
          {
            label: "Goals",
            data: chartData.map((d) => d.avgGoals),
            borderColor: "rgba(255, 99, 132, 1)",
            backgroundColor: "rgba(255, 99, 132, 0.2)",
            tension: 0.4,
          },
          {
            label: "Assists",
            data: chartData.map((d) => d.avgAssists),
            borderColor: "rgba(54, 162, 235, 1)",
            backgroundColor: "rgba(54, 162, 235, 0.2)",
            tension: 0.4,
          },
          {
            label: "Rating",
            data: chartData.map((d) => d.avgRating),
            borderColor: "rgba(255, 205, 86, 1)",
            backgroundColor: "rgba(255, 205, 86, 0.2)",
            tension: 0.4,
            yAxisID: "y1",
          },
        ],
      };

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false, // Disable animations to prevent canvas issues
        interaction: {
          mode: "index",
          intersect: false,
        },
        plugins: {
          legend: {
            labels: {
              color: "#fff",
              font: {
                size: 11,
              },
            },
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "#fff",
            bodyColor: "#fff",
          },
        },
        scales: {
          x: {
            ticks: {
              color: "#fff",
            },
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
          },
          y: {
            type: "linear",
            display: true,
            position: "left",
            ticks: {
              color: "#fff",
            },
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
          },
          y1: {
            type: "linear",
            display: true,
            position: "right",
            ticks: {
              color: "#fff",
            },
            grid: {
              drawOnChartArea: false,
            },
          },
        },
      };

      this.monthlyTrendChart = new Chart(ctx, {
        type: "line",
        data: data,
        options: options,
      });
    },

    renderDefensiveActionsChart() {
      const canvas = document.getElementById("defensiveActionsCanvas");
      if (!canvas) {
        console.error("Defensive Actions canvas not found");
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.error("Could not get 2D context for defensive actions canvas");
        return;
      }

      // Destroy existing chart if any
      if (this.defensiveActionsChart) {
        try {
          this.defensiveActionsChart.destroy();
        } catch (error) {
          console.warn(
            "Error destroying existing defensive actions chart:",
            error,
          );
        }
        this.defensiveActionsChart = null;
      }

      const player = this.selectedScottishPlayer;
      if (!player) {
        console.error("No player selected for Defensive Actions chart");
        return;
      }

      const tackles = this.getPlayerStatValue(player, "tackles") || 0;
      const interceptions =
        this.getPlayerStatValue(player, "interceptions") || 0;
      const clearances = this.getPlayerStatValue(player, "clearances") || 0;
      const blockedShots =
        this.getPlayerStatValue(player, "blocked_shots") || 0;
      const foulsCommitted =
        this.getPlayerStatValue(player, "fouls_committed") || 0;

      console.log("Defensive Actions Chart Data:", {
        tackles,
        interceptions,
        clearances,
        blockedShots,
        foulsCommitted,
        playerName: player.full_name || player.name,
      });

      // Check if all values are 0, provide fallback data
      const hasData =
        tackles ||
        interceptions ||
        clearances ||
        blockedShots ||
        foulsCommitted;
      const chartData = hasData
        ? [tackles, interceptions, clearances, blockedShots, foulsCommitted]
        : [8, 5, 12, 3, 4]; // Fallback data for demo purposes

      const data = {
        labels: [
          "Tackles",
          "Interceptions",
          "Clearances",
          "Blocked Shots",
          "Fouls",
        ],
        datasets: [
          {
            label: "Defensive Actions",
            data: chartData,
            backgroundColor: [
              "rgba(75, 192, 192, 0.8)",
              "rgba(153, 102, 255, 0.8)",
              "rgba(255, 159, 64, 0.8)",
              "rgba(54, 162, 235, 0.8)",
              "rgba(255, 99, 132, 0.8)",
            ],
            borderColor: [
              "rgba(75, 192, 192, 1)",
              "rgba(153, 102, 255, 1)",
              "rgba(255, 159, 64, 1)",
              "rgba(54, 162, 235, 1)",
              "rgba(255, 99, 132, 1)",
            ],
            borderWidth: 2,
          },
        ],
      };

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false, // Disable animations to prevent canvas issues
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "#fff",
            bodyColor: "#fff",
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              color: "#fff",
            },
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
          },
          x: {
            ticks: {
              color: "#fff",
            },
            grid: {
              color: "rgba(255, 255, 255, 0.1)",
            },
          },
        },
      };

      this.defensiveActionsChart = new Chart(ctx, {
        type: "bar",
        data: data,
        options: options,
      });
    },

    // Helper methods for getting player stats
    getNestedValue(obj, path) {
      return path.split(".").reduce((current, key) => current?.[key], obj);
    },

    normalizePlayerStat(value, min, max) {
      if (value === null || value === undefined || isNaN(value)) return 0;
      const numValue = parseFloat(value);
      return Math.round(((numValue - min) / (max - min)) * 100);
    },

    // Method for template to get player stats
    // Performance Charts Methods
    renderPlayerAttributesChart() {
      const canvas = document.getElementById("playerAttributesRadarChart");
      if (!canvas) {
        console.log("Player attributes radar canvas not found");
        return;
      }

      const player = this.selectedScottishPlayer;
      if (!player) return;

      const ctx = canvas.getContext("2d");
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      const radius = canvas.width / 2 - 70;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Get attribute values (0-100 scale)
      const attributes = [
        {
          label: "Attacking",
          value: parseFloat(
            player.attacking || player.attributes?.attacking || 0,
          ),
          color: "#3498db", // Blue
        },
        {
          label: "Technical",
          value: parseFloat(
            player.technical || player.attributes?.technical || 0,
          ),
          color: "#17a2b8", // Cyan
        },
        {
          label: "Tactical",
          value: parseFloat(
            player.tactical || player.attributes?.tactical || 0,
          ),
          color: "#28a745", // Green
        },
        {
          label: "Defending",
          value: parseFloat(
            player.defending || player.attributes?.defending || 0,
          ),
          color: "#ffc107", // Yellow/Orange
        },
        {
          label: "Creativity",
          value: parseFloat(
            player.creativity || player.attributes?.creativity || 0,
          ),
          color: "#dc3545", // Red
        },
      ];

      const angleStep = (Math.PI * 2) / attributes.length;

      // Draw background circles (20, 40, 60, 80, 100)
      ctx.strokeStyle = "#e9ecef";
      ctx.lineWidth = 1;
      for (let i = 1; i <= 5; i++) {
        ctx.beginPath();
        ctx.arc(centerX, centerY, (radius / 5) * i, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Draw value labels (20, 40, 60, 80, 100)
      ctx.fillStyle = "#6c757d";
      ctx.font = "11px Arial";
      ctx.textAlign = "center";
      for (let i = 1; i <= 5; i++) {
        const value = i * 20;
        ctx.fillText(value.toString(), centerX, centerY - (radius / 5) * i - 5);
      }

      // Draw axes and labels
      ctx.strokeStyle = "#ced4da";
      ctx.lineWidth = 1.5;
      attributes.forEach((attribute, index) => {
        const angle = angleStep * index - Math.PI / 2;
        const x = centerX + Math.cos(angle) * radius;
        const y = centerY + Math.sin(angle) * radius;

        // Draw axis line
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.lineTo(x, y);
        ctx.stroke();

        // Draw label with colored background
        ctx.font = "bold 13px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";

        const labelX = centerX + Math.cos(angle) * (radius + 35);
        const labelY = centerY + Math.sin(angle) * (radius + 35);

        // Draw colored circle behind label
        ctx.fillStyle = attribute.color;
        ctx.beginPath();
        ctx.arc(labelX, labelY - 12, 8, 0, Math.PI * 2);
        ctx.fill();

        // Draw label text
        ctx.fillStyle = "#ffffff";
        ctx.fillText(attribute.label, labelX, labelY + 5);

        // Draw value
        ctx.font = "bold 11px Arial";
        ctx.fillStyle = attribute.color;
        ctx.fillText(`${Math.round(attribute.value)}`, labelX, labelY + 20);
      });

      // Draw data polygon (filled)
      ctx.beginPath();
      attributes.forEach((attribute, index) => {
        const angle = angleStep * index - Math.PI / 2;
        const distance = (attribute.value / 100) * radius;
        const x = centerX + Math.cos(angle) * distance;
        const y = centerY + Math.sin(angle) * distance;

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      ctx.closePath();

      // Fill polygon with gradient
      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        radius,
      );
      gradient.addColorStop(0, "rgba(52, 152, 219, 0.3)");
      gradient.addColorStop(1, "rgba(52, 152, 219, 0.05)");
      ctx.fillStyle = gradient;
      ctx.fill();

      // Draw polygon border
      ctx.strokeStyle = "#3498db";
      ctx.lineWidth = 2.5;
      ctx.stroke();

      // Draw data points
      attributes.forEach((attribute, index) => {
        const angle = angleStep * index - Math.PI / 2;
        const distance = (attribute.value / 100) * radius;
        const x = centerX + Math.cos(angle) * distance;
        const y = centerY + Math.sin(angle) * distance;

        // Outer circle (white)
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(x, y, 6, 0, Math.PI * 2);
        ctx.fill();

        // Inner circle (colored)
        ctx.fillStyle = attribute.color;
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
      });

      // Draw center point
      ctx.fillStyle = "#2c3e50";
      ctx.beginPath();
      ctx.arc(centerX, centerY, 4, 0, Math.PI * 2);
      ctx.fill();

      console.log("Player attributes chart rendered successfully");
    },

    renderAllPerformanceCharts() {
      this.$nextTick(() => {
        this.renderPerformanceRadarChartJS();
        this.renderGoalContributionChartJS();
        this.renderDuelsGauges();
      });
    },

    loadPlayersProfilePerformanceData() {
      this.resetAllPlayerData();
      this.getAllGamesAttendedById();
      this.getAllTraningAttendedById();
      this.getThisSeasonsgamePerformanceReportsForUser();
    },
    async archiveUser(userData) {
      userData.userType = "Archived";
      const playerData = {
        ...userData,
        action: "archiveUser",
      };
      const responce = await Users.find(playerData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.saveTrialistPerformanceReview();
        this.getTrialists();
        this.showViewTrialistLeadPlayerProfile = false;
        this.$toast.info(responce.data, {
          title: "Player archived",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    async rejectPlayer() {
      this.archiveUser(this.selectedUserData);
    },
    async signPlayer() {
      const data = {
        trialistId: this.selectedTrialistsData.id,
        action: "signTrialistPlayer",
      };

      const responce = await Users.find(data).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.saveTrialistPerformanceReview();
        this.getTrialists();
        this.showViewTrialistLeadPlayerProfile = false;
        this.loadingStuff = false;
        this.$toast.info("Trialist signed.", {
          title: "removed",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });

        this.loadPlayersProfilePerformanceData();
      }
    },
    getCoachesName(coach_id) {
      let name = "";
      this.coachData.map((coach) => {
        if (coach.id === coach_id) {
          name = coach.name;
        }
      });
      return name;
    },
    outComeActions(value) {
      value == 1 ? this.signPlayer() : this.rejectPlayer();
    },
    async populateTeamData() {
      const teamData = {
        clubId: this.userData.clubId,
        action: "populateTeamData",
      };

      const responce = await Teams.find(teamData).catch((error) => {
        console.log(error);
      });

      return responce.data.length > 0 ? responce.data : [];
    },
    async removeTrialistFromDB(id) {
      const userData = {
        action: "removeTrialist",
        id: id,
      };

      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.getTrialists();
        this.loadPlayerLeads();
        this.showViewTrialist = false;
        this.loadingStuff = false;
        this.$toast.info("Trialist removed.", {
          title: "removed",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    recordOutcome(value) {
      this.selectedTrialistsData.outcome = value;
      this.outComeActions(value);
    },
    outComeText(outCome) {
      switch (outCome) {
        case 0:
          return "Under consideration";
        case 1:
          return "Signed";
        case 2:
          return "Rejected";
        case 3:
          return "Waiting list";
      }
    },
    totalScore(trialist) {
      let values = [
        trialist.attitude,
        trialist.skill,
        trialist.physique,
        trialist.mentality,
        trialist.leadership,
        trialist.professionalism,
      ];

      var totalYears = values.reduce(function (accumulator, value) {
        return accumulator + +value;
      }, 0);

      return totalYears;
    },
    getTeamName(teamId) {
      if (teamId === "") {
        return teamId;
      }
      var selectedTeam = this.clubTeams.find(function (team) {
        return team.id == teamId;
      });

      return selectedTeam !== undefined ? selectedTeam.teamName : "";
    },
    trialLength(number) {
      if (number == "6") {
        return "6 weeks";
      }
      if (number == "12") {
        return "12 weeks";
      }
    },
    tabChange(tabValue, selectedTabValue) {
      // Clean up performance charts when switching away from performance charts tab
      if (
        tabValue === "selectedScottishPlayerTab" &&
        this.selectedScottishPlayerTab === "player-performance-charts" &&
        selectedTabValue !== "player-performance-charts"
      ) {
        console.log("Switching away from Performance Charts - cleaning up...");
        this.cleanupPerformanceCharts();
      }

      this[tabValue] = selectedTabValue;

      if (selectedTabValue === "player-documents-tab") {
        if (this.selectedTrialistsData.id !== undefined) {
          this.selectedDocumentData.approvedAccessList.users = [];
          this.selectedDocumentData.approvedAccessList.users.push({
            id: this.selectedTrialistsData.id,
            name: this.selectedTrialistsData.name,
          });
        }

        this.getDocumentUploadsByUserId();
      }

      if (selectedTabValue === "player-videos-tab") {
        this.populatePlayerVideoLinkData();
      }

      // Fetch player media when media tab is selected
      if (
        selectedTabValue === "player-media" &&
        this.selectedScottishPlayer.player_id
      ) {
        this.fetchPlayerMedia(this.selectedScottishPlayer.player_id);
      }

      // Fetch heatmap data when Scottish player heatmap tab is selected
      if (selectedTabValue === "player-heatmap") {
        if (this.selectedScottishPlayer && this.selectedScottishPlayer.id) {
          console.log(
            "Fetching heatmap for player:",
            this.selectedScottishPlayer.id,
          );
          this.fetchPlayerHeatmap(this.selectedScottishPlayer.id);
        } else {
          console.warn("No selected Scottish player for heatmap");
        }
      }

      // Fetch heatmap data when trialist heatmap tab is selected
      if (selectedTabValue === "player-heatmap-tab") {
        console.log("Loading trialist heatmap data");
        // This will be handled by the watcher we added
      }

      // Render performance charts when performance charts tab is selected
      if (selectedTabValue === "player-performance-charts") {
        if (this.selectedScottishPlayer) {
          console.log(
            "Rendering performance charts for player:",
            this.selectedScottishPlayer.name,
          );
          this.renderAllPerformanceCharts();
        } else {
          console.warn("No selected Scottish player for performance charts");
        }
      }

      if (selectedTabValue === "player-report-tab") {
        this.getAllgoalsScoredByPlayerId();
        this.getAllgoalAssistsByPlayerId();
        this.populatePlayerVideoLinkData();
        this.populateAcademyReport();
        this.produceTheAcademyReport();
      }

      if (selectedTabValue === "player-history-tab") {
        this.populatePlayerCV(this.selectedUserData);
      }
      if (selectedTabValue === "fixtures-tab") {
        this.populatefixtures();
      }
      if (selectedTabValue === "analytics-tab" && this.isAnalyticsDashboardVisible) {
        this.loadAnalyticsData();
      }
    },
    getImgUrl(pic) {
      return pic !== undefined && pic !== "" ? `/images/${pic}` : "";
    },
    clearImageDataFromUpload() {
      ((this.imageURL = ""),
        (this.image = {
          imageName: "",
          path: "",
          file: null,
        }));
    },
    imageUploadObjectDataChanged(data) {
      this.image = data;
    },
    imageUploadFileDataChnged(data) {
      this.imageFile = data;
    },
    trialistDataconvertStringsToNumbers(trialist) {
      trialist.attitude = parseInt(trialist.attitude);
      trialist.skill = parseInt(trialist.skill);
      trialist.physique = parseInt(trialist.physique);
      trialist.mentality = parseInt(trialist.mentality);
      trialist.leadership = parseInt(trialist.leadership);
      trialist.professionalism = parseInt(trialist.professionalism);

      return trialist;
    },
    // Analytics Dashboard Methods
    loadAnalyticsData() {
      // Load analytics data when the tab is activated
      this.analyticsData = {
        trialists: this.liveTrialistsData,
        playerLeads: this.allPlayerLeadsData,
        gameAttendance: this.allScoutGameAttendanceReports,
        // Add more analytics data as needed
      };

      // Set default date range (last 3 months)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3);
      this.dateRange = [startDate, endDate];
    },
    handleAnalyticsDataPointClick(data) {
      // Handle clicks on analytics data points
      console.log("Analytics data point clicked:", data);

      // You can add logic here to navigate to specific views
      // or show detailed information based on the clicked data
      if (data.type === "distribution") {
        // Handle distribution chart clicks
        this.$toast.info(
          `Clicked on ${data.category} distribution: ${data.range}`,
        );
      } else if (data.type === "calibration") {
        // Handle calibration chart clicks
        this.$toast.info(
          `Scout prediction: ${data.predicted}, Actual: ${data.actual}`,
        );
      }
    },

    handleAnalyticsNavigation(view) {
      // Handle navigation between analytics views
      console.log("Navigating to analytics view:", view);
      this.analyticsView = view;
    },
    async getUserByEmailAndName(email, name) {
      const userData = {
        name: name,
        email: email,
        action: "getUserByEmailAndName",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.selectedUserData = responce.data;
        this.loadPlayersProfilePerformanceData();
      }
    },
    async populateLeadAndTrialistData(playerData, type) {
      //get trialist id
      let trialistData = this.trialistDataconvertStringsToNumbers(playerData);
      this.selectedTrialistsData = trialistData;

      if (type === "signed") {
        await this.getUserByEmailAndName(playerData.email, playerData.name);
      } else {
        this.getUserByTrialistId(this.selectedTrialistsData.id);
      }

      //populate player lead data
      this.selectedLeadData = this.allPlayerLeadsData.find(
        (lead) => lead.trialistId === playerData.id,
      );
      if (this.selectedLeadData === undefined) {
        this.selectedLeadData = {};
      }
    },
    selectedUseChanged(playerData, type, state) {
      if (type === "trialist") {
        this.populateLeadAndTrialistData(playerData, state);
      } else {
        //lead mode
        if (playerData.trialistId !== null) {
          this.selectedTrialistsData = this.trialistsData.find(
            (lead) => Number(lead.id) === Number(playerData.trialistId),
          );
          this.getUserByTrialistId(this.selectedTrialistsData.id);
          this.selectedLeadData = playerData;
        } else {
          this.selectedLeadData = playerData;
          this.selectedUserData = {
            name: playerData.name,
            age_group: playerData.age_group,
            userImage: null,
          };
        }
      }
      this.showViewTrialistLeadPlayerProfile = true;
      this.loadLeadAssessments();
    },
    weekSelectedChanged(number) {
      this.weekSelectedNumber = number.weekSelectedNumber;
    },
    mondaySunDatesChanged(data) {
      this.selectedWeekMonSunday = data.monToFri;
    },
    setMonToSundayWeekDates() {
      const year = JSON.stringify(moment().year());
      const monday = moment()
        .year(year)
        .isoWeek(this.weekSelectedNumber)
        .day("monday")
        .startOf("day");
      this.selectedWeekMonSunday =
        monday.format("MMM D - ") + monday.add(6, "days").format("D YYYY");
      // this.depatchMonToSundayWeekDatesInLocalStorage();
    },
    async getUserByTrialistId(trialistsId) {
      const userData = {
        trialistsId: trialistsId,
        action: "getUserByTrialistId",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.selectedUserData = responce.data;
        this.loadPlayersProfilePerformanceData();
      }
    },
    async getUserById(clientId) {
      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.userData = responce.data[0];
        const userData = responce.data[0];
        this.$store.dispatch(USER_POPULATE, { userData });
        return true;
      }
    },
    async getUserByIdForHeader(clientId) {
      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.userHeaderData = responce.data[0];
      }
    },
    addEditCoachDetails() {
      this.editMode !== true
        ? this.addTrialistDetailsSubmit()
        : this.saveTrialistPerformanceReview(); //this.editTrialistDetailsSubmit();
    },
    async saveTrialistPerformanceReview() {
      this.selectedTrialistsData.trial_start_date = this.fotmatDatesForDb(
        this.selectedTrialistsData.trial_start_date,
      );

      if (this.image.imageFile !== null && this.imageFile !== null) {
        this.selectedUserData.imageFile = this.image;
        this.selectedUserData.fileName = this.imageFile.imageName;
      }

      this.loadingStuff = true;
      const userData = {
        action: "updateTrialist",
        ...this.selectedTrialistsData,
        trialUserProfileData: this.selectedUserData,
      };

      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.getTrialists();
        this.loadPlayerLeads();
        this.showViewTrialist = false;
        this.showAddEditTrialist = false;
        this.showViewTrialistLeadPlayerProfile = false;
        this.loadingStuff = false;
        this.$toast.info("Trialist updated.", {
          title: "Updated",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    filterTrialistsIntoCategories() {
      this.liveTrialistsData = this.trialistsData.filter(
        (trialists) => trialists.outcome === 0,
      );
      this.liveTrialistsDataFiltered = this.liveTrialistsData;

      this.signedtrialistsData = this.trialistsData.filter(
        (trialists) => trialists.outcome === 1,
      );
      this.archivedtrialistsData = this.trialistsData.filter(
        (trialists) => trialists.outcome === 2,
      );
    },
    async getOnlyScoutTrialists() {
      const userData = {
        action: "getOnlyScoutTrialists",
        clubId: this.userData.clubId,
        scoutId: this.userData.id,
      };

      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.trialistsData = responce.data;
      }

      this.filterTrialistsIntoCategories();

      this.coachData = await this.populateCoachesData();
      this.teamData = await this.populateTeamData();
    },
    async getAllTrialists() {
      const userData = {
        action: "getTrialists",
        clubId: this.userData.clubId,
      };

      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.data.length > 0) {
        this.trialistsData = responce.data;
      }

      this.filterTrialistsIntoCategories();

      this.coachData = await this.populateCoachesData();
      this.teamData = await this.populateTeamData();
    },
    async getTrialists() {
      this.getAllTrialists();

      /*this.userData.userType === "Scout"
      ? this.getOnlyScoutTrialists()
      : this.getAllTrialists();*/
    },
    setClubName() {
      if (this.userData.clubId === "surfSoccer-2085XH") {
        return "The big test";
      }

      if (this.userData.clubId === "dfc-2085XH") {
        return "Dundee";
      }

      if (this.userData.clubId === "dfc-2085XH") {
        return "Dundee";
      }

      if (this.userData.clubId === "hafc-2085XH") {
        return "Hamilton Academical";
      }

      if (this.userData.clubId === "kfc-2085XH") {
        return "kilmarnock fc";
      }
    },
    async getTeamsByUserId() {
      const data = {
        action: "getTeamsDataById",
        userId: this.userData.id,
      };

      if (
        this.userData.userType === "Admin" ||
        this.userData.userType === "Coach"
      ) {
        data.clubId = this.userData.clubId;
      }
      const responce = await Teams.find(data).catch((error) => {
        console.log(error);
      });

      if (this.clubTeams.length > 0) {
        this.clubTeams = this.removeDuplicates(responce.data, "id");
      } else {
        this.clubTeams = await this.populateTeamData();
        this.clubTeams = this.removeDuplicates(this.clubTeams, "id");
      }

      this.season_start_date = this.clubTeams[0].season_start_date;
      this.season_end_date = this.clubTeams[0].season_end_date;

      // Don't clobber a past-season override. When the global season
      // selector switches the app to a past season it sets
      // `selectedSeasonYear` in localStorage — leaving the effective
      // window alone here keeps the user's selection in effect.
      if (!localStorage.getItem("selectedSeasonYear")) {
        localStorage.setItem(
          "currentPlayingseasonStartDate",
          this.season_start_date,
        );
        localStorage.setItem(
          "currentPlayingseasonEndDate",
          this.season_end_date,
        );
      }
    },
    async addTrialistDetailsSubmit() {
      this.selectedTrialistsData.attitude = 0;
      this.selectedTrialistsData.skill = 0;
      this.selectedTrialistsData.physique = 0;
      this.selectedTrialistsData.mentality = 0;
      this.selectedTrialistsData.leadership = 0;
      this.selectedTrialistsData.professionalism = 0;
      this.selectedTrialistsData.comments = "";
      this.selectedTrialistsData.outcome = 0;
      this.selectedTrialistsData.clubId = this.userData.clubId;

      this.selectedTrialistsData.leadId = this.selectedLeadData.id;
      this.loadingStuff = true;
      if (this.image.imageFile !== null && this.imageFile !== null) {
        this.selectedTrialistsData.fileName = this.imageFile.imageName;
        this.selectedTrialistsData.imageFile = this.image;
      }
      this.selectedTrialistsData.trial_start_date = this.fotmatDatesForDb(
        this.selectedTrialistsData.trial_start_date,
      );
      this.selectedTrialistsData.age_group = this.formatAgeGroupForSQL(
        this.selectedTrialistsData.dob,
      );

      this.selectedTrialistsData.coach_name = this.getCoachesName(
        this.selectedUserData.coach_id,
      );
      this.selectedTrialistsData.permission === true ? 1 : 0;
      this.selectedTrialistsData.userType = "User";

      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedTrialistsData,
          "name",
        )
      ) {
        this.selectedTrialistsData.name = this.selectedUserData.name;
      }
      if (
        !Object.prototype.hasOwnProperty.call(this.selectedTrialistsData, "dob")
      ) {
        this.selectedTrialistsData.dob = this.fotmatDatesForDb(
          this.selectedUserData.dob,
        );
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedTrialistsData,
          "playing_position",
        )
      ) {
        this.selectedTrialistsData.playing_position =
          this.selectedUserData.position;
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedTrialistsData,
          "preferred_foot",
        )
      ) {
        this.selectedTrialistsData.preferred_foot =
          this.selectedUserData.preferred_foot;
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedTrialistsData,
          "email",
        )
      ) {
        this.selectedTrialistsData.email = this.selectedUserData.email;
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedTrialistsData,
          "leadId",
        )
      ) {
        this.selectedTrialistsData.leadId = 0;
      }

      if (
        !Object.prototype.hasOwnProperty.call(this.selectedUserData, "position")
      ) {
        this.selectedUserData.position =
          this.selectedTrialistsData.playing_position;
      }
      if (
        !Object.prototype.hasOwnProperty.call(this.selectedUserData, "clubId")
      ) {
        this.selectedUserData.clubId = this.userData.clubId;
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedUserData,
          "userImage",
        )
      ) {
        this.selectedUserData.userImage = "";
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedUserData,
          "place_of_birth",
        )
      ) {
        this.selectedUserData.place_of_birth = "";
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedUserData,
          "country_of_birth",
        )
      ) {
        this.selectedUserData.country_of_birth = "";
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedUserData,
          "nationality",
        )
      ) {
        this.selectedUserData.nationality = "";
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedUserData,
          "previous_club",
        )
      ) {
        this.selectedUserData.previous_club = "";
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedUserData,
          "joined_date",
        )
      ) {
        this.selectedUserData.joined_date = null;
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedUserData,
          "nationality",
        )
      ) {
        this.selectedUserData.joined_date = null;
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedUserData,
          "preferred_foot",
        )
      ) {
        this.selectedUserData.joined_date = null;
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedTrialistsData,
          "age_group",
        )
      ) {
        this.selectedTrialistsData.age_group = this.formatAgeGroupForSQL(
          this.selectedUserData.dob,
        );
      }

      if (
        !Object.prototype.hasOwnProperty.call(this.selectedUserData, "userType")
      ) {
        this.selectedUserData.userType = "User";
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          this.selectedUserData,
          "age_group",
        )
      ) {
        this.selectedUserData.age_group = this.formatAgeGroupForSQL(
          this.selectedUserData.dob,
        );
      }

      this.selectedTrialistsData.preferred_foot === "Left"
        ? (this.selectedUserData.preferred_foot = 0)
        : (this.selectedUserData.preferred_foot = 1);

      const userData = {
        ...this.selectedTrialistsData,
        registerUser: this.selectedUserData,

        action: "addTrialist",
      };
      const responce = await Trialists.find(userData).catch((error) => {
        console.log(error);
      });

      if (responce.status === 200) {
        this.getTrialists();
        this.loadPlayerLeads();
        this.showAddEditTrialist = false;
        this.loadingStuff = false;
        this.showViewTrialist = false;
        this.$toast.info("Trialist added.", {
          title: "Added",
          toaster: "b-toaster-bottom-right",
          solid: true,
          appendToast: true,
          variant: "success",
        });
      }
    },
    resetTrialistData() {
      this.selectedUserData = {};
      this.selectedLeadData = {};
      this.selectedTrialistsData = {};
      this.editMode = false;
      this.resetInviteState();
    },
    async addEditTrialistModel(trialist) {
      console.log(trialist);
      if (trialist !== undefined) {
        this.selectedUserData = {};
        this.selectedLeadData = trialist;
        this.selectedTrialistsData = { ...trialist };

        this.selectedUserData = {
          img: "",
          name: "",
          email: "",
          password: "",
          dob: "",
          position: "",
          phone: "",
          emergency_contact_one: "",
          emergency_contact_two: "",
          userType: "User",
          teamId: 0,
          permission: 0,
          clubId: null,
          coach_id: 0,
          team_module: 1,
          preferred_foot: 0,
          joined_date: null,
          medical: "",
        };

        this.selectedUserData.name = trialist.name;
        this.selectedUserData.dob = trialist.dob;
        this.selectedUserData.email = "";
        this.selectedUserData.position = trialist.playing_position;
        this.selectedUserData.age_group = trialist.age_group;
        this.selectedUserData.phone = trialist.phone || "";
      } else {
        this.selectedUserData = {};
        this.selectedLeadData = {};
        this.selectedTrialistsData = {};
        this.editMode = false;
      }
      this.showAddEditTrialist = true;
    },
    resetSelectedCoach() {
      this.selectedTrialistsData = {
        dob: "",
        trial_length: "6",
        trial_start_date: "",
        preferred_foot: "Right",
        email: "",
        name: "",
        position: "",
        attitude: 0,
        skill: 0,
        physique: 0,
        mentality: 0,
        leadership: 0,
        professionalism: 0,
        comments: "",
        outcome: "0",
        clubId: "",
        teamId: "",
      };

      this.selectedLeadData = {};
      this.selectedTab = "leads-tab";
      this.selectedPlayerInfoTab = "lead-tab";
    },
    setTodaysDate() {
      return dayjs().format("DD-MM-YYYY");
    },
    setHeaderUserData() {
      if (this.getProfile !== null) {
        this.userHeaderData = this.getProfile;
      }
    },
    // showModal and hideModal methods removed - now using direct boolean properties
    fotmatDatesForDb(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    formatDateForHuman(date) {
      return moment(date).format("MMMM Do YYYY");
    },
    formatDatesForSQL(date) {
      return moment(date).format(moment.HTML5_FMT.DATE);
    },
    formatAgeGroupForSQL(dob) {
      const d = new Date(dob);
      return d.getFullYear();
    },
    dayMonthYear(date) {
      return moment(date).format("D MMM YYYY");
    },
    setPlayerLeadTextColor(state) {
      switch (state) {
        case "Invited for trial":
          return "green-text";
        case "Still to Watch":
          return "amber-text";
        case "Not at the standard required":
          return "red-text";
        case "Trial invite refused":
          return "red-text";
        default:
          return "";
      }
    },
    populatePlayerCV(data) {
      this.playerCvData.personal_profile = data.personal_profile;
      this.populatePlayerPlayingExperenceData();
      this.populatePlayerEducationData();
      this.populatePlayerInterestsHobbiesData();
      this.populatePlayerVideoLinkData();
    },
    async populatePlayerEducationData() {
      const data = {
        action: "populatePlayerEducationData",
        userId: this.selectedUserData.id,
      };
      const responce = await PlayerCv.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.playerCvData.education = responce.data;
      }
    },
    async populatePlayerInterestsHobbiesData() {
      const data = {
        action: "populatePlayerInterestsHobbiesData",
        userId: this.selectedUserData.id,
      };
      const responce = await PlayerCv.find(data).catch((error) => {
        console.log(error);
      });
      if (responce.status === 200) {
        this.playerCvData.interests_hobbies = responce.data;
      }
    },
    // Helper functions for Player Detail Modal
    // Team Management Methods
    async loadUserManagementTeams() {
      console.log("Loading user management teams for user:", this.userData.id);

      if (
        !this.selectedUserTeamId &&
        this.userTeams &&
        this.userTeams.length > 0
      ) {
        this.selectedUserTeamId = this.userTeams[0].team_id;
        this.loadUserTeam(this.selectedUserTeamId);
      }

      /*try {
        const response = await Teams.getTeamsByUserId(this.userData.id);
        if (response.data && response.data.length > 0) {
          this.userTeams = response.data;
          // Select the first team by default if none selected
          if (!this.selectedUserTeamId) {
            this.selectedUserTeamId = this.userTeams[0].id;
            this.loadUserTeam(this.selectedUserTeamId);
          }
        }
      } catch (error) {
        console.error("Error loading user teams:", error);
      } */
    },
    async loadUserTeam(teamId) {
      if (!teamId) return;

      this.loadingStuff = true;

      // Get formation from selected team
      const selectedTeam = this.userTeams.find((t) => t.team_id === teamId);
      if (selectedTeam) {
        this.currentTeamFormation = selectedTeam.formation || "4-4-2";
      }

      const data = {
        action: "searchScottishPlayers",
        name: "",
        position: "",
        league: "",
        team: teamId,
        nationality: "",
        foot: "",
        age_min: null,
        age_max: null,
        season: null,
        min_height: null,
        max_height: null,
        min_weight: null,
        max_weight: null,
        min_market_value: null,
        max_market_value: null,
        min_goals: null,
        max_goals: null,
        min_assists: null,
        max_assists: null,
        min_minutes: null,
        max_minutes: null,
        min_appearances: null,
        max_appearances: null,
        min_rating: null,
        max_rating: null,
        min_yellow_cards: null,
        min_red_cards: null,
        min_penalty_goals: null,
        min_penalty_conversion: null,
        min_penalty_conceded: null,
        min_accurate_crosses_percentage: null,
        min_accurate_long_balls_percentage: null,
        min_accurate_passes_percentage: null,
        min_aerial_duels_won_percentage: null,
        min_ground_duels_won_percentage: null,
        min_total_duels_won_percentage: null,
        min_offsides: null,
        min_clean_sheets: null,
        min_possession_lost: null,
        min_possession_won_att_third: null,
        min_saved_shots_from_inside_the_box: null,
        min_saves: null,
        min_scoring_frequency: null,
        min_tackles_won_percentage: null,
        min_total_long_balls: null,
        min_proposed_market_value: null,
        max_proposed_market_value: null,
        min_attacking: 0,
        min_technical: 0,
        min_tactical: 0,
        min_defending: 0,
        min_creativity: 0,
        useAdvancedSearch: false,
      };

      try {
        // First, try to load saved team with custom positions
        let savedTeamData = null;
        try {
          const teamResponse = await Teams.getTeamById(teamId);
          savedTeamData = teamResponse.data;
          console.log("📥 Loaded saved team data:", savedTeamData);
        } catch (e) {
          console.log("No saved team data found, will use formation positions");
        }

        // Get players from scout search
        const response = await PlayerScoutSearch.find(data);
        if (response && response.data) {
          const players = response.data.data.players;

          // Check if we have saved positions
          if (savedTeamData && savedTeamData.players) {
            try {
              // Parse saved positions if it's a string
              const savedPlayersData =
                typeof savedTeamData.players === "string"
                  ? JSON.parse(savedTeamData.players)
                  : savedTeamData.players;

              const mergedPlayers = players.map((currentPlayer) => {
                const savedPlayer = savedPlayersData.find(
                  (sp) =>
                    sp.sofascore_player_id ===
                    currentPlayer.sofascore_player_id,
                );

                console.log("single saved player", savedPlayer);

                if (
                  savedPlayer &&
                  savedPlayer.x_percent !== undefined &&
                  savedPlayer.y_percent !== undefined
                ) {
                  // Use saved position
                  return {
                    ...currentPlayer,
                    x_percent: savedPlayer.x_percent,
                    y_percent: savedPlayer.y_percent,
                  };
                } else {
                  // Apply default formation position for new players
                  const formationPositions = this.getFormationPositions(
                    this.currentTeamFormation,
                  );
                  const playerIndex = players.indexOf(currentPlayer);
                  let position;

                  if (playerIndex < formationPositions.length) {
                    // Starting 11 - use formation position
                    position = formationPositions[playerIndex];
                  } else {
                    // Substitutes - position along the bottom
                    const subIndex = playerIndex - formationPositions.length;
                    position = {
                      x: 10 + subIndex * 6, // Spread horizontally, starting at 10%
                      y: 95, // Bottom of pitch
                    };
                  }

                  console.log(
                    `❌ No saved position for ${currentPlayer.full_name} (ID: ${currentPlayer.player_id}), using ${playerIndex < 11 ? "formation" : "substitute"} default: x=${position.x}, y=${position.y}`,
                  );
                  return {
                    ...currentPlayer,
                    x_percent: position.x,
                    y_percent: position.y,
                  };
                }
              });

              this.currentTeamPlayers = mergedPlayers;
            } catch (e) {
              console.log(
                "Error parsing saved positions, using formation positions",
              );
              this.currentTeamPlayers = this.applyFormationPositions(
                players,
                this.currentTeamFormation,
              );
            }
          } else {
            // No saved positions, apply default formation
            console.log("No saved positions, using formation positions");
            this.currentTeamPlayers = this.applyFormationPositions(
              players,
              this.currentTeamFormation,
            );
          }

          this.loadingStuff = false;
        }
        this.loadingStuff = false;
      } catch (error) {
        console.error("Error loading team players:", error);
        this.$toast.error("Failed to load team players");
        this.loadingStuff = false;
      }

      /*try {
        const response = await Teams.getTeamById(teamId);
        if (response.data) {
          const teamData = response.data;
          // Parse players if it's a string
          if (typeof teamData.players === 'string') {
            try {
              this.currentTeamPlayers = JSON.parse(teamData.players);
            } catch (e) {
              this.currentTeamPlayers = [];
            }
          } else {
            this.currentTeamPlayers = teamData.players || [];
          }
        }
      } catch (error) {
        console.error("Error loading team:", error);
        this.$toast.error("Failed to load team");
      } finally {
        this.loadingStuff = false;
      }*/
    },
    // Drag and Drop functionality
    // Sidebar Search, Watch List, and Compare functionality
    loadSavedWatchList() {
      try {
        const saved = localStorage.getItem("teamBuilderWatchList");
        if (saved) {
          this.watchList = JSON.parse(saved);
        }
      } catch (error) {
        console.log("Could not load saved watch list:", error);
      }
    },
    // Unified player details method - uses the working Scottish player modal
    async showPlayerDetails(player) {
      console.log("showPlayerDetails called with player:", player);

      // Set the selected player data
      this.selectedScottishPlayer = player;
      this.selectedScottishPlayerTab = "player-details"; // Reset to first tab

      // Clear previous AI report data to prevent showing wrong player's report
      this.aiScoutReport = null;
      this.aiReportError = null;
      this.loadingAIReport = false;

      // Show the modal (use the working Scottish player modal)
      this.showViewScottishPlayer = true;

      try {
        // Get player ID from various possible properties
        const playerId = player.id || player.player_id || player.playerId;

        if (!playerId) {
          console.error("No valid player ID found in player object:", player);
          throw new Error("Player ID is missing");
        }

        // Fetch historical attributes
        await this.fetchPlayerAttributesHistory(playerId);

        // Render attributes chart after modal is shown
        this.$nextTick(() => {
          if (this.renderPlayerAttributesChart) {
            this.renderPlayerAttributesChart();
          }
        });

        // Also fetch detailed stats for compatibility
        const data = {
          action: "getPlayerDetails",
          playerId: playerId,
          season: "2023",
        };

        console.log("Fetching player details with data:", data);
        const response = await PlayerScoutSearch.find(data);

        if (response && response.data && response.data.response) {
          // Update the selected player with detailed stats
          this.selectedScottishPlayer = {
            ...player,
            detailedStats: response.data.response[0],
          };

          // Also set for backward compatibility
          this.selectedPlayerDetail = this.selectedScottishPlayer;

          this.$toast.success(
            `Loaded details for ${player.full_name || player.name}`,
            {
              title: "Player Details",
              toaster: "b-toaster-bottom-right",
              solid: true,
              appendToast: true,
              variant: "success",
            },
          );

          console.log("Detailed player data:", this.selectedScottishPlayer);
        }

        // Load transfers if method exists
        if (this.loadPlayerTransfers) {
          this.loadPlayerTransfers(player);
        }
      } catch (error) {
        console.error("Error loading player details:", error);
        // Don't show error toast for missing details - the modal can still show basic info
        console.log("Continuing with basic player info");
      }
    },
    // Performance Analysis Functions
    // Trialist Heatmap methods
    async loadTrialistPositionData() {
      if (!this.selectedUserData || !this.selectedUserData.id) {
        return;
      }

      try {
        this.heatmapLoading = true;
        this.heatmapError = null;

        // Use the same API URL as Opposition Analysis component
        const scottishFootballApiUrl =
          "http://localhost:8888/scottish-football-data/api/";

        const response = await fetch(
          `${scottishFootballApiUrl}opposition_analysis_enhanced.php?action=player_heatmap&club_id=${this.userData.clubId || 1}&player_id=${this.selectedUserData.id}`,
        );

        if (!response.ok) {
          throw new Error("Failed to load heatmap data");
        }

        const data = await response.json();

        if (data.success) {
          if (data.data && data.data.length > 0) {
            // Find the player's heatmap data
            const playerData =
              data.data.find((p) => p.player_id == this.selectedUserData.id) ||
              data.data[0];

            if (
              playerData &&
              playerData.heatmap_points &&
              playerData.heatmap_points.length > 0
            ) {
              this.heatmapPoints = playerData.heatmap_points.map((point) => ({
                x_coordinate: point.x,
                y_coordinate: point.y,
                touch_count:
                  point.touch_count || Math.round(point.intensity / 2),
                intensity: point.intensity,
              }));
            } else {
              // No heatmap data available
              this.heatmapPoints = [];
            }
          } else {
            // No data returned
            this.heatmapPoints = [];
          }
        } else {
          this.heatmapError = data.message || "Failed to load heatmap data";
        }
      } catch (err) {
        console.error("Error loading trialist heatmap data:", err);
        this.heatmapError = "Error loading position data";
      } finally {
        this.heatmapLoading = false;
      }
    },

    convertHeatmapX(x) {
      // Convert from 0-100 coordinate system to SVG coordinate system
      // Based on SVG analysis: pitch runs from x=0 to x=115 in original coordinates
      // After transform matrix (8.1300813 scale + 28.520325 offset), this becomes roughly:
      // Left goal line: ~28.5, Right goal line: ~963.5
      // So usable pitch width is ~935
      return 28.5 + (x / 100) * 935;
    },

    convertHeatmapY(y) {
      // Convert from 0-68 coordinate system to SVG coordinate system
      // Based on SVG analysis: pitch runs from y=978.3622 to y=1052.3622 in original coordinates
      // After transform and translation, this maps to the full height
      // The pitch height in final coordinates is approximately 600 units, centered vertically
      const pitchTop = 17; // Top of playable area
      const pitchHeight = 600; // Height of playable area
      return pitchTop + (y / 68) * pitchHeight;
    },

    getTrialistHeatmapRadius(intensity) {
      return Math.max(10, Math.min(40, (intensity / 25) * 10));
    },

    getTrialistHeatmapColor(intensity) {
      // Heat gradient from blue (low) to red (high)
      if (intensity < 25) return "#3498db";
      if (intensity < 50) return "#f39c12";
      if (intensity < 75) return "#e67e22";
      return "#e74c3c";
    },

    getTrialistHeatmapOpacity(intensity) {
      return Math.max(0.3, Math.min(0.8, intensity / 100));
    },

    getTrialistPositionColor(intensity) {
      return intensity > 50 ? "#007bff" : "#6c757d";
    },

    showTrialistPointTooltip(event, point) {
      const rect = event.target.getBoundingClientRect();
      this.heatmapTooltip = {
        visible: true,
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY - 60,
        data: point,
      };
    },

    hideTrialistPointTooltip() {
      this.heatmapTooltip.visible = false;
    },

    exportTrialistHeatmapData() {
      const exportData = {
        player: {
          name: this.selectedUserData.name,
          position: this.selectedUserData.position,
        },
        analysis: {
          totalTouches: this.trialistTotalTouches,
          averagePosition: this.trialistAveragePosition,
          movementRange: this.trialistMovementRange,
          zoneDistribution: this.trialistZoneDistribution,
          tacticalInsights: this.trialistTacticalInsights,
        },
        heatmapPoints: this.heatmapPoints,
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${this.selectedUserData.name.replace(/\s+/g, "_")}_trialist_heatmap_analysis.json`;
      link.click();
      URL.revokeObjectURL(url);
    },

    // Opposition Analysis style heatmap methods
    async loadHeatmapData() {
      if (
        !this.selectedScottishPlayer ||
        !this.selectedScottishPlayer.player_id
      ) {
        return;
      }

      try {
        this.heatmapLoading = true;
        this.heatmapError = null;

        // Use the same API URL as Opposition Analysis component
        const scottishFootballApiUrl =
          "http://localhost:8888/scottish-football-data/api/";

        const response = await fetch(
          `${scottishFootballApiUrl}opposition_analysis_enhanced.php?action=player_heatmap&club_id=${this.selectedScottishPlayer.club_id || 1}&player_id=${this.selectedScottishPlayer.player_id}`,
        );

        if (!response.ok) {
          throw new Error("Failed to load heatmap data");
        }

        const data = await response.json();

        if (data.success) {
          if (data.data && data.data.length > 0) {
            // Find the player's heatmap data
            const playerData =
              data.data.find(
                (p) => p.player_id == this.selectedScottishPlayer.player_id,
              ) || data.data[0];

            if (
              playerData &&
              playerData.heatmap_points &&
              playerData.heatmap_points.length > 0
            ) {
              this.heatmapPoints = playerData.heatmap_points.map((point) => ({
                x_coordinate: point.x,
                y_coordinate: point.y,
                touch_count:
                  point.touch_count || Math.round(point.intensity / 2),
                intensity: point.intensity,
              }));
            } else {
              // No heatmap data available
              this.heatmapPoints = [];
            }
          } else {
            // No data returned
            this.heatmapPoints = [];
          }
        } else {
          this.heatmapError = data.message || "Failed to load heatmap data";
        }
      } catch (err) {
        console.error("Error loading heatmap data:", err);
        this.heatmapError = "Error loading position data";
      } finally {
        this.heatmapLoading = false;
      }
    },

  },
  mounted() {
    console.log(
      "🚀 Trialist component mounted, loadingStuff:",
      this.loadingStuff,
    );

    // Load saved watch list from localStorage
    this.loadSavedWatchList();

    console.log(
      "✅ Trialist component mounted complete, loadingStuff:",
      this.loadingStuff,
    );

    // Ensure spinner is hidden after mount
    this.$nextTick(() => {
      if (this.loadingStuff) {
        console.log(
          "⚠️ loadingStuff is still true after mount, forcing to false",
        );
        this.loadingStuff = false;
      }
    });
  },

  beforeUnmount() {
    // Clean up all chart instances when component is destroyed
    this.cleanupPerformanceCharts();
  },
};
</script>
<style lang="scss" scoped>
/* Section side-drawer layout (SectionNav). Fixed left rail; page content
   padded left to clear it. Mirrors the Profile page pattern. */
.trialist-page.with-drawer {
  --section-nav-w: 260px;
  padding-left: calc(var(--section-nav-w) + 28px);
  transition: padding-left 0.2s ease;
}
.trialist-page.with-drawer.nav-collapsed {
  --section-nav-w: 72px;
}
@media (max-width: 768px) {
  .trialist-page.with-drawer,
  .trialist-page.with-drawer.nav-collapsed {
    padding-left: 0;
  }
}

.stats-item {
  .text-muted {
    color: #fafdffbf;
  }
}

.results-grid {
  align-items: start;
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.player-card-compact {
  background: #3a3b43;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  border: 1px solid #65666f;
  display: flex;
  flex-direction: column;
  width: 23.9rem;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    border-color: #667eea;
  }

  .card-header-compact {
    padding: 1rem;
    display: flex;
    gap: 1rem;
    align-items: center;
    border-bottom: 1px solid #65666f;

    .player-thumb-wrapper {
      width: 50px;
      height: auto;
      flex-shrink: 0;
      border-radius: 8px;
      overflow: hidden;

      .player-thumb {
        width: 100%;
        height: 100%;
        object-fit: cover;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      }

      .player-thumb-placeholder {
        width: 100%;
        height: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: 0.9rem;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      }
    }

    .player-info-compact {
      flex: 1;
      min-width: 0;

      h5 {
        margin: 0 0 0.2rem 0;
        font-size: 1rem;
        font-weight: 700;
        color: #ffffff;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      small {
        display: block;
        color: #d3d4da;
        font-size: 0.8rem;
        margin-bottom: 0.3rem;
      }

      .club-compact {
        display: flex;
        align-items: center;
        gap: 0.4rem;

        .club-logo-mini {
          width: auto;
          height: 27px;
          object-fit: contain;
        }

        span {
          font-size: 0.75rem;
          color: #d3d4da;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .card-stats-compact {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    padding: 0.75rem;
    background: #282b33;
    gap: 0.5rem;

    .stat-box {
      text-align: center;

      strong {
        display: block;
        font-size: 1.1rem;
        font-weight: 700;
        color: #ffffff;
        line-height: 1;
      }

      small {
        font-size: 0.7rem;
        color: #d3d4da;
        text-transform: uppercase;
      }

      &.rating strong {
        color: #667eea;
      }
    }
  }

  .card-actions-compact {
    padding: 0.75rem;
    background: #3a3b43;
  }
}

.empty-state-search {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  color: #d3d4da;
  text-align: center;

  i {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
    color: #667eea;
  }

  p {
    font-size: 1.2rem;
    font-weight: 500;
  }
}

.advanced-filter-grid {
  padding: 1rem;

  h5 {
    color: #2c3e50;
    font-weight: 700;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #f1f3f5;
  }
}

.advanced-toggle {
  .small {
    color: #c5cbd2;
  }
}

.advanced-form {
  .attribute-sliders {
    .slider-label {
      color: #c5cbd2;
    }
  }
}


.scout-id-tab {
  background: #e5e5e5;
}

.analytics-dashboard-tab {
  background: #f8f9fa;

  .analytics-dashboard-wrapper {
    padding: 2rem 0;
    min-height: 600px;
  }

  .analytics-navigation {
    position: fixed;
    top: 80px;
    left: 20px;
    z-index: 1000;
  }

  .nav-back-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: #007bff;
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
  }

  .nav-back-btn:hover {
    background: #0056b3;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
  }

  .nav-back-btn i {
    font-size: 1rem;
  }
}

.filters {
  width: 100%;
  transition: all 0.4s ease-in-out;
}

.filters-sort {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 1rem;
}

.search-small {
  max-width: 20rem;
}

.filter-small {
  height: 2.5rem;
  border: transparent;
  color: #9ea7b0;

  &:hover,
  &:focus {
    background: transparent;
    color: #7c838a;
    border: transparent;
    outline: transparent;
    box-shadow: 0 0 0 0rem;
  }
}

.scout-id {
  border: 2px solid #e1e5e9;
  padding: 2rem;
  border-radius: 10px;
  width: 50rem !important;
  position: relative;
  margin-bottom: 1rem;
  box-shadow: 0 4px 16px rgba(138, 138, 138, 0.3);
  background: white;

  .background-logo {
    position: absolute;
    top: 1rem;
    right: 1rem;
    opacity: 0.2;
    width: 37%;
  }

  h2 {
    border-bottom: 1px solid #e1e5e9;
    padding-bottom: 1rem;
    margin-bottom: 1rem;

    img {
      margin-right: 1rem;
      width: 4rem;
    }
  }

  .id-info-wrapper {
    display: flex;
    gap: 1rem;

    .user-photo-info {
      border: 1px solid #e1e5e9;
    }
  }
}

.other-leads-wrapper {
  margin-top: 3rem;
}

img.user-image-small {
  width: 3rem;
  height: 3rem;
  display: block;
  margin: 0 auto;
  border-radius: 400px;
}

.player-status-text {
  font-size: 2rem;
  line-height: 2rem;
  margin: 1rem 0 0 0;
  padding: 0;
  font-weight: bold;
}

.player-status-text.signed {
  color: #019e53;
}

.player-status-text.rejected {
  color: #ff2850;
}

.red-text {
  color: #ff2850;
  font-weight: bold;
}

.amber-text {
  color: #ffae21;
  font-weight: bold;
}

.amber-text {
  color: #ffae21;
  font-weight: bold;
}

.green-text {
  color: #019e53;
  font-weight: bold;
}

.trial-in-progress {
  color: #61b720;
}

.trial-ended {
  color: #f33256;
}

.player-added {
  border: 1px solid #06a110;
  padding: 1rem;
  border-radius: 4px;
}

.player-watched-details-wrapper {
  .player-watched-details {
    border: 1px solid #dee2e6;
    padding: 2rem;
    margin: 1rem 0;

    .buttons-wrapper {
      display: flex;
      gap: 0.5rem;
    }
  }
}

.invite-panel {
  padding: 1.5rem;
  background: #f3faf2;
  border: 1px solid #d8ecd5;
  border-radius: 4px;
  margin-bottom: 1.5rem;

  .invite-panel__title {
    font-size: 1.1rem;
    margin: 0 0 0.5rem;
  }

  .invite-panel__hint {
    margin-bottom: 1rem;
    color: #4c5a51;
  }

  .invite-panel__fields {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;

    .form__item {
      flex: 1 1 12rem;
      margin-bottom: 0.5rem;
    }
  }

  .invite-panel__email {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin: 0.5rem 0 0.75rem;

    input {
      flex: 1 1 14rem;
    }
  }

  .invite-panel__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.5rem;
  }

  .invite-panel__link {
    font-size: 0.85rem;
    word-break: break-all;
    background: #fff;
    border: 1px solid #d8ecd5;
    border-radius: 4px;
    padding: 0.5rem 0.75rem;
    flex: 1 1 16rem;
  }

  .invite-panel__error {
    color: #d85c64;
    margin: 0.75rem 0 0;
  }

  .invite-panel__divider {
    margin: 1.25rem 0 0;
    padding-top: 1rem;
    border-top: 1px solid #d8ecd5;
    font-weight: bold;
  }
}

.table-scroll {
  width: 100%;
  overflow: scroll;

  .w-a-style {
    width: 84rem;
  }
}

.player-report-content {
  margin-top: 1rem;
  width: 100%;
  height: 50rem;

  iframe {
    width: 100%;
    height: 100%;
  }
}

.intro-header {
  border-bottom: none;
}

.player-cv {
  border: 1px solid #a9b3bc;
  max-width: 1000px;
  padding: 2rem;
  overflow: scroll;
}

.cv-section {
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px dashed #dddddd;
}

.each-target.playing-experence-item {
  border-bottom: 1px dashed #dddddd;
  padding-bottom: 1rem;
  margin-bottom: 2rem;
}

.playing-experence-wrapper {
  padding-top: 1rem;

  .playing-experiance-add-button {
    margin-right: 1rem;
  }

  label {
    margin-right: 1rem;
  }
}

.player-cv-content {
  margin-top: 1rem;
  width: 100%;
  height: 50rem;

  iframe {
    width: 100%;
    height: 100%;
  }

  .form__item {
    display: grid;
  }

  // Enhanced Game Context Form Styles
  .enhanced-game-context-section,
  .venue-information-section,
  .match-result-section {
    margin: 2rem 0;
    padding: 1.5rem;
    border: 1px solid #e1e5e9;
    border-radius: 8px;
    background-color: #f8f9fa;

    .section-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 1rem;
      padding-bottom: 0.5rem;
      border-bottom: 2px solid #3498db;
    }
  }

  .form-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;

    .form__item--half {
      flex: 1;
    }

    .form__item--third {
      flex: 1;
      min-width: 0;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 0.5rem;
    }
  }

  .result-display {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.75rem;
    background-color: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    font-weight: 600;
    font-size: 1rem;

    .team-result {
      color: #2c3e50;
      font-size: 0.9rem;
    }

    .score-display {
      margin: 0 1rem;
      font-size: 1.2rem;
      color: #e74c3c;
      font-weight: bold;
    }
  }

  .form__input,
  .form__label {
    font-size: 0.9rem;
  }

  .form__input[type="number"] {
    text-align: center;
  }

  .advanced-mode-toggle {
    text-align: right;
    margin-bottom: 1rem;

    .btn {
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-1px);
      }

      i {
        margin-right: 0.5rem;
      }
    }
  }

  // Enhanced Games Attended Table Styles
  .competition-info,
  .venue-info,
  .result-info {
    .text-muted {
      font-size: 0.8rem;
      color: #6c757d !important;
    }

    strong {
      color: #2c3e50;
    }

    .badge {
      font-size: 0.7rem;
      padding: 0.2rem 0.4rem;
      margin-top: 0.2rem;

      &.badge-primary {
        background-color: #007bff;
      }

      &.badge-danger {
        background-color: #dc3545;
      }

      &.badge-success {
        background-color: #28a745;
      }

      &.badge-warning {
        background-color: #ffc107;
        color: #212529;
      }

      &.badge-info {
        background-color: #17a2b8;
      }

      &.badge-secondary {
        background-color: #6c757d;
      }
    }
  }

  .result-info {
    .score {
      font-size: 1.1rem;
      color: #e74c3c;
    }
  }

  // Enhanced table responsiveness
  .table-wrapper {
    overflow-x: auto;

    .vgt-table {
      min-width: 1000px;

      th,
      td {
        white-space: nowrap;
        padding: 0.75rem 0.5rem;
        vertical-align: middle;

        &:first-child,
        &:last-child {
          position: sticky;
          background-color: white;
          z-index: 1;
        }

        &:first-child {
          left: 0;
        }

        &:last-child {
          right: 0;
        }
      }
    }
  }
}

#small_shirt {
  width: 2.5rem;
  margin-right: 1.1rem;
}

.add-new-lead-wrapper {
  width: 100%;
  margin-bottom: 1rem;
}

.player-cv-content {
  .form__input {
    width: 100% !important;
  }
}

.leads-tab {
  padding: 1rem;
}

.lead-tabs-filters {
  margin: 1rem 0 1.25rem;
}

.leads-count {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.lead-phone-link {
  color: #0d6efd;
  text-decoration: none;
  font-variant-numeric: tabular-nums;

  &:hover {
    text-decoration: underline;
  }
}

.lead-cell-empty {
  color: #adb5bd;
}

.trial-status-pill {
  display: inline-block;
  margin-left: 0.6rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  vertical-align: middle;

  &.status-active {
    background: #d1e7dd;
    color: #0a3622;
  }

  &.status-ending-soon {
    background: #fff3cd;
    color: #664d03;
  }

  &.status-ended {
    background: #f8d7da;
    color: #58151c;
  }

  &.status-not-started {
    background: #cfe2ff;
    color: #052c65;
  }

  &.status-unknown {
    background: #f1f3f5;
    color: #6c757d;
  }
}

// `section.player-profile-wrapper` (defined in styles/index.scss) is
// display:flex with flex-wrap, so any direct child becomes a flex item.
// These elements need to stretch full-width and break to their own line
// rather than sitting alongside the trialist cards.
:deep(section.player-profile-wrapper) > .lead-tabs-filters,
:deep(section.player-profile-wrapper) > .leads-count,
:deep(section.player-profile-wrapper) > .trialist-group-heading,
:deep(section.player-profile-wrapper) > .empty-state {
  flex: 0 0 100%;
  width: 100%;
  max-width: 100%;
}

// Heading container is full-width only to force the flex parent to break
// to a new row; the visible pill below sizes to content.
.trialist-group-heading {
  margin: 1.5rem 0 0.75rem;
  padding: 0;
  background: none;
  border: 0;
  font-size: 0.95rem;
  color: inherit;
  font-weight: normal;
}

.trialist-group-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.85rem 0.4rem 0.75rem;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #495057;
  background: #f8f9fa;
  border-left: 4px solid #adb5bd;
  border-radius: 4px;

  .group-count {
    font-size: 0.8rem;
    color: #6c757d;
    font-weight: 500;
  }
}

.trialist-group-heading.group-ending-soon .trialist-group-pill {
  border-left-color: #ffc107;
  background: #fff8e1;
  color: #664d03;
}

.trialist-group-heading.group-active .trialist-group-pill {
  border-left-color: #198754;
  background: #e8f5e9;
  color: #0a3622;
}

.trialist-group-heading.group-ended .trialist-group-pill {
  border-left-color: #dc3545;
  background: #fbeaec;
  color: #58151c;
}

.players-assessed-pill {
  display: inline-block;
  min-width: 1.75rem;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  text-align: center;
  background: #e7f1ff;
  color: #084298;

  &.no-players {
    background: #f1f3f5;
    color: #adb5bd;
    font-weight: 500;
  }
}

.result-info .score {
  font-variant-numeric: tabular-nums;
  font-size: 0.95rem;
}

// Override vue-good-table's default right-alignment on date/number columns
// where we want left-aligned content for visual consistency with the rest
// of the table.
:deep(.vgt-text-left) {
  text-align: left !important;
}

.lead-date {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;

  &.fresh {
    background: #d1e7dd;
    color: #0a3622;
  }

  &.warning {
    background: #fff3cd;
    color: #664d03;
  }

  &.stale {
    background: #f8d7da;
    color: #58151c;
  }

  &.due-today {
    background: #cfe2ff;
    color: #052c65;
  }

  &.due-soon {
    background: #fff3cd;
    color: #664d03;
  }

  &.overdue {
    background: #f8d7da;
    color: #58151c;
    font-weight: 600;
  }
}

.lead-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem 1rem;

  .filter-group {
    display: flex;
    flex-direction: column;
    min-width: 180px;
    flex: 0 1 auto;
  }

  .filter-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6c757d;
    margin-bottom: 0.25rem;
  }

  .filter-select {
    height: 38px;
    padding: 0.375rem 2rem 0.375rem 0.75rem;
    font-size: 0.9rem;
    line-height: 1.5;
    color: #212529;
    background-color: #fff;
    border: 1px solid #ced4da;
    border-radius: 6px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:hover {
      border-color: #adb5bd;
    }

    &:focus {
      border-color: #80bdff;
      outline: 0;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15);
    }
  }

  .filter-reset {
    height: 38px;
    padding: 0 0.85rem;
    font-size: 0.85rem;
    color: #6c757d;
    background: transparent;
    border: 1px solid #ced4da;
    border-radius: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    align-self: flex-end;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    &:hover {
      background: #f8f9fa;
      border-color: #adb5bd;
      color: #212529;
    }

    i {
      font-size: 1rem;
    }
  }
}

.two-col-pop {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #dee2e6;
}

span.vue-star-rating-rating-text {
  font-size: 2rem;
  padding: 0.5rem 1rem 0 1rem;
}

.user-details-pop p span {
  font-weight: bold;
  display: inline-block;
  min-width: 8rem;
}

.trial-performance-wrapper {
  margin-bottom: 2rem;
}

.out-come-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  height: 7rem;
  margin-top: 4rem;

  p {
    width: 100%;
  }

  button {
    min-width: 9rem;
  }

  .red-button {
    background: #d85c64;
  }

  .yellow-button {
    background: #ffc107;
  }
}

.outCome-Under {
  border: 1px solid #004ca3;
  padding: 0.5rem !important;
  margin: 0.5rem 0 !important;
  color: #008afc;
  background: #e8f5ff;
}

.outCome-Signed {
  border: 1px solid #18a300;
  padding: 0.5rem !important;
  margin: 0.5rem 0 !important;
  color: #18a300;
  background: #ecffe8;
}

.outCome-Rejected {
  border: 1px solid #d85c64;
  padding: 0.5rem !important;
  margin: 0.5rem 0 !important;
  color: #d85c64;
  background: #ffe8ec;
}

.outCome-Waiting {
  border: 1px solid #ffc107;
  padding: 0.5rem !important;
  margin: 0.5rem 0 !important;
  color: #ffc107;
  background: #fffbe8;
}

.trialist-tab {
  padding: 1rem;
}

.sessions-info .session-card {
  display: block;
}

.document {
  max-width: 14rem;
  text-align: center;
  padding: 1rem;
  margin: 1rem;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(138, 138, 138, 0.3);
  border: 1px solid transparent;

  img {
    width: 40%;
  }

  h3 {
    margin-top: 1rem;
    font-size: 1rem;
    overflow: hidden;
    white-space: nowrap;
    text-transform: inherit;
  }
}

.lead-form-wrapper {
  margin-bottom: 1rem;
}

.lead-buttons {
  display: flex;
  gap: 0.5rem;
}

@media screen and (min-width: 1200px) {
  .video-wrapper {
    width: 20%;
  }

  .document {
    width: 20%;
  }
}

@media screen and (min-width: 1600px) {
  .games-info {
    margin: 2rem 0;
  }
}

// Phase 2: Advanced Player Demographics & Profiling Styles
.advanced-profiling-sections {
  margin-top: 2rem;
  border-top: 2px solid #e1e5e9;
  padding-top: 2rem;

  .section-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 1.5rem;
    padding: 0.75rem 1rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 6px;
    display: flex;
    align-items: center;

    i {
      margin-right: 0.75rem;
      font-size: 1.2rem;
    }
  }

  .demographics-section,
  .physical-attributes-section,
  .technical-skills-section,
  .mental-attributes-section,
  .match-performance-section,
  .development-potential-section {
    margin-bottom: 2.5rem;
    padding: 1.5rem;
    border: 1px solid #e1e5e9;
    border-radius: 10px;
    background: linear-gradient(145deg, #f8f9fa 0%, #ffffff 100%);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }

  .form-row {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;

    .form__item--quarter {
      flex: 1;
      min-width: 0;
    }

    .form__item--third {
      flex: 1;
      min-width: 0;
    }

    .form__item--half {
      flex: 1;
      min-width: 0;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 0.5rem;
    }
  }

  .rating-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;

    .form__item {
      text-align: center;
      padding: 1rem;
      background: white;
      border-radius: 8px;
      border: 1px solid #e9ecef;
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
      }

      .form__label {
        font-size: 0.85rem;
        font-weight: 600;
        color: #495057;
        margin-bottom: 0.5rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .ratingStars {
        display: flex;
        justify-content: center;
        margin-top: 0.5rem;
      }
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    }
  }

  .form__input {
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 0.75rem;
    font-size: 0.9rem;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;

    &:focus {
      border-color: #667eea;
      box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
    }

    &[type="number"] {
      text-align: center;
    }
  }

  .form__label {
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.5rem;
    display: block;
  }

  // Demographics section specific styles
  .demographics-section {
    .section-title {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
  }

  // Physical attributes section specific styles
  .physical-attributes-section {
    .section-title {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    }
  }

  // Technical skills section specific styles
  .technical-skills-section {
    .section-title {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }
  }

  // Mental attributes section specific styles
  .mental-attributes-section {
    .section-title {
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
    }
  }

  // Match performance section specific styles
  .match-performance-section {
    .section-title {
      background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
    }
  }

  // Development potential section specific styles
  .development-potential-section {
    .section-title {
      background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
      color: #2c3e50;
    }

    .ratingStars {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 0.5rem;
    }
  }

  textarea {
    border: 1px solid #ced4da;
    border-radius: 6px;
    padding: 0.75rem;
    font-size: 0.9rem;
    resize: vertical;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;

    &:focus {
      border-color: #667eea;
      box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
    }
  }
}

.advanced-profiling-toggle {
  text-align: center;
  margin: 1.5rem 0;

  .btn {
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    border-radius: 25px;
    transition: all 0.3s ease;
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
    }

    &.btn-success {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-color: #667eea;
    }

    &.btn-outline-secondary {
      border-color: #6c757d;
      color: #6c757d;

      &:hover {
        background: #6c757d;
        color: white;
      }
    }

    i {
      margin-right: 0.5rem;
      font-size: 1.1rem;
    }
  }
}

// Enhanced responsive design for mobile
@media (max-width: 576px) {
  .advanced-profiling-sections {
    .form-row {
      flex-direction: column;

      .form__item--quarter,
      .form__item--third,
      .form__item--half {
        flex: none;
      }
    }

    .rating-grid {
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    }

    .section-title {
      font-size: 1rem;
      padding: 0.5rem 0.75rem;

      i {
        margin-right: 0.5rem;
        font-size: 1rem;
      }
    }
  }
}

// AI Insights Integration Styling
.ai-insights-integration {
  margin: 1.5rem 0;
  border: 2px solid #e8f4f8;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8fdff 0%, #e8f4f8 100%);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.1);
  transition: all 0.3s ease;

  &:hover {
    border-color: #667eea;
    box-shadow: 0 6px 25px rgba(102, 126, 234, 0.15);
    transform: translateY(-2px);
  }

  // Ensure proper spacing and styling consistency
  .ai-insights-panel {
    border: none;
    border-radius: 12px;
    background: transparent;
  }

  // Match the form styling of the trialist component
  .ai-insight-item {
    background: white;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 0.75rem;
    border: 1px solid #e3e9ef;
    transition: all 0.2s ease;

    &:hover {
      border-color: #c5d2d9;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
  }

  // Responsive design for mobile
  @media (max-width: 768px) {
    margin: 1rem 0;
    border-radius: 8px;

    .ai-insights-panel {
      border-radius: 8px;
    }

    .ai-insight-item {
      border-radius: 6px;
      padding: 0.75rem;
    }
  }
}

// AI Scout Report Styles
.ai-scout-report-container {
  padding: 1.5rem;
  background: #1a1d29;
  border-radius: 12px;

  h6 {
    color: rgb(222 239 252) !important;
  }

  .text-muted {
    color: rgb(222 239 252) !important;
  }

  .report-header {
    .badge {
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;
    }
  }

  .scout-report-content {
    .report-meta {
      padding: 1rem;
      background: linear-gradient(135deg, #2d3142 0%, #1a1d29 100%);
      border-radius: 12px;
      color: #e0e0e0;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
      border: 1px solid rgba(102, 126, 234, 0.2);

      small {
        color: rgba(255, 255, 255, 0.7);
      }
    }

    .player-overview-card {
      background: linear-gradient(135deg, #2d3142 0%, #252836 100%);
      border-radius: 16px;
      padding: 2rem;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
      border: 1px solid rgba(102, 126, 234, 0.3);

      .player-name {
        color: #ffffff;
        font-weight: 700;
        font-size: 1.75rem;
        margin-bottom: 1rem;

        i {
          color: #667eea;
        }
      }

      .player-details {
        display: flex;
        gap: 1rem;
        flex-wrap: wrap;

        .detail-badge {
          background: rgba(102, 126, 234, 0.15);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
          color: #e0e0e0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(102, 126, 234, 0.3);

          i {
            color: #667eea;
            margin-right: 0.5rem;
          }
        }
      }

      .confidence-score {
        .score-circle {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-direction: column;
          box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
          margin: 0 auto;

          .score-value {
            font-size: 2rem;
            font-weight: 700;
            color: white;
            line-height: 1;
          }

          .score-max {
            font-size: 0.9rem;
            color: rgba(255, 255, 255, 0.8);
          }
        }
      }
    }

    .metric-card {
      background: linear-gradient(135deg, #2d3142 0%, #252836 100%);
      border-radius: 12px;
      padding: 1.5rem;
      height: 100%;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
      border-left: 4px solid;
      transition:
        transform 0.2s,
        box-shadow 0.2s;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 6px 25px rgba(0, 0, 0, 0.7);
      }

      .card-icon {
        width: 50px;
        height: 50px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;

        i {
          font-size: 1.5rem;
        }
      }

      h6 {
        font-weight: 600;
        color: #ffffff;
        margin-bottom: 1rem;
      }

      .metric-list {
        list-style: none;
        padding: 0;
        margin: 0;

        li {
          padding: 0.5rem 0;
          font-size: 0.9rem;
          color: #e0e0e0;
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;

          i {
            margin-top: 0.2rem;
            flex-shrink: 0;
          }
        }
      }

      &.strengths-card {
        border-color: #f39c12;

        .card-icon {
          background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
          color: white;
        }
      }

      &.development-card {
        border-color: #3498db;

        .card-icon {
          background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
          color: white;
        }
      }

      &.recommendation-card {
        border-color: #27ae60;

        .card-icon {
          background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
          color: white;
        }

        .recommendation-badge {
          display: inline-block;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.95rem;
          margin-top: 0.5rem;

          &.rec-strong-sign {
            background: linear-gradient(135deg, #27ae60 0%, #229954 100%);
            color: white;
          }

          &.rec-trial {
            background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
            color: white;
          }

          &.rec-monitor {
            background: linear-gradient(135deg, #f39c12 0%, #e67e22 100%);
            color: white;
          }

          &.rec-pass {
            background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
            color: white;
          }

          &.rec-default {
            background: linear-gradient(135deg, #95a5a6 0%, #7f8c8d 100%);
            color: white;
          }
        }
      }
    }

    .chart-card {
      background: linear-gradient(135deg, #2d3142 0%, #252836 100%);
      border-radius: 12px;
      padding: 1.5rem;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
      height: 100%;
      border: 1px solid rgba(102, 126, 234, 0.2);

      h6 {
        font-weight: 600;
        color: #ffffff;
        margin-bottom: 1.5rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;

        i {
          color: #667eea;
        }
      }

      canvas {
        max-height: 300px;
      }

      .attributes-display {
        .attribute-item {
          margin-bottom: 1.5rem;

          .attribute-label {
            display: block;
            font-weight: 600;
            color: #ffffff;
            margin-bottom: 0.5rem;
            font-size: 0.9rem;
          }

          .attribute-bar-wrapper {
            background: rgba(0, 0, 0, 0.3);
            border-radius: 10px;
            height: 30px;
            overflow: hidden;
            position: relative;
            border: 1px solid rgba(102, 126, 234, 0.2);

            .attribute-bar {
              background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: flex-end;
              padding-right: 10px;
              border-radius: 10px;
              transition: width 0.3s ease;
              box-shadow: 0 0 15px rgba(102, 126, 234, 0.4);

              .attribute-value {
                color: white;
                font-weight: 600;
                font-size: 0.85rem;
                text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
              }
            }
          }

          .rating-circles {
            display: flex;
            gap: 0.5rem;

            .rating-circle {
              width: 20px;
              height: 20px;
              border-radius: 50%;
              border: 2px solid rgba(102, 126, 234, 0.5);
              background: rgba(0, 0, 0, 0.3);
              transition: all 0.2s ease;

              &.filled {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                box-shadow: 0 0 8px rgba(102, 126, 234, 0.6);
                border-color: #667eea;
              }
            }
          }
        }
      }
    }

    .report-body {
      background: linear-gradient(135deg, #2d3142 0%, #252836 100%);
      border-radius: 12px;
      padding: 2rem;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.5);
      line-height: 1.8;
      border: 1px solid rgba(102, 126, 234, 0.2);
      color: #e0e0e0;

      :deep(.report-section) {
        margin-bottom: 2rem;
        padding: 1.5rem;
        background: rgba(0, 0, 0, 0.2);
        border-radius: 10px;
        transition: all 0.3s ease;

        h1 {
          display: none;
        }

        h2 {
          color: #ffffff;
        }

        &:hover {
          background: rgba(0, 0, 0, 0.3);
          border-left-color: #667eea;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        &.section-strengths {
          border-left-color: #f39c12;
          background: rgba(243, 156, 18, 0.05);
        }

        &.section-development {
          border-left-color: #3498db;
          background: rgba(52, 152, 219, 0.05);
        }

        &.section-recommendation {
          border-left-color: #27ae60;
          background: rgba(39, 174, 96, 0.05);
        }

        &.section-stats {
          border-left-color: #9b59b6;
          background: rgba(155, 89, 182, 0.05);
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid rgba(102, 126, 234, 0.2);

          i {
            font-size: 1.5rem;
            color: #667eea;
            background: rgba(102, 126, 234, 0.15);
            padding: 0.5rem;
            border-radius: 8px;
          }

          h3 {
            color: #ffffff;
            font-weight: 700;
            font-size: 1.3rem;
            margin: 0;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }

        .section-content {
          color: #e0e0e0;
          line-height: 1.8;
        }
      }

      :deep(.subsection-title) {
        display: block;
        color: #667eea;
        font-weight: 600;
        font-size: 1.05rem;
        margin: 1.5rem 0 0.75rem;
        padding-left: 0.5rem;
      }

      :deep(.report-paragraph) {
        color: #d0d0d0;
        margin-bottom: 1rem;
        line-height: 1.8;
        text-align: left;
        display: flex;
        gap: 1rem;
      }

      :deep(.report-list) {
        list-style: none;
        padding: 0;
        margin: 1rem 0 1.5rem;

        li {
          color: #d0d0d0;
          line-height: 1.8;
          padding: 0.75rem 0;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;

          .bullet-icon {
            color: #667eea;
            font-size: 0.8rem;
            margin-top: 0.4rem;
            flex-shrink: 0;
          }

          .number-badge {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            font-size: 0.85rem;
            flex-shrink: 0;
            box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
          }
        }
      }

      :deep(strong) {
        color: #ffffff;
        font-weight: 600;
      }

      // Section-specific styling
      h4:has(.bi-star-fill) {
        color: #f39c12;
      }

      h4:has(.bi-tools) {
        color: #3498db;
      }

      h4:has(.bi-flag-fill) {
        color: #27ae60;
      }

      h4:has(.bi-graph-up) {
        color: #667eea;
      }
    }
  }
}

// Player Media Tab Styles
.player-media-container {
  .media-filter-bar {
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;

    .btn-group {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      border-radius: 8px;
      overflow: hidden;

      .btn {
        border-radius: 0;
        padding: 0.75rem 1.5rem;
        font-weight: 500;
        transition: all 0.3s ease;

        i {
          margin-right: 0.5rem;
        }

        &:hover:not(.btn-primary) {
          background-color: #f8f9fa;
          transform: translateY(-1px);
        }
      }
    }
  }

  .media-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.5rem;
    padding: 1rem 0;

    .media-card {
      background: linear-gradient(135deg, #2d3142 0%, #252836 100%);
      border-radius: 12px;
      overflow: hidden;
      transition: all 0.3s ease;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(102, 126, 234, 0.2);

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
        border-color: rgba(102, 126, 234, 0.5);

        .play-overlay {
          opacity: 1;
          background: rgba(0, 0, 0, 0.7);

          i {
            transform: scale(1.2);
          }
        }

        .thumbnail-image {
          transform: scale(1.05);
        }
      }

      .media-link {
        text-decoration: none;
        color: inherit;
        display: block;

        &:hover {
          text-decoration: none;
        }
      }

      .media-thumbnail {
        position: relative;
        width: 100%;
        padding-top: 56.25%; // 16:9 aspect ratio
        overflow: hidden;
        background: #000;

        .thumbnail-image {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .play-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.5);
          opacity: 0;
          transition: all 0.3s ease;

          i {
            font-size: 4rem;
            color: #fff;
            transition: transform 0.3s ease;
            filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
          }
        }

        .media-type-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          padding: 0.35rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          backdrop-filter: blur(10px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

          &.badge-video {
            background: rgba(220, 53, 69, 0.9);
            color: #fff;
          }

          &.badge-short {
            background: rgba(255, 193, 7, 0.9);
            color: #000;
          }

          i {
            font-size: 0.9rem;
          }
        }
      }

      .media-info {
        padding: 1rem;

        .media-title {
          font-size: 0.95rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.75rem;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          min-height: 2.8rem;
        }

        .media-meta {
          display: flex;
          gap: 1rem;
          margin-bottom: 0.5rem;
          flex-wrap: wrap;

          .meta-item {
            display: flex;
            align-items: center;
            gap: 0.3rem;
            font-size: 0.8rem;
            color: #b0b0b0;

            i {
              color: #667eea;
              font-size: 0.9rem;
            }
          }
        }

        .media-source {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.75rem;
          color: #888;
          padding-top: 0.5rem;
          border-top: 1px solid rgba(102, 126, 234, 0.2);

          i {
            color: #ff0000;
            font-size: 1rem;
          }
        }
      }
    }
  }
}

.alert {
  border-radius: 8px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &.alert-info {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    color: #1976d2;
    border-left: 4px solid #2196f3;
  }

  &.alert-danger {
    background: linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%);
    color: #c62828;
    border-left: 4px solid #f44336;
  }
}

.spinner-border {
  border-width: 0.25rem;
}

.progress {
  height: 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.1);

  .progress-bar {
    background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  }
}

/* Your Team Tab Styles */

.team-controls {
  margin-bottom: 20px;
  display: block;
  gap: 10px;
  align-items: center;
}

.pitch-container {
  flex: 2;
  position: relative;
  background-color: #2e7d32;
  border: 2px solid #1b5e20;
  border-radius: 4px;
  overflow: hidden;
  /* Maintain aspect ratio roughly */
  aspect-ratio: 105/68;
  max-height: 800px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.pitch-svg {
  width: 100%;
  height: 100%;
  display: block;
}

/* Global dragging styles */
body.dragging {
  user-select: none;
  cursor: grabbing !important;
}

body.dragging * {
  cursor: grabbing !important;
}

.player-marker {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 12px;
  color: #333;
  position: relative;
}

.player-tooltip {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s;
  margin-bottom: 5px;
}

.remove-player-btn {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 16px;
  height: 16px;
  background: red;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  padding: 0;
}

.team-sidebar {
  flex: 1;
  background: #3a3b43;
  color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  border: 1px solid #65666f;
}

.team-sidebar h3,
.team-sidebar h4 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.2rem;
  border-bottom: 1px solid #65666f;
  padding-bottom: 10px;
  color: #fff;
}

.team-stats {
  margin-bottom: 20px;
  padding-top: 15px;
  border-top: 1px solid #65666f;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 0.9rem;
  color: #adb5bd;
}

.stat-row strong {
  color: #fff;
}

.empty-squad {
  padding: 20px;
  text-align: center;
  color: #adb5bd;
  font-style: italic;
}

.empty-team-state {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px;
  text-align: center;
  color: #adb5bd;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 12px;
  border: 2px dashed #65666f;
}

.empty-team-state i {
  font-size: 4rem;
  margin-bottom: 20px;
  color: #65666f;
}

.empty-team-state h3 {
  color: #fff;
  margin-bottom: 10px;
}

/* Sidebar Search Functionality Styles */
.team-sidebar-search {
  border-top: 1px solid #404258;
  padding-top: 1rem;
}

.tab-btn {
  flex: 1;
  background: transparent;
  border: none;
  color: #adb5bd;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  transition: all 0.2s;
  cursor: pointer;
}

.tab-btn.active {
  background: #0d6efd;
  color: white;
}

.tab-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.tab-btn i {
  margin-right: 4px;
  font-size: 0.8rem;
}

/* Quick Search Styles */
.quick-search h6 {
  color: #fff;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.player-result-item,
.watchlist-item,

.player-result-item:hover,
.watchlist-item:hover,

.player-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.player-mini-avatar,
.player-mini-avatar-placeholder {
  width: 32px;
  margin-right: 8px;
  object-fit: cover;
  border: 2px solid #404258;
}

.player-mini-avatar-placeholder {
  background: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
  color: white;
}

.player-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.player-meta {
  font-size: 0.75rem;
  color: #adb5bd;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.player-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

.btn-xs {
  padding: 2px 6px;
  font-size: 0.7rem;
  line-height: 1.2;
  border-radius: 4px;
}

/* Empty States */
.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: #6c757d;
}

.empty-state i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
  display: block;
}

.empty-state p {
  margin-bottom: 0.25rem;
  color: #adb5bd;
}

.empty-state small {
  color: #6c757d;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .sidebar-tabs {
    flex-direction: column;
  }

  .tab-btn {
    margin-bottom: 2px;
  }

  .player-actions {
    flex-direction: column;
    gap: 2px;
  }
}

/* Player Detail Modal Dark Theme */

.player-overview-header {
  background: linear-gradient(135deg, #1a1d29 0%, #2c3034 100%) !important;
  border: 1px solid #404258 !important;
  color: #ffffff !important;
}

.player-main-info h2 {
  color: #ffffff !important;
}

.player-meta .meta-item {
  color: #adb5bd !important;
}

.player-overview-header .nav-pills .nav-link {
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: #adb5bd !important;
  border: 1px solid #404258 !important;
}

.player-overview-header .nav-pills .nav-link.active {
  background-color: #0d6efd !important;
  color: #ffffff !important;
}

.player-overview-header .nav-pills .nav-link:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.2) !important;
  color: #ffffff !important;
}

/* Player Compare Modal Styles */

.player-photo-container {
  margin-bottom: 15px;
}

.player-photo {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid #404258;
}

.player-photo-placeholder {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
  border: 3px solid #404258;
  margin: 0 auto;
}

.player-name {
  color: #ffffff;
  margin: 10px 0 5px;
  font-size: 18px;
}

.player-meta {
  color: #adb5bd;
  font-size: 14px;
}

.section-title {
  color: #ffffff;
  padding: 0 20px;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 600;
}

.section-title i {
  margin-right: 8px;
  color: #0d6efd;
}

.nationality-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.country-flag {
  width: 16px;
  height: 12px;
}

.chart-container {
  padding: 20px;
  text-align: center;
}

.chart-container canvas {
  max-width: 100%;
  height: auto;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-dialog.modal-xl {
  max-width: 1400px;
  width: 95%;
  margin: 30px auto;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .compare-attribute {
    flex: 0 0 150px;
  }

  .player-photo,
  .player-photo-placeholder {
    width: 60px;
    height: 60px;
    font-size: 20px;
  }
}

@media (max-width: 768px) {
  .modal-dialog.modal-xl {
    width: 100%;
    margin: 0;
  }

  .compare-header {
    flex-wrap: wrap;
  }

  .compare-player-header {
    flex: 0 0 50%;
    border-bottom: 1px solid #404258;
    padding: 10px;
  }

  .compare-row {
    flex-wrap: wrap;
  }

  .compare-attribute {
    flex: 0 0 100%;
    padding: 5px 20px;
    font-weight: 600;
  }

  .compare-value {
    flex: 0 0 50%;
    padding: 5px;
  }
}

/* Match Stats Styles */
.match-stats-container {
  background-color: #212529 !important;
  color: #ffffff !important;

  h1,
  h2,
  h3,
  h4,
  h5 {
    color: #ffffff;
  }
}

.season-summary {
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid #404258;
  margin-bottom: 20px;
}

.summary-card {
  text-align: center;
  padding: 15px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  border: 1px solid #404258;
}

.summary-number {
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
  margin-bottom: 5px;
}

.summary-label {
  font-size: 12px;
  color: #adb5bd;
  text-transform: uppercase;
}

.matches-list {
  margin-top: 20px;
}

.match-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.match-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid #404258;
  border-radius: 8px;
  padding: 15px;
  transition: all 0.2s ease;
}

.match-card:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: #0d6efd;
}

.match-header {
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #404258;
}

.match-info {
  flex: 1;
}

.match-date {
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 5px;
}

.competition {
  font-size: 12px;
  color: #adb5bd;
  margin-bottom: 3px;
}

.opposition {
  font-size: 14px;
  color: #ffffff;
  font-weight: 500;
}

.match-result {
  text-align: right;
}

.result-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 12px;
  text-transform: uppercase;
}

.result-win {
  background-color: #00d25b;
  color: #ffffff;
}

.result-loss {
  background-color: #fc424a;
  color: #ffffff;
}

.result-draw {
  background-color: #ffc107;
  color: #000000;
}

.match-stats {
  margin-top: 10px;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 15px;
}

.stat-group {
  flex: 1;
  text-align: center;
  padding: 8px 5px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  border: 1px solid #404258;
}

.stat-label {
  display: block;
  font-size: 11px;
  color: #adb5bd;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.stat-value {
  display: block;
  font-weight: bold;
  font-size: 14px;
  color: #ffffff;
}

/* Rating color classes */
.rating-excellent {
  color: #00d25b !important;
}

.rating-good {
  color: #28a745 !important;
}

.rating-average {
  color: #ffc107 !important;
}

.rating-below-average {
  color: #fd7e14 !important;
}

.rating-poor {
  color: #fc424a !important;
}

.detailed-stats {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #404258;
}

.detailed-stats h6 {
  color: #0d6efd;
  margin-bottom: 10px;
  font-size: 14px;
  text-transform: uppercase;
}

.detail-stat {
  padding: 3px 0;
  color: #adb5bd;
  font-size: 13px;
  border-bottom: 1px solid rgba(64, 66, 88, 0.3);
}

.detail-stat:last-child {
  border-bottom: none;
}

.filter-controls select {
  background-color: #2c3034 !important;
  border: 1px solid #404258 !important;
  color: #ffffff !important;
}

.filter-controls select:focus {
  border-color: #0d6efd !important;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25) !important;
}

.no-data {
  padding: 40px 20px;
}

.no-data .alert {
  background-color: rgba(13, 202, 240, 0.1) !important;
  border-color: #0dcaf0 !important;
  color: #ffffff !important;
}

/* Responsive design */
@media (max-width: 768px) {
  .match-header {
    flex-direction: column;
    text-align: center;
    gap: 10px;
  }

  .stat-row {
    flex-wrap: wrap;
    gap: 10px;
  }

  .stat-group {
    flex: 0 0 48%;
  }

  .summary-card {
    margin-bottom: 10px;
  }

  .detailed-stats .row {
    flex-direction: column;
  }

  .detailed-stats .col-md-4 {
    margin-bottom: 15px;
  }
}

// Dark theme chart styles
.dark-card {
  background: linear-gradient(135deg, #2d3142 0%, #252836 100%) !important;
  border: 1px solid rgba(102, 126, 234, 0.3) !important;
  color: #ffffff !important;

  .dark-header {
    background: rgba(102, 126, 234, 0.1) !important;
    border-bottom: 1px solid rgba(102, 126, 234, 0.3) !important;
    color: #ffffff !important;

    h6 {
      color: #ffffff !important;
    }
  }

  .dark-body {
    background: transparent !important;
    color: #ffffff !important;

    canvas {
      background-color: rgba(0, 0, 0, 0.2) !important;
      border-radius: 8px !important;
    }
  }
}

.chart-wrapper {
  position: relative;

  canvas {
    max-width: 100% !important;
    height: auto !important;
  }
}

.chart-legend {
  .legend-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;

    .legend-color {
      width: 20px;
      height: 12px;
      border-radius: 3px;
    }
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;

  .stat-item {
    text-align: center;
    padding: 1rem;
    background: rgba(102, 126, 234, 0.1);
    border-radius: 8px;
    border: 1px solid rgba(102, 126, 234, 0.3);

    .stat-number {
      font-size: 1.5rem;
      font-weight: bold;
      margin-bottom: 0.5rem;
    }

    .stat-label {
      font-size: 0.875rem;
      color: #a0a1a7;
    }
  }
}

// Heatmap Functionality Styles (Dark Theme - Matching Opposition Analysis)
.tactical-pitch-container {
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
}

.pitch-wrapper {
  position: relative;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 8px;
  padding: 1rem;
  overflow: hidden;

  .football-pitch {
    width: 100%;
    height: auto;
    display: block;
    filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
  }

  .pitch-markings {
    animation: fadeIn 0.5s ease;
  }

  .heat-zone {
    transition: opacity 0.3s ease;
    cursor: pointer;

    &:hover {
      opacity: 0.8 !important;
    }
  }

  .press-line {
    animation: dashAnimation 2s linear infinite;
  }

  .player-position {
    transition: all 0.3s ease;

    &:hover,
    &.selected {
      .player-circle {
        filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.6));
      }
    }
  }

  .player-circle {
    transition: all 0.3s ease;

    &.threat-1,
    &.threat-2 {
      fill: #3498db;
    }

    &.threat-3,
    &.threat-4 {
      fill: #f39c12;
    }

    &.threat-5 {
      fill: #e74c3c;
    }
  }
}

.player-details-panel,
.moment-details-panel {
  position: absolute;
  bottom: 1.5rem;
  right: 1.5rem;
  background: rgba(0, 0, 0, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 1.25rem;
  max-width: 300px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);

  .close-btn {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.5);
    font-size: 1.25rem;
    cursor: pointer;
    padding: 0.25rem;
    line-height: 1;
    transition: color 0.2s ease;

    &:hover {
      color: #ffffff;
    }
  }

  h4 {
    color: #ffffff;
    font-size: 1.125rem;
    margin: 0 0 1rem 0;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;

    .label {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.875rem;
    }

    .value {
      color: #ffffff;
      font-size: 0.875rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }

  .threat-meter {
    width: 80px;
    height: 8px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    overflow: hidden;
    display: inline-block;

    .threat-fill {
      height: 100%;
      display: block;
      border-radius: 4px;
      transition: width 0.3s ease;

      &.threat-1,
      &.threat-2 {
        background: #3498db;
      }

      &.threat-3,
      &.threat-4 {
        background: #f39c12;
      }

      &.threat-5 {
        background: #e74c3c;
      }
    }
  }
}

.moment-details-panel {
  .moment-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    .moment-type {
      padding: 0.25rem 0.75rem;
      border-radius: 4px;
      font-size: 0.75rem;
      font-weight: 700;
      text-transform: uppercase;

      &.goal {
        background: #2ecc71;
        color: #ffffff;
      }

      &.chance {
        background: #f39c12;
        color: #ffffff;
      }

      &.defensive_action {
        background: #3498db;
        color: #ffffff;
      }
    }

    .moment-minute {
      color: rgba(255, 255, 255, 0.7);
      font-size: 0.875rem;
      font-weight: 600;
    }
  }

  .moment-description {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.875rem;
    line-height: 1.5;
    margin: 0 0 1rem 0;
  }

  .moment-meta {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    span {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.8125rem;
      display: flex;
      align-items: center;
      gap: 0.5rem;

      i {
        color: #667eea;
      }

      &.outcome {
        font-weight: 600;

        &.success {
          color: #2ecc71;
        }

        &.failed,
        &.saved {
          color: #e74c3c;
        }
      }
    }
  }
}

// Heatmap Analysis Cards
.heatmap-analysis-card {
  background: #3a3b43;
  border: 1px solid #65666f;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  .card-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    i {
      color: #667eea;
      font-size: 1.25rem;
    }

    h3 {
      color: #ffffff;
      font-size: 1.25rem;
      font-weight: 700;
      margin: 0;
    }

    .badge {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #ffffff;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
    }
  }

  .card-body {
    color: #d3d4da;
    line-height: 1.6;

    .metric-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);

      &:last-child {
        border-bottom: none;
      }

      .metric-label {
        color: #a0a1a7;
        font-size: 0.875rem;
      }

      .metric-value {
        color: #ffffff;
        font-weight: 600;
        font-size: 1rem;

        &.positive {
          color: #2ecc71;
        }

        &.negative {
          color: #e74c3c;
        }

        &.neutral {
          color: #f39c12;
        }
      }
    }
  }
}

// Heatmap Button Styles
.heatmap-toggle-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }

  &.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #ffffff;
    border-color: #667eea;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  i {
    font-size: 1rem;
  }
}

// Heatmap Overlay Styles
.heatmap-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  .heatmap-modal {
    background: #2c2d34;
    border: 1px solid #65666f;
    border-radius: 16px;
    padding: 2rem;
    max-width: 95vw;
    max-height: 95vh;
    overflow: auto;
    position: relative;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5);

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;

      h2 {
        color: #ffffff;
        font-size: 1.5rem;
        font-weight: 700;
        margin: 0;
        display: flex;
        align-items: center;
        gap: 0.75rem;

        i {
          color: #667eea;
        }
      }

      .close-modal-btn {
        background: none;
        border: none;
        color: rgba(255, 255, 255, 0.5);
        font-size: 2rem;
        cursor: pointer;
        padding: 0.5rem;
        line-height: 1;
        transition: all 0.2s ease;
        border-radius: 50%;

        &:hover {
          color: #ffffff;
          background: rgba(255, 255, 255, 0.1);
        }
      }
    }
  }
}

// Animations for Heatmap
@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 0.6;
  }
}

@keyframes dashAnimation {
  to {
    stroke-dashoffset: -20;
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

// Responsive Heatmap Styles
@media (max-width: 768px) {
  .tactical-pitch-container {
    padding: 1rem;
  }

  .pitch-controls {
    .control-btn {
      padding: 0.4rem 0.75rem;
      font-size: 0.8125rem;

      span {
        display: none;
      }
    }
  }

  .formation-info,
  .player-details-panel,
  .moment-details-panel {
    max-width: 200px;
    font-size: 0.8125rem;
  }

  .heatmap-overlay {
    padding: 1rem;

    .heatmap-modal {
      padding: 1.5rem;
      border-radius: 12px;
    }
  }

  .heatmap-analysis-card {
    padding: 1rem;

    .card-header {
      h3 {
        font-size: 1.125rem;
      }
    }
  }
}

@media (max-width: 576px) {
  .pitch-controls {
    justify-content: center;

    .control-group {
      flex-wrap: wrap;
      justify-content: center;
    }

    .control-btn {
      flex: 1;
      min-width: 80px;
      justify-content: center;
    }
  }

  .heatmap-toggle-btn {
    width: 100%;
    justify-content: center;
  }
}

/* Trialist Heatmap Styles - copied from PlayerHeatmapModal.vue */
.loading-state,
.error-state,
.no-data {
  text-align: center;
  padding: 60px 20px;
  color: rgba(232, 244, 253, 0.7);
}

.loading-state .spinner-border {
  margin-bottom: 16px;
}

.no-data i {
  font-size: 48px;
  color: rgba(232, 244, 253, 0.5);
  margin-bottom: 16px;
}

.heatmap-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  gap: 20px;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.control-group label {
  font-weight: 600;
  color: #e8f4fd;
  white-space: nowrap;
}

.btn-group {
  display: flex;
}

.btn {
  padding: 6px 12px;
  border: 1px solid #74b9ff;
  background: none;
  color: #74b9ff;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.btn:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.btn:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.btn + .btn {
  border-left: none;
}

.btn-primary {
  background: #74b9ff;
  color: white;
}

.btn-outline-primary {
  color: #74b9ff;
}

.btn-outline-primary:hover {
  background: rgba(116, 185, 255, 0.1);
}

.intensity-slider {
  width: 120px;
  margin: 0 8px;
}

.intensity-value {
  font-weight: 600;
  color: #e8f4fd;
  min-width: 35px;
  text-align: right;
}

.pitch-container {
  position: relative;
  margin-bottom: 30px;
  border-radius: 8px;
  padding: 20px;
}

// Specific styles for heatmap pitch to override dark modal styles
.heatmap-tab-content {
  background: transparent !important;

  .pitch-container,
  .heatmap-pitch-container {
    background: transparent !important;
    border: none !important;
    padding: 0 !important;
  }

  .pitch-wrapper {
    background: transparent !important;
    padding: 0 !important;
  }

  .football-pitch {
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
}

.heatmap-pitch-container {
  background: transparent !important;
  border: none !important;

  .pitch-wrapper {
    position: relative !important;
    background: transparent !important;
    padding: 0 !important;
  }

  .football-pitch {
    border-radius: 8px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
}

// Override tab-content background for heatmap section
.tab-content .heatmap-pitch-container {
  background: transparent !important;
}

.pitch-wrapper {
  position: relative;
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
}

.football-pitch {
  width: 100%;
  height: auto;
  display: block;
}

.pitch-heatmap-svg .heatmap-point,

.heatmap-point {
  cursor: pointer;
}

.position-point {
  cursor: pointer;
}

.average-position {
  cursor: pointer;
}

.position-label {
  fill: white;
  font-size: 30px;
  font-weight: bold;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.8);
}

.zone-label {
  fill: rgba(255, 255, 255, 0.7);
  font-size: 30px;
  font-weight: 500;
}

.heatmap-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  pointer-events: none;
  z-index: 1000;
  white-space: nowrap;
}

.position-analysis {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.analysis-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.analysis-card {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
}

.analysis-card h5 {
  margin: 0 0 16px 0;
  color: #e8f4fd;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
}

.zone-stats {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.zone-stat {
  display: grid;
  grid-template-columns: 80px 1fr 40px;
  align-items: center;
  gap: 12px;
}

.zone-name {
  font-size: 12px;
  font-weight: 600;
  color: rgba(232, 244, 253, 0.8);
}

.zone-bar {
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.zone-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.zone-fill.defensive {
  background: linear-gradient(90deg, #dc3545, #ff6b7a);
}

.zone-fill.midfield {
  background: linear-gradient(90deg, #fd7e14, #ffb347);
}

.zone-fill.attacking {
  background: linear-gradient(90deg, #28a745, #5cbf60);
}

.zone-percentage {
  font-size: 12px;
  font-weight: bold;
  color: #e8f4fd;
  text-align: right;
}

.movement-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.stat-item:last-child {
  border-bottom: none;
}

.stat-item label {
  font-size: 12px;
  color: rgba(232, 244, 253, 0.7);
  font-weight: 500;
}

.stat-value {
  font-size: 14px;
  font-weight: bold;
  color: #e8f4fd;
}

.tactical-insights h5 {
  margin: 0 0 12px 0;
  color: #e8f4fd;
  display: flex;
  align-items: center;
  gap: 8px;
}

.insights-list {
  margin: 0;
  padding-left: 16px;
  list-style: none;
}

.insight {
  margin-bottom: 8px;
  font-size: 14px;
  color: rgba(232, 244, 253, 0.8);
  line-height: 1.4;
  position: relative;
}

.insight::before {
  content: "💡";
  position: absolute;
  left: -20px;
  top: 0;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-left .checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: rgba(232, 244, 253, 0.8);
  cursor: pointer;
}

.heatmap-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-left .checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: rgba(232, 244, 253, 0.8);
  cursor: pointer;
}

.footer-right {
  display: flex;
  gap: 12px;
}

.btn-secondary {
  background: rgba(108, 117, 125, 0.8);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn-secondary:hover {
  background: rgba(84, 91, 98, 0.9);
}

.btn-primary {
  background: rgba(116, 185, 255, 0.8);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background: rgba(116, 185, 255, 1);
}

@media (max-width: 768px) {
  .heatmap-controls {
    flex-direction: column;
    gap: 12px;
  }

  .analysis-grid {
    grid-template-columns: 1fr;
  }

  .football-pitch {
    height: 300px;
  }

  .modal-footer {
    flex-direction: column;
    gap: 12px;
  }

  .footer-right {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
